<template>
  <div>
    <!-- 保存・取得中は画面全体を覆う -->
    <div v-if="loading" class="loading-mask">
      Waiting...
    </div>

    <!-- 種別名は「変更・解約・新規」の短い名前なので、画面の題は「契約詳細 変更」のように前に付ける -->
    <PageHeader :title="'契約詳細' + (val.typeName ? ' ' + val.typeName : '')">
      <template #title-header-content>
        <div class="flex-1 flex items-center justify-end gap-x-2 mr-2">

          <!-- 削除ボタン（保存済みで、一時保存・差戻のとき。誰でも押せる。承認者の画面には出さない）
               ほかのボタンと離して左端に置き、赤以外の色にする（隣だと押し間違えるため。ユーザー指示 2026-09-23）
               保存していた内容を消して状況を「削除」にし、一覧へ戻る。復活はメインタスク・サブタスクのボタンから開き直すだけ（メインタスクが未完了のとき） -->
          <PrimaryButton v-if="!isReadOnly && !isApprover && val.id" text="削除" size="xs" class="w-20 justify-center mr-auto bg-gray-700 hover:bg-gray-800 border border-white" @click="deleteRequest()">
            <Icon slot="before" iconName="Trash" iconType="outline" :strokeWidth="2" class="h-3.5 w-3.5" />
          </PrimaryButton>

          <!-- 全て開く -->
          <div class="w-24 text-center font-bold text-sm text-gray-50 cursor-pointer hover:text-gray-300 whitespace-nowrap" @click="allOpen()">
            <span class="border-b border-gray-50 px-2">全て開く</span>
          </div>

          <!-- 全て閉じる -->
          <div class="w-24 text-center font-bold text-sm text-gray-50 cursor-pointer hover:text-gray-300 whitespace-nowrap ml-2 mr-6" @click="allClose()">
            <span class="border-b border-gray-50 px-2">全て閉じる</span>
          </div>

          <!-- メインタスク遷移ボタン -->
          <PrimaryButton text="メインタスクへ" class="w-32 px-1 bg-teal-600 hover:bg-teal-700" @click="toMainTask()">
            <Icon slot="before" iconName="ViewGrid" iconType="solid" :strokeWidth="2" class="h-4 w-4" />
          </PrimaryButton>

          <!-- 状況で出すボタンを変える（AkbsInputForm と同じ）
               ・未保存・一時保存・差戻：申請、一時保存（削除は左端）
               ・承認待ち：申請取消、差戻、AKBSへ登録（内容は閲覧のみ）
               ・AKBS登録済み：なし（閲覧のみ） -->
          <template v-if="!isReadOnly">
            <PrimaryButton text="申請" class="w-32" @click="save(true)">
              <Icon slot="before" iconName="PaperAirplane" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
            </PrimaryButton>

            <PrimaryButton text="一時保存" class="w-32 bg-yellow-600 hover:bg-yellow-700" @click="save(false)">
              <Icon slot="before" iconName="Save" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
            </PrimaryButton>
          </template>

          <!-- AKBS登録済み（承認済み）はボタンの代わりに印（閲覧のみと分かるように） -->
          <div v-if="isRegistered" class="registered-mark">
            <Icon iconName="CheckCircle" iconType="solid" class="h-5 w-5 mr-1" />AKBSに登録済み
          </div>

          <template v-if="isPending">
            <PrimaryButton v-if="!isApprover" text="申請取消" class="w-32 bg-gray-500 hover:bg-gray-600" @click="cancelApply()">
              <Icon slot="before" iconName="X" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
            </PrimaryButton>

            <template v-if="canApprove">
              <PrimaryButton text="差戻" class="w-32 bg-yellow-600 hover:bg-yellow-700" @click="sendBack()">
                <Icon slot="before" iconName="Reply" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
              </PrimaryButton>

              <PrimaryButton text="AKBSへ登録" class="w-36 bg-pink-600 hover:bg-pink-700" @click="register()">
                <Icon slot="before" iconName="CheckCircle" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
              </PrimaryButton>
            </template>
          </template>

          <!-- 一覧へ移るドロップダウン -->
          <DropdownButton text="一覧" size="font-bold text-white rounded bg-transparent" :menus="listMenu" :menuWidth="160" class="ml-4" @select="selectList">
            <Icon slot="before" iconName="ViewList" class="h-4 w-4" />
            <Icon slot="after" iconName="ChevronDown" class="h-4 w-4" />
          </DropdownButton>
        </div>
      </template>
    </PageHeader>

    <main class="page-main absolute top-20 w-screen bottom-4 -mt-2 leading-8">
      <div class="mx-auto px-2 sm:px-4 h-full">
        <div ref="form" class="form-scroll h-full bg-gray-200 rounded-md border border-gray-300 overflow-auto px-2 pb-4">

          <!-- 契約の表より上の部分（注意書き・差戻理由・申請の概要・影響される清掃員）をひとまとめにして、高さの変化を見張る（watchTopHeight）。
               下の契約で変更の方法を押すと「影響される清掃員」の枠が出たり伸びたりして、押した契約が下へ逃げるので、伸びた分だけスクロールして位置を保つ -->
          <div ref="topWrap" class="top-wrap">

          <!-- 注意書き -->
          <div v-if="isRegistered" class="mt-3 px-1 text-xs font-bold text-red-700 leading-5">
            この申請はAKBSへ登録済みです。内容の変更や再登録はできません。
          </div>
          <div v-else-if="deletedFinal" class="mt-3 px-1 text-xs font-bold text-red-700 leading-5">
            この申請は削除済みで、メインタスクも完了しているため、復活できません（閲覧のみ）。
          </div>
          <!-- 承認者として開いたが自分の申請（作成者本人は承認できない） -->
          <div v-else-if="isPending && isApprover && isMyRequest" class="mt-3 px-1 text-xs font-bold text-red-700 leading-5">
            自分が作成した申請は承認できません。他の承認者に確認してもらってください（閲覧のみ）。
          </div>
          <div v-else-if="isPending && !isApprover" class="mt-3 px-1 text-xs font-bold text-red-700 leading-5">
            この申請は承認待ちです。内容を直すときは、先に右上の「申請取消」を押してください。
          </div>
          <div v-else-if="!isPending" class="mt-3 px-1 text-xs font-bold text-red-700 leading-5">
            <span class="text-red-500">*</span> の項目は必須です。一時保存の場合は必須項目が未入力でも保存できます。
          </div>

          <!-- 差戻理由 -->
          <div v-if="canWriteReturnReason || val.returnReason" class="mt-4 bg-white border-2 border-yellow-500 rounded shadow">
            <div class="text-sm font-bold text-yellow-900 bg-yellow-200 rounded-t px-3 py-2 border-b border-yellow-500">
              差戻理由
            </div>
            <div class="px-3 py-3">
              <WmsTextInput v-if="canWriteReturnReason" name="newReturnReason" caption="今回の差戻理由" v-model="newReturnReason" :multiline="true" :rows="3" class="mb-3" />
              <template v-if="val.returnReason">
                <label v-if="canWriteReturnReason" class="block text-xs font-bold text-gray-700">これまでの差戻理由</label>
                <div class="view whitespace-pre-wrap" :class="canWriteReturnReason ? 'mt-1' : ''">{{ val.returnReason }}</div>
              </template>
            </div>
          </div>

          <!-- 申請の概要（閲覧のみ。新規登録は受注タスクの内容なので、欄の名前に「受注の」を付けて受注タスクの情報だと分かるようにする） -->
          <div class="mt-4 bg-white border border-indigo-200 rounded shadow">
            <div class="text-sm font-bold text-indigo-900 bg-indigo-50 rounded-t px-3 py-2 border-b border-indigo-200">
              申請の概要
            </div>
            <div class="px-3 py-2 flex flex-wrap gap-x-8 gap-y-1">
              <div>
                <div class="cap">種別</div>
                <div class="view">{{ typeText }}</div>
              </div>
              <div v-if="!isCreate">
                <div class="cap">{{ isEnd ? '解約日' : '変更日（適用開始日）' }}</div>
                <div class="view">{{ formatDate(rp.changeDate) }}</div>
              </div>
              <div>
                <div class="cap">{{ isCreate ? '受注の得意先' : '得意先' }}</div>
                <div class="view">
                  {{ rp.clientName }}（{{ rp.clientCode || 'コード無し' }}）
                  <template v-if="rp.changeClient">
                    <span class="text-gray-500 px-2">⇒</span>
                    <span class="text-red-700 font-bold">{{ rp.clientName2 }}（{{ rp.clientCode2 }}）</span>
                  </template>
                </div>
              </div>
              <!-- 新規登録：受注の人数（見積で決めた人数の合計）。足した契約コードがこれより少ないと申請できない -->
              <div v-if="isCreate">
                <div class="cap">受注の人数（見積の人数の合計）</div>
                <div class="view text-center">{{ orderPeople }}名</div>
              </div>
              <div>
                <div class="cap">変更の内訳</div>
                <!-- 1件以上あるものだけ札で出す（色は変えた欄と同じ黄色） -->
                <div v-if="tallyList.length" class="tally-list">
                  <span v-for="t in tallyList" :key="'tally' + t.mode" class="tally-tag">{{ t.text }}<b>{{ t.count }}</b>件</span>
                </div>
                <div v-else class="view">なし</div>
              </div>
            </div>

            <!-- 新規登録：受注の内容（対象の作業内容と人数。受注画面の【日常清掃】などの見出しと同じ）
                 承認者が、何の受注の契約詳細かをすぐ見て取れるように出す。受注が親で、足りない情報をこの画面で補う -->
            <div v-if="isCreate" class="px-3 pb-2">
              <div class="cap">受注の内容（作業内容と人数）と、契約詳細との比べ</div>
              <!-- 作業内容ごとに1行の表。受注タスク（週の回数 × 1回の時間 × 人数 × 4.3 ＝ 見積の「○○時間/月」と同じ数。週の回数 × 人数）と、
                   その作業内容に属する契約コードぜんぶ（受注から作った分＋手で足して作業内容を選んだ分）の月の時間（曜日ごとの実働 × 勤務形態の係数）・週の回数（係数 ÷ 4.3）を列でそろえて並べる
                   受注の「週4.5回」の .5 は勤務の形としては決まっていないので、形ではなく数で比べる。許す差（整数なら 1%・.5 が付けば 5%）以上違う数のセルだけ黄色（申請時に確認ダイアログ。お客様との決まり） -->
              <table v-if="orderItems.length" class="order-table mt-1">
                <thead>
                  <tr>
                    <th rowspan="2" class="col-no">受注</th>
                    <th rowspan="2" class="col-name">作業内容</th>
                    <th rowspan="2" class="col-people">人数</th>
                    <th colspan="2" class="grp-order">受注タスク</th>
                    <th colspan="3" class="grp-contract">契約詳細（この画面）</th>
                    <th rowspan="2" class="col-judge">判定</th>
                  </tr>
                  <tr>
                    <th class="grp-order col-num">時間/月</th>
                    <th class="grp-order col-num">週の回数</th>
                    <th class="grp-contract col-num">時間/月</th>
                    <th class="grp-contract col-num">週の回数</th>
                    <th class="grp-contract col-cnt">契約コード</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, ii) in orderItems" :key="'oi' + ii" v-tooltip="compareTip(item)">
                    <td class="col-no"><span class="order-badge">受注{{ item.no }}</span></td>
                    <td class="col-name"><span class="order-item-name">{{ orderItemName(item) }}</span></td>
                    <td class="col-people">
                      <div class="order-item-people">{{ item.people }}名の手配</div>
                      <!-- 人数より勤務の行が少ない作業内容：足りない分は勤務が空の契約コードにしてあるので、赤く知らせる -->
                      <div v-if="blankCount(item)" class="order-item-blank">勤務が空の契約コード {{ blankCount(item) }}件（勤務を入力してください）</div>
                    </td>
                    <td class="col-num"><template v-if="item.monthlyHours"><b>{{ item.monthlyHours }}</b> 時間</template><span v-else class="unknown">不明</span></td>
                    <td class="col-num"><template v-if="orderWeekTimes(item)">週 <b>{{ orderWeekTimes(item) }}</b> 回</template><span v-else class="unknown">不明</span></td>
                    <td class="col-num" :class="hoursMismatch(item) ? 'mismatch' : ''"><b>{{ contractMonthlyHours(item) }}</b> 時間</td>
                    <td class="col-num" :class="weekMismatch(item) ? 'mismatch' : ''">週 <b>{{ contractWeekTimes(item) }}</b> 回</td>
                    <td class="col-cnt">{{ orderItemContracts(item).length }}件</td>
                    <td class="col-judge">
                      <span v-if="!item.monthlyHours && !orderWeekTimes(item)" class="judge-none">－</span>
                      <!-- 明らかに違う（申請できない）は赤、許す差を超えるが確認＋理由で進めるものは橙 -->
                      <span v-else-if="itemFar(item)" class="judge-far" v-tooltip="'受注タスクと明らかに違うので申請できません（月の時間 10% 超、または週の回数 1日以上）'">✕ {{ judgeText(item) }}</span>
                      <span v-else-if="itemMismatch(item)" class="judge-ng">{{ judgeText(item) }}</span>
                      <span v-else class="judge-ok">OK</span>
                    </td>
                  </tr>
                  <!-- 合計（見せるだけ。判定は作業内容ごとに行う。ユーザー指示 2026-10-09） -->
                  <tr class="total-row">
                    <td class="col-no"></td>
                    <td class="col-name">合計</td>
                    <td class="col-people">{{ orderPeople }}名</td>
                    <td class="col-num"><template v-if="orderTotalHours"><b>{{ orderTotalHours }}</b> 時間</template><span v-else class="unknown">－</span></td>
                    <td class="col-num"></td>
                    <td class="col-num"><b>{{ contractTotalHours }}</b> 時間</td>
                    <td class="col-num"></td>
                    <td class="col-cnt">{{ tally.add }}件</td>
                    <td class="col-judge"><span v-if="orderTotalHours" class="total-diff">{{ signedHours(contractTotalHours - orderTotalHours) }}時間</span><span v-else class="judge-none">－</span></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="view text-gray-500">対象の作業内容がありません</div>
            </div>

            <!-- 新規登録：契約詳細を登録する物件コード（受注で物件を選んであれば、それが初期値）
                 同じ得意先の物件から選ぶ。まだ物件マスタに無い物件は、物件コードを数字で直接入れる（物件マスタに無ければ申請時に注意だけ出す）
                 決めると、その物件に今ある契約を取り直して、足した契約コードを採番し直す（applyCreateSite） -->
            <div v-if="isCreate" class="px-3 pb-3">
              <div class="text-xs font-bold text-red-700 leading-5">
                <template v-if="orderSiteLocked">・受注タスクで選んだ物件に登録します（この画面では変えられません。違う物件なら受注タスクで選び直してください）。</template>
                <template v-else>・契約詳細を登録する物件を候補から選んでください。</template>
              </div>
              <!-- 候補は得意先の物件。得意先は受注タスクの得意先が初期値で、この画面で選び直せる（新規の得意先で受注タスクにコードが無いときなど。
                   メインタスクから得意先を選び直すのが本来だが、お客様が嫌がるので、ここでちゃんと選ぶ。受注タスクと違えば確認を取る）
                   新しい物件・得意先はビルメンツに登録されて AKBS に取り込まれるまで候補に出ない -->
              <table class="new-site-table mt-1">
                <thead>
                  <tr>
                    <th>物件名（受注タスク）</th>
                    <th>得意先</th>
                    <!-- 保存先の列は見出しも緑（セルと同じ。ユーザー指示） -->
                    <th class="th-target-site">物件（契約詳細の保存先）</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(site, si) in akbs.sites" :key="'create' + si">
                    <!-- メインタスクの物件名は薄いグレー（見るだけ）、契約詳細の保存先の物件は緑で目立たせる（ユーザー指示 2026-10-09） -->
                    <td class="font-bold cell-main-site">{{ rp.siteName || rp.buildingName || siteName(site) }}</td>
                    <td>
                      <!-- 受注タスクで物件コードの入った物件が選んであれば、得意先も物件も変えられない -->
                      <template v-if="!isReadOnly && !orderSiteLocked">
                        <multiselect
                          :value="clientItem(site)"
                          :options="clientOptions"
                          :multiple="false"
                          :show-labels="false"
                          :searchable="true"
                          :allow-empty="false"
                          :max-height="240"
                          :custom-label="clientLabel"
                          :loading="allSitesLoading"
                          track-by="clientCode"
                          placeholder="得意先コード・得意先名で探して選んでください"
                          class="new-site new-client"
                          :class="site.clientCode ? 'picked' : 'required-empty'"
                          @open="loadAllSites"
                          @input="clientChanged(site, $event)"
                        >
                          <!-- 選んだ得意先：名前を上に太く、コードを下に小さく（横に並べると長い名前が途中で折れて読みにくい。文字だけのときと同じ見た目） -->
                          <template slot="singleLabel" slot-scope="{ option }">
                            <div class="pick-label">
                              <div class="pick-label-name">{{ option.clientName || '' }}</div>
                              <div class="pick-label-code">{{ option.clientCode }}</div>
                            </div>
                          </template>
                          <template slot="option" slot-scope="{ option }">
                            <div class="site-opt"><b>{{ option.clientCode }}</b> {{ option.clientName }}</div>
                          </template>
                          <span slot="noResult">候補にありません</span>
                          <span slot="noOptions">{{ allSitesLoading ? '取得中…' : '候補にありません' }}</span>
                        </multiselect>
                        <!-- 受注タスクの得意先（コード無し＝登録のない新規の得意先、または選び直して違う得意先） -->
                        <div v-if="!rp.clientCode" class="site-note">受注タスクは登録のない得意先（コード無し）です。ビルメンツに登録されて AKBS に取り込まれたら、ここで得意先を選んでください。</div>
                        <div v-else-if="isClientDiff(site)" class="site-missing">受注タスクと違う得意先です。</div>
                        <div v-else class="site-chosen">受注タスクの得意先と同じです。</div>
                      </template>
                      <!-- 文字だけのとき：名前を上、コードを下に（横に並べると読みにくい。ユーザー指示 2026-10-09） -->
                      <div v-else class="site-fixed" :class="site.clientCode ? 'text-indigo-900' : 'text-gray-400'">
                        <template v-if="site.clientCode">
                          <div class="site-fixed-name">{{ site.clientName }}</div>
                          <div class="site-fixed-code">{{ site.clientCode }}</div>
                        </template>
                        <template v-else>未選択</template>
                        <div v-if="site.clientCode && isClientDiff(site)" class="site-missing">受注タスクと違う得意先です。</div>
                        <div v-else-if="orderSiteLocked && !isReadOnly" class="site-chosen">受注タスクの得意先です。</div>
                      </div>
                    </td>
                    <td class="cell-target-site">
                      <template v-if="!isReadOnly && !orderSiteLocked">
                        <!-- 選んだ得意先の物件はまだ候補に無い -->
                        <div v-if="site.clientCode && !siteListFor(site).length" class="site-note">この得意先の物件はまだ候補にありません。ビルメンツに登録されて AKBS に取り込まれると選べるようになります（一時保存はできます。申請は物件を選んでから）。</div>
                        <multiselect
                          :value="createSiteItem(site)"
                          :options="siteListFor(site)"
                          :multiple="false"
                          :show-labels="false"
                          :searchable="true"
                          :allow-empty="true"
                          :max-height="240"
                          :custom-label="siteLabel"
                          track-by="siteCode"
                          placeholder="物件コード・物件名で探して選んでください"
                          class="new-site"
                          :class="site.siteCode ? 'picked' : 'required-empty'"
                          :disabled="!site.clientCode"
                          @input="createSiteChanged(site, $event)"
                        >
                          <!-- 選んだ物件：名前を上に太く、コードを下に小さく（得意先と同じ） -->
                          <template slot="singleLabel" slot-scope="{ option }">
                            <div class="pick-label">
                              <div class="pick-label-name">{{ option.site1 }} {{ option.site2 }} <span class="site-opt-flg">{{ option.agreementFlg }}</span></div>
                              <div class="pick-label-code">{{ option.siteCode }}</div>
                            </div>
                          </template>
                          <!-- 候補には住所も出して、同じ名前の物件を取り違えないようにする -->
                          <template slot="option" slot-scope="{ option }">
                            <div class="site-opt">
                              <div><b>{{ option.siteCode }}</b> {{ option.site1 }} {{ option.site2 }} <span class="site-opt-flg">{{ option.agreementFlg }}</span></div>
                              <div class="site-opt-addr">{{ option.zip }} {{ option.address1 }} {{ option.address2 }}</div>
                            </div>
                          </template>
                          <span slot="noResult">候補にありません</span>
                          <span slot="noOptions">候補にありません</span>
                        </multiselect>
                        <!-- 選んだ物件の住所（名前と住所で確かめてもらう） -->
                        <div v-if="createSiteItem(site)" class="site-chosen">{{ createSiteItem(site).zip }} {{ createSiteItem(site).address1 }} {{ createSiteItem(site).address2 }}</div>
                        <!-- 控えてある物件コードが候補に無い（前に直接入力したもの）：申請では止まるので選び直してもらう -->
                        <div v-else-if="site.siteCode" class="site-missing">控えてある物件コード {{ site.siteCode }} は候補にありません。候補から選び直してください。</div>
                      </template>
                      <div v-else class="site-fixed" :class="site.siteCode ? 'text-indigo-900' : 'text-gray-400'">
                        <template v-if="site.siteCode">
                          <div class="site-fixed-name">{{ siteName(site) }}</div>
                          <div class="site-fixed-code">{{ site.siteCode }}</div>
                        </template>
                        <template v-else>未選択</template>
                        <template v-if="orderSiteLocked && !isReadOnly">
                          <div v-if="createSiteItem(site)" class="site-chosen">{{ createSiteItem(site).zip }} {{ createSiteItem(site).address1 }} {{ createSiteItem(site).address2 }}</div>
                          <div v-else class="site-missing">受注タスクの物件コード {{ site.siteCode }} は AKBS の物件マスタにありません。受注タスクの物件を確認してください。</div>
                        </template>
                      </div>
                      <!-- 受注タスクの物件名と物件マスタの物件名が違う：受注タスクで物件を選んだあとに物件名を貼り替えると、物件コードと画面の物件名がずれる。申請時に確認 -->
                      <div v-if="orderSiteNameDiff(site)" class="site-missing">受注タスクの物件名「{{ orderSiteName }}」と、物件マスタの物件名「{{ siteName(createSiteItem(site)) }}」が違います。受注タスクの物件を確認してください。</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 得意先変更のとき：物件ごとに変更後の物件コードを候補から選んでもらう（入力ミスを防ぐため）
                 メインタスクの「物件コード変更」のチェックは 2026-09-23 に無くした（ユーザー指示）ので、物件コードが変わるのは得意先変更だけ -->
            <div v-if="newSiteTargets.length" class="px-3 pb-3">
              <div class="text-xs font-bold text-red-700 leading-5">
                ・物件コードが変わります。変更後の物件コードを入れてください。「変更（履歴残す）」で足す行がその物件コードで登録されます。
                <span v-if="rp.changeClient" class="font-normal text-gray-600 pl-2">{{ rp.clientCode2 ? '（変更後の得意先の物件から選べます）' : '（契約中で同じ物件名の物件から選べます）' }}</span>
              </div>
              <table class="new-site-table mt-1">
                <thead>
                  <tr>
                    <th>物件名</th>
                    <th>現在の物件コード</th>
                    <th></th>
                    <th>変更後の物件コード</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="site in newSiteTargets" :key="'new' + site.siteCode">
                    <td class="font-bold">{{ siteName(site) }}</td>
                    <td class="text-center font-bold text-indigo-900">{{ site.siteCode }}</td>
                    <td class="text-center text-gray-500">⇒</td>
                    <td>
                      <template v-if="!isReadOnly">
                        <WmsSuggestInput
                          :name="'newSite' + site.siteCode"
                          caption=""
                          v-model="site.newSiteName"
                          :displayField="['siteCode', 'site1', 'site2', 'agreementFlg']"
                          :selectionItems="newSiteCandidates(site)"
                          :error="!!site.newSiteName && !site.newSiteCode"
                          errorMessage="候補から選択してください。"
                          class="new-site"
                          @selected="newSiteSelected(site, $event)"
                          @change="newSiteChanged(site)"
                        >
                        </WmsSuggestInput>
                      </template>
                      <div v-else class="text-center" :class="site.newSiteCode ? 'font-bold text-indigo-900' : 'text-gray-400'">{{ site.newSiteCode || '未入力' }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- メモ（承認者などに伝えたいこと。req_akbs_contract.memo）
                 何か書いてあるときは見落とされないよう、欄をオレンジ色にする（清掃員の申請画面 AkbsInputForm と同じ見た目） -->
            <div class="px-3 pb-3">
              <WmsTextInput name="memo" caption="メモ（承認者などに伝えたいこと）" v-model="val.memo" :multiline="true" :rows="2" :disabled="isReadOnly" :class="hasMemo ? 'memo-filled' : ''" />
            </div>
          </div>

          <!-- 清掃員の雇い入れタスク
               契約詳細を変えると、その契約で働いている清掃員の雇用指示も直す必要がある。この画面では雇用指示は書かず、
               作成者が承認を待たずに、ここから雇い入れ（清掃員）のメインタスクを作る（清掃員1人に1件。サブタスクも一緒に登録）
               ・契約終了の契約に清掃員がいる：作成必須（チェックは外せない。未作成のまま一時保存・申請を押すと、確認して作る）
               ・それ以外：作るかどうかは作成者に任せる
               ・同じ清掃員のまだ AKBS 登録されていない雇い入れが既にある（札「既にタスクあり」。この画面から作った分も同じ扱い）：
                 新しく作らない。✓欄も出さない（✓の意味は「雇い入れタスクを作る」だけ）
               ・清掃員の申請（req_akbs）の「契約詳細の変更内容」（contract_notes。見るだけのメモ）へ今回の変更を書くのはバックで、相手は
                 契約終了の清掃員の申請と、この画面から作った雇い入れタスクの申請だけ（保存のたびに自動）
                 ほかで作った雇い入れの申請には、契約終了でなければ何も書かない（契約が変わっても勤務を変えない清掃員もいる。
                 勤務時間が増えるのを断った、別の契約コードで埋め合わせる、など。必要なら作成者が申請画面を開いて直す）
                 あるかどうかは清掃員の申請（req_akbs）だけで決める。メインタスクは引かないので、種別などは出さず、「開く」（申請画面）と、req_akbs に控えた問い合わせNo だけ
                 この契約詳細の申請から作った雇い入れ（req_akbs.contract_form_id）は札「作成済み」、ほかで作った雇い入れは「既にタスクあり」
               変更のある契約に清掃員がいなければ枠ごと出さない。色は紫ベース（ほかの枠の藍色と見分ける。紫＝清掃員） -->
          <div v-if="empRows.length" class="emp-card mt-4 bg-white rounded shadow">
            <!-- 見出しはほかの枠（藍色）と違う紫。下の契約の表の清掃員の行と同じ色にして「紫＝清掃員」で揃える -->
            <div class="emp-card-head text-sm font-bold rounded-t px-3 py-2">
              {{ isEnd ? '契約解約' : '契約詳細変更' }}により、影響される清掃員
              <span class="font-bold text-xs text-gray-800 pl-3">ここから雇い入れタスクを作れます。すでに同一清掃員の他の申請がある場合は、タスクを新規で作らずに既存の申請を編集してください。</span>
            </div>
            <div class="px-3 py-3">
              <!-- ✓＝雇い入れタスクを作る。AKBS登録済みは何もできないので出さない -->
              <div v-if="!isRegistered" class="emp-lead">
                雇い入れタスクを作る場合は✓を入れてください。契約終了の場合は雇い入れタスク作成必須です。
              </div>

              <table class="emp-task-table">
                <thead>
                  <tr>
                    <th class="w-12">✓</th>
                    <th>雇入れタスク作成状況</th>
                    <th>対象清掃員</th>
                    <th>変更のある物件</th>
                    <th>契約コード</th>
                    <th>契約詳細の変更</th>
                    <th>雇い入れ種別</th>
                    <th>タスク依頼部署</th>
                    <th>タスク担当者</th>
                    <th>タスク期限</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- 清掃員1人がひとまとまり。複数の契約にいる人は、物件名・契約コード・変更内容だけ行を分ける -->
                  <template v-for="r in empRows">
                    <tr v-for="(c, ci) in r.cons" :key="'emp' + r.staffCode + '-' + ci" :class="r.state">
                      <template v-if="ci === 0">
                        <td :rowspan="r.cons.length" class="text-center">
                          <!-- 未作成の行だけ：✓でタスクを作る。契約終了の清掃員は外せない -->
                          <input
                            v-if="canEditEmp(r)"
                            type="checkbox"
                            v-model="r.edit.checked"
                            :disabled="r.must"
                            class="rounded"
                          />
                          <span v-else class="text-gray-400">-</span>
                        </td>
                        <td :rowspan="r.cons.length">
                          <!-- この契約詳細の申請から作った雇い入れは「作成済み」、ほかで作った雇い入れは「既にタスクあり」 -->
                          <span v-if="r.state === 'exist'" class="emp-chip" :class="r.task.created ? 'done' : 'exist'">{{ r.task.created ? '作成済み' : '既にタスクあり' }}</span>
                          <span v-else-if="r.must" class="emp-chip must">タスク作成必須</span>
                          <span v-else class="emp-chip todo">未作成</span>
                        </td>
                        <td :rowspan="r.cons.length">
                          <span class="font-bold">{{ r.staffName }}</span>
                          <span class="emp-code">{{ r.staffCode }}</span>
                        </td>
                      </template>

                      <td>{{ c.siteName }}</td>
                      <td class="text-center emp-cd">{{ c.cdContract }}</td>
                      <!-- 契約終了は清掃員のシフトに影響が出るので赤字にする -->
                      <td><span :class="c.mode === 'end' ? 'emp-end' : ''">{{ c.modeText }}</span><span v-if="c.cdChange" class="emp-code">（物件コード変更）</span></td>

                      <template v-if="ci === 0">
                        <!-- 未作成：ここで決める（初期値は勤怠採用課・未定・期限は翌日） -->
                        <template v-if="canEditEmp(r)">
                          <td :rowspan="r.cons.length">
                            <select v-model="r.edit.kind" class="emp-kind-select" @change="r.edit.kindTouched = true">
                              <option v-for="k in empKinds" :key="'ek' + k.id" :value="k.id">{{ k.name }}</option>
                            </select>
                          </td>
                          <!-- 部署・担当者は候補から1つ選ぶ（検索可）。裏で持つ ID と名前が必ず一致するように、名前の手入力はさせない -->
                          <td :rowspan="r.cons.length" class="emp-select-cell">
                            <multiselect
                              v-model="r.edit.department"
                              :options="departmentList"
                              :multiple="false"
                              :searchable="true"
                              :show-labels="false"
                              :allow-empty="false"
                              :max-height="240"
                              label="name"
                              track-by="departmentId"
                              class="task-select"
                              placeholder="部署を選ぶ"
                              @input="empDepartmentSelected(r.edit)"
                            ></multiselect>
                          </td>
                          <td :rowspan="r.cons.length" class="emp-select-cell">
                            <multiselect
                              v-model="r.edit.staff"
                              :options="empStaffList(r.edit)"
                              :multiple="false"
                              :searchable="true"
                              :show-labels="false"
                              :allow-empty="true"
                              :max-height="240"
                              label="userName"
                              track-by="userId"
                              class="task-select"
                              placeholder="担当者を選ぶ"
                            ></multiselect>
                          </td>
                          <td :rowspan="r.cons.length" class="emp-date-cell">
                            <WmsDateField :name="'empLimit' + r.staffCode" caption="" v-model="r.edit.limitDate" :clearable="false" class="date-field" />
                          </td>
                        </template>

                        <!-- 未作成で AKBS登録済み（完了した申請）：作れないので空 -->
                        <template v-else-if="r.state === 'todo'">
                          <td :rowspan="r.cons.length" class="text-gray-400">-</td>
                          <td :rowspan="r.cons.length" class="text-gray-400">-</td>
                          <td :rowspan="r.cons.length" class="text-gray-400">-</td>
                          <td :rowspan="r.cons.length" class="text-gray-400">-</td>
                        </template>

                        <!-- 既にタスクあり：同じ清掃員の AKBS登録前の申請がある（この画面から作った分も同じ）。中身は申請画面で見てもらう
                             メインタスクは引いていないので、種別・依頼部署などは出さず、右の4列をまとめて案内にする -->
                        <template v-else>
                          <td :rowspan="r.cons.length" colspan="4">
                            <a href="#" class="emp-open" @click.prevent="openEmpForm(r.task)">開く</a>
                            <!-- 雇い入れメインタスクの問い合わせNo（req_akbs に控えたもの。控える前に作られた申請は空なので出さない） -->
                            <span v-if="r.task.requestNo" class="emp-no">No.{{ r.task.requestNo }}</span>
                            <!-- AKBS登録済み（✓が無い）のときだけ、記載してあるかを出す -->
                            <span v-if="empNoteText(r)" class="emp-note">{{ empNoteText(r) }}</span>
                          </td>
                        </template>
                      </template>
                    </tr>
                  </template>
                </tbody>
              </table>

              <!-- この画面の保存とは独立していて、承認待ちの間も使える（AKBS登録済みは完了した申請なので出さない）
                   未作成で✓の人の雇い入れタスクを作る -->
              <div v-if="!isRegistered" class="mt-3 flex items-center">
                <PrimaryButton :text="empButtonText" size="sm" class="emp-create-btn" :disabled="!empCheckedCount" @click="createEmpTasks()" />
              </div>
            </div>
          </div>

          </div>
          <!-- ここまでが高さを見張る上の部分（topWrap） -->

          <!-- 承認待ち・AKBS登録済みは閲覧のみ（fieldset の disabled で中の入力欄をまとめて止める） -->
          <fieldset :disabled="isReadOnly" :class="isReadOnly ? 'readonly' : ''">

            <!-- 対象物件の見出しと「物件を追加」（メインタスクは入口なだけで、物件の増減はこの画面で行う。足せるのは同じ得意先の物件だけ） -->
            <div class="mt-4 flex items-center">
              <div class="text-sm font-bold text-gray-700">対象物件<span class="pl-3">{{ akbs.sites.length }}件</span></div>
              <!-- 新規登録は受注の物件1つだけ（物件は上の概要で決める）なので、物件の追加・除外は出さない -->
              <PrimaryButton v-if="!isReadOnly && !isCreate" text="＋ 物件を追加" size="xs" class="ml-4" @click="openSiteModal()" />
              <div v-if="!isReadOnly && !isCreate" class="text-xs text-gray-500 ml-3">同じ得意先の物件から選べます</div>
            </div>

            <div v-if="!akbs.sites.length && !isCreate" class="mt-2 px-1 text-sm font-bold text-gray-600">
              契約詳細を変更する物件がありません。「＋ 物件を追加」から物件を選んでください。
            </div>

            <!-- 受注タスクと違う理由を書いてもらうモーダル（申請で、月の時間・週の回数が受注タスクと違うのに「はい」で進むとき。書かないと進めない。承認では出さない） -->
            <div v-if="reasonModal.open" class="reason-overlay" @click.self="closeDiffReason(false)">
              <div class="reason-box">
                <div class="reason-title">受注タスクと違う理由</div>
                <div class="reason-desc">次の作業内容は、契約詳細の月の時間か週の回数が受注タスクと違います。進めるには理由を書いてください。理由はメモに残り、承認者に伝わります。</div>
                <pre class="reason-lines">{{ reasonModal.lines }}</pre>
                <!-- v-model だと日本語の変換中（確定前）は値が入らず「この理由で進む」が押せないままに見えるので、input のたびに入れる -->
                <textarea :value="reasonModal.text" rows="4" class="reason-text" placeholder="例：お客様の希望で、火曜だけ2人体制にするため" @input="reasonModal.text = $event.target.value"></textarea>
                <div class="reason-btns">
                  <button type="button" class="reason-btn cancel" @click="closeDiffReason(false)">キャンセル</button>
                  <button type="button" class="reason-btn ok" :disabled="!String(reasonModal.text || '').trim()" @click="closeDiffReason(true)">この理由で進む</button>
                </div>
              </div>
            </div>

            <!-- 物件追加モーダル（変更処理の画面と同じ部品。既に入っている物件は出さない） -->
            <OrderChangeSiteModal
              v-model="siteModal"
              :open="siteModal"
              :list="addableSites"
              :clientName="rp.clientName || ''"
              @modalSiteSelected="sitesAdded"
            />

            <!-- 物件ごとにアコーディオン -->
            <div v-for="site in akbs.sites" :key="site.siteCode" class="mt-4 bg-white border border-indigo-200 rounded shadow">

              <!-- 見出し（クリックで開閉） -->
              <div
                class="flex items-center cursor-pointer select-none text-sm font-bold text-indigo-900 bg-indigo-50 hover:bg-indigo-100 rounded-t px-3 py-2 transition-colors"
                :class="opened[site.siteCode] ? 'border-b border-indigo-200' : ''"
                @click="toggle(site)"
              >
                <span class="mr-2 text-xs">{{ opened[site.siteCode] ? '▼' : '▶' }}</span>
                {{ siteName(site) }}
                <span class="font-normal text-gray-600 pl-3">物件コード：{{ site.siteCode || '未入力' }}</span>
                <span v-if="isEndedSite(site)" class="text-red-700 pl-3">解約済み</span>
                <!-- 開いていなくても直した箇所が分かるようにする（雇用指示の表の見出しと同じ出し方） -->
                <span v-if="siteChangedFieldCount(site)" class="changed-count">（{{ siteChangedFieldCount(site) }}箇所変更）</span>
                <span v-if="siteAddedCount(site)" class="changed-count">（契約コード追加{{ siteAddedCount(site) }}件）</span>
                <span v-if="!siteChangeCount(site)" class="font-normal text-gray-500 text-xs pl-3">契約 {{ site.contracts.length }}件<span class="pl-2">変更なし</span></span>

                <!-- 終了した行も表示（登録済み一覧と同じ。初期値はオフ）。件数を添え、0件なら押せない -->
                <label
                  class="ml-auto font-normal text-xs flex items-center gap-1"
                  :class="endedCount(site) ? 'text-gray-600 cursor-pointer' : 'text-gray-400 cursor-default'"
                  @click.stop
                >
                  <input type="checkbox" v-model="showEnded[site.siteCode]" :disabled="!endedCount(site)" class="rounded" />
                  終了した行も表示（{{ endedCount(site) }}件）
                </label>
                <!-- 変更なしのため除外する（この物件を対象から外す。押すと確認ダイアログ） -->
                <button v-if="!isReadOnly && !isCreate" type="button" class="remove-site-btn ml-4" v-tooltip="'この物件を申請の対象から外します。入力した内容があれば消えます。'" @click.stop="removeSite(site)">
                  <Icon iconName="X" iconType="outline" :strokeWidth="2.5" class="w-3.5 h-3.5 mr-1" />
                  変更なしのため除外する
                </button>
              </div>

              <!-- 中身 -->
              <div class="accordion px-3" :class="opened[site.siteCode] ? 'open' : ''">
                <div v-if="!site.contracts.length" class="py-3 text-sm text-gray-500">この物件には契約詳細情報がありません。</div>
                <!-- 表の枠は横スクロールの枠にしない（見出しの行を上に固定する sticky が、一番近いスクロールする枠 .form-scroll を基準にするため。横に長い分も .form-scroll がスクロールする） -->
                <div v-if="site.contracts.length" class="py-3 con-table-wrap">
                  <table class="con-table">
                    <colgroup>
                      <col class="col-ops"><col class="col-con"><col class="col-period">
                      <col v-for="w in weekList" :key="'col' + w.id" class="col-day">
                      <col class="col-work">
                      <col class="col-attr"><col class="col-attr"><col class="col-attr"><col class="col-attr">
                    </colgroup>
                    <thead>
                      <tr>
                        <th>変更の方法</th>
                        <th>契約</th>
                        <th>契約期間</th>
                        <th v-for="w in weekList" :key="'th' + w.id" :class="{ sun: w.id === 'Sunday' }">{{ w.name }}</th>
                        <th class="attr-start">作業内容</th>
                        <th class="attr">祝日勤務</th>
                        <th class="attr">ゴミ搬出</th>
                        <th class="attr attr-narrow">全社休日出勤</th>
                        <th class="attr">時間指定</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- 物件コード×契約コードごとにひとまとまり。契約ごとに見出しの帯（契約コード）を置き、契約の間は少し空ける -->
                      <template v-for="(con, ci) in site.contracts">
                        <tr v-if="ci > 0" :key="'gap' + con.key" class="gap"><td colspan="15"><div class="gap-bar"></div></td></tr>

                        <!-- 契約の見出しの帯（区切りと名札を兼ねる。各行の契約セルには契約コードを出さない） -->
                        <!-- 同じ物件のほかの契約で変更を始めたら、変更なしの契約はこの帯の1行に畳む（帯を押すと開閉。閲覧のみでも押せるよう button にしない） -->
                        <tr :key="'head' + con.key" class="con-head" :class="canFold(site, con) ? 'foldable' : ''" @click="toggleCon(site, con)">
                          <td colspan="15">
                            <span v-if="canFold(site, con)" class="con-head-mark">{{ isFolded(site, con) ? '▶' : '▼' }}</span>
                            契約コード <b>{{ con.cdContract }}</b>
                            <span v-if="con.isNewContract" class="con-head-new">新規追加</span>
                            <!-- 受注から作った契約コード：受注のどの作業内容の何人目か。受注と違う欄（黄色）があれば「初期状態に戻す」で受注の今の値に戻せる -->
                            <span v-if="con.orderName" class="con-head-order">{{ con.orderName }}</span>
                            <!-- 手で足した契約コード（新規登録で作業内容が2つ以上）：受注のどの作業内容の分かを選ぶ（作業内容ごとに受注タスクと比べるため。申請時に必須） -->
                            <span v-else-if="isCreate && con.isNewContract && orderItems.length > 1" class="con-head-item" @click.stop>
                              <span class="con-head-item-cap">受注の作業内容</span>
                              <multiselect
                                v-if="!isReadOnly"
                                :value="orderItemOf(con)"
                                :options="orderItems"
                                :multiple="false"
                                :show-labels="false"
                                :searchable="false"
                                :allow-empty="true"
                                :max-height="200"
                                :custom-label="orderItemText"
                                track-by="key"
                                placeholder="選んでください"
                                class="con-head-item-select"
                                :class="con.orderItem ? '' : 'required-empty'"
                                :ref="'orderItem' + con.key"
                                @input="setOrderItem(con, $event)"
                                @open="attendanceOpened('orderItem' + con.key)"
                                @close="attendanceClosed"
                              />
                              <b v-else>{{ orderItemOf(con) ? orderItemText(orderItemOf(con)) : '未選択' }}</b>
                            </span>
                            <button v-if="!isReadOnly && con.orderKey && hasOrderDiff(con)" type="button" class="con-head-reset" v-tooltip="'黄色の欄を受注画面の今の値に戻します'" @click.stop="resetFromOrder(con)">初期状態に戻す</button>
                            <span v-if="con.mode === 'none'" class="con-head-none">変更なし</span>
                            <span v-if="canFold(site, con)" class="con-head-fold">{{ isFolded(site, con) ? '開く' : '閉じる' }}</span>
                          </td>
                        </tr>

                        <template v-if="!isFolded(site, con)">

                        <!-- 止める文（変更日が最新の行より前、契約終了なのに未来の行がある） -->
                        <tr v-if="con.blocked" :key="'blk' + con.key" class="grp-first">
                          <td class="ops grp-l" :rowspan="groupRows(site, con)">
                            <div class="ops-list">
                              <!-- 未操作：3つのボタン（雇用指示の表と同じ作り）。選ぶと状態の文字＋取消に変わる -->
                              <template v-if="con.mode === 'none' && !isReadOnly">
                                <button type="button" class="op-btn stack overwrite" v-tooltip="'今の内容を書き換えます。変更前の内容は残りません。'" @click="changeMode(site, con, 'over')">
                                  修正（上書き）
                                  <span class="op-sub">（履歴なし）</span>
                                </button>
                                <button type="button" class="op-btn stack keep" v-tooltip="'今の契約内容は履歴として残ります。'" @click="changeMode(site, con, 'new')">
                                  変更
                                  <span class="op-sub">（履歴残す）</span>
                                </button>
                                <button type="button" class="op-btn finish" v-tooltip="'この契約を終了します。契約終了日の入力が必要です。'" @click="changeMode(site, con, 'end')">
                                  契約終了
                                </button>
                              </template>
                              <template v-else>
                                <span class="mode-state" :class="con.mode">{{ modeText(con.mode) }}</span>
                                <button v-if="!isReadOnly" type="button" class="op-btn cancel" @click="changeMode(site, con, 'none')">
                                  <Icon iconName="X" iconType="outline" :strokeWidth="2" class="w-3.5 h-3.5 mr-1" />
                                  取消
                                </button>
                              </template>
                            </div>
                          </td>
                          <td colspan="14" class="blocked grp-r">{{ con.blocked }}</td>
                        </tr>

                        <!-- 行（契約詳細情報マスタの1行ずつ）。勤務の行と、その下の備考の行の2行で1件 -->
                        <template v-for="(row, ri) in visibleRows(site, con)">
                        <tr
                          :key="'r' + con.key + row.key"
                          :class="[rowClass(con, row), !con.blocked && ri === 0 ? 'grp-first' : '']"
                        >
                          <!-- 変更の方法（契約1件の全部の行にまたがる） -->
                          <td v-if="!con.blocked && ri === 0" class="ops grp-l" :rowspan="groupRows(site, con)">
                            <div class="ops-list">
                              <!-- 未操作：3つのボタン（雇用指示の表と同じ作り）。選ぶと状態の文字＋取消に変わる -->
                              <template v-if="con.mode === 'none' && !isReadOnly">
                                <button type="button" class="op-btn stack overwrite" v-tooltip="'今の内容を書き換えます。変更前の内容は残りません。'" @click="changeMode(site, con, 'over')">
                                  修正（上書き）
                                  <span class="op-sub">（履歴なし）</span>
                                </button>
                                <button type="button" class="op-btn stack keep" v-tooltip="'今の契約内容は履歴として残ります。'" @click="changeMode(site, con, 'new')">
                                  変更
                                  <span class="op-sub">（履歴残す）</span>
                                </button>
                                <button type="button" class="op-btn finish" v-tooltip="'この契約を終了します。契約終了日の入力が必要です。'" @click="changeMode(site, con, 'end')">
                                  契約終了
                                </button>
                              </template>
                              <template v-else>
                                <span class="mode-state" :class="con.mode">{{ modeText(con.mode) }}</span>
                                <button v-if="!isReadOnly" type="button" class="op-btn cancel" @click="changeMode(site, con, 'none')">
                                  <Icon iconName="X" iconType="outline" :strokeWidth="2" class="w-3.5 h-3.5 mr-1" />
                                  取消
                                </button>
                              </template>
                            </div>
                          </td>

                          <!-- 契約（契約コード・行の状態。状態の列は無くして、ここにまとめる） -->
                          <td class="con-cell">
                            <div class="con-code">契約コード <b>{{ con.cdContract }}</b></div>
                            <span class="row-badge" :class="badgeClass(row)">{{ badgeText(row) }}</span>
                            <div v-if="row.isNew && site.newSiteCode" class="cap mt-1">物件コード（変更後）：<span class="font-bold text-indigo-900">{{ site.newSiteCode }}</span></div>

                            <!-- 行の状態（変更後・変更前・修正・終了する） -->
                            <div class="row-state" :class="{ edit: con.mode === 'over' && !row.isNew, newrow: row.isNew }">
                              <template v-if="row.isNew">
                                <template v-if="con.mode === 'new'">変更後</template>
                                <div class="cap">{{ formatDate(row.startDateContract) || '開始日を入力' }} から</div>                              </template>
                              <template v-else-if="con.mode === 'over'">
                                修正（上書き）
                                <div><button v-if="!isReadOnly && isFuture(row)" type="button" class="del-link" @click="deleteRow(con, row)">この行を削除</button></div>
                              </template>
                              <template v-else-if="con.mode === 'end' && isLatest(con, row)">
                                <div class="text-red-700">契約終了</div>
                              </template>
                              <template v-else-if="isPrevOfNew(con, row)">
                                変更前
                                <div class="cap">{{ formatDate(row.endDateContract) || '---' }} まで</div>
                              </template>
                              <div v-if="row.notInAkbs" class="text-red-700 text-xs mt-1">AKBS側に無い行</div>
                            </div>

                            <!-- 週（月〜日の7曜日分の勤務だけ）のコピー・貼付。作業内容・祝日勤務・ゴミ搬出・全社休日出勤・時間指定・契約期間は写さない（ユーザー 2026-10-09。名前は「週をコピー／週に貼付」）
                                 コピーは見るだけの行からもできる（今ある契約を元に、足した契約コードへ写すため）。貼付は入力できる行だけ -->
                            <div v-if="!isReadOnly" class="row-copy">
                              <button type="button" class="day-btn copy" @click="copyRowValues(row)">週をコピー</button>
                              <button v-if="isEditable(con, row)" type="button" class="day-btn paste" :disabled="!copiedRow" @click="pasteRowValues(row)">週に貼付</button>
                            </div>
                          </td>

                          <!-- 契約期間 -->
                          <td class="period">
                            <div class="field">
                              <label>契約開始日<span v-if="isEditable(con, row)" class="text-red-500 ml-1">*</span></label>
                              <WmsDateField
                                v-if="isEditable(con, row)"
                                :name="'start' + site.siteCode + con.key + row.key"
                                caption=""
                                v-model="row.startDateContract"
                                :clearable="true"
                                :disabled="isReadOnly"
                                :class="['date-field', isChanged(row, 'startDateContract') ? 'changed' : '', isRequiredEmpty(row, 'startDateContract') ? 'required-empty' : '', isNg(site, con, row, 'startDateContract') ? 'ng' : '']"
                                v-tooltip="orderTip(row, 'startDateContract')"
                                @change="startChanged(con, row)"
                              />
                              <div v-else class="val" :class="isChanged(row, 'startDateContract') ? 'changed' : ''">{{ formatDate(row.startDateContract) }}</div>
                            </div>
                            <div class="field">
                              <label>契約終了日<span v-if="con.mode === 'end' && isLatest(con, row)" class="text-red-500 ml-1">*</span></label>
                              <WmsDateField
                                v-if="isEditable(con, row) || isEndEditable(con, row)"
                                :name="'end' + site.siteCode + con.key + row.key"
                                caption=""
                                v-model="row.endDateContract"
                                :clearable="true"
                                :disabled="isReadOnly"
                                :class="['date-field', isChanged(row, 'endDateContract') ? 'changed' : '', isNg(site, con, row, 'endDateContract') ? 'ng' : '']"
                                @change="reload++"
                              />
                              <!-- 変更（履歴残す）の変更前の行：終了日は変更後の開始日の前日が自動で入る（手では直せない） -->
                              <template v-else-if="isPrevOfNew(con, row)">
                                <div v-if="row.endDateContract" class="val" :class="[isChanged(row, 'endDateContract') ? 'changed' : '', isNg(site, con, row, 'endDateContract') ? 'ng' : '']">{{ formatDate(row.endDateContract) }}</div>
                                <div v-else class="auto-end" :class="isNg(site, con, row, 'endDateContract') ? 'ng' : ''">変更後の開始日を<br>入力すると自動で入ります</div>
                              </template>
                              <div v-else class="val" :class="[row.endDateContract ? '' : 'none', isChanged(row, 'endDateContract') ? 'changed' : '']">{{ formatDate(row.endDateContract) || '---' }}</div>
                            </div>
                          </td>

                          <!-- 曜日別 -->
                          <td v-for="w in weekList" :key="'d' + row.key + w.id" class="day-cell" :class="isDayOff(row, w) ? '' : 'day-on'">
                            <!-- 入力欄 -->
                            <div v-if="isEditable(con, row)" class="day">
                              <multiselect
                                v-model="row['attendance' + w.id]"
                                :options="attendanceList"
                                :multiple="true"
                                :close-on-select="false"
                                :show-labels="false"
                                :searchable="false"
                                :allow-empty="true"
                                :hide-selected="true"
                                :max-height="160"
                                :disabled="isReadOnly"
                                label="name"
                                track-by="id"
                                class="multiselect"
                                :class="[isChanged(row, 'attendance' + w.id) ? 'changed' : '', isNg(site, con, row, 'attendance' + w.id) ? 'ng' : '']"
                                v-tooltip="orderTip(row, 'attendance' + w.id)"
                                placeholder="-"
                                :ref="'attendance' + site.siteCode + con.key + row.key + w.id"
                                @input="attendanceChanged(row, w.id)"
                                @open="attendanceOpened('attendance' + site.siteCode + con.key + row.key + w.id)"
                                @close="attendanceClosed"
                              ></multiselect>

                              <template v-if="!isDayOff(row, w)">
                                <div class="times">
                                  <AkbsTimeInput
                                    v-model="row['start' + w.id]"
                                    :disabled="isReadOnly"
                                    :class="[isChanged(row, 'start' + w.id) ? 'changed' : '', isRequiredEmpty(row, 'start' + w.id) ? 'required-empty' : '', isNg(site, con, row, 'start' + w.id) ? 'ng' : '']"
                                    v-tooltip="orderTip(row, 'start' + w.id)"
                                    @change="setTime(row, w.id)"
                                  />
                                  <span class="tilde">~</span>
                                  <AkbsTimeInput
                                    v-model="row['end' + w.id]"
                                    :disabled="isReadOnly"
                                    :class="[isChanged(row, 'end' + w.id) ? 'changed' : '', isRequiredEmpty(row, 'end' + w.id) ? 'required-empty' : '', isNg(site, con, row, 'end' + w.id) ? 'ng' : '']"
                                    v-tooltip="orderTip(row, 'end' + w.id)"
                                    @change="setTime(row, w.id)"
                                  />
                                </div>
                                <div class="work">
                                  実働
                                  <input
                                    type="number"
                                    v-model="row['workingHours' + w.id]"
                                    step="0.25"
                                    min="0"
                                    :disabled="isReadOnly"
                                    class="wh"
                                    :class="[isChanged(row, 'workingHours' + w.id) ? 'changed' : '', isRequiredEmpty(row, 'workingHours' + w.id) ? 'required-empty' : '', isNg(site, con, row, 'workingHours' + w.id) ? 'ng' : '']"
                                    v-tooltip="orderTip(row, 'workingHours' + w.id)"
                                    @wheel="$event.target.blur()"
                                    @change="reload++"
                                  />
                                  h
                                </div>
                              </template>
                              <template v-else>
                                <!-- 元は勤務があったのに無くしたときは、消えた時間が見えないので印を出す -->
                                <div v-if="row.original && !isDayOff(row.original, w)" class="changed-off">勤務なしに変更</div>
                              </template>

                              <!-- コピペ（雇用指示の表と同じ。休みの曜日にも貼り付けられるよう、勤務の有無に関わらず出す） -->
                              <div v-if="!isReadOnly" class="day-copy">
                                <button type="button" class="day-btn copy" @click="copyDay(row, w.id)">コピー</button>
                                <button type="button" class="day-btn paste" :disabled="!copied" @click="pasteDay(row, w.id)">貼付</button>
                              </div>
                            </div>

                            <!-- 表示だけ（閲覧のみのときもここ。変えた欄は入力欄と同じく黄色にする） -->
                            <div v-else-if="!isDayOff(row, w)" class="day">
                              <div class="attendance-labels" :class="isChanged(row, 'attendance' + w.id) ? 'view-changed' : ''">
                                <div v-for="(a, k) in row['attendance' + w.id]" :key="'a' + k" class="attendance-label">{{ a.name }}</div>
                              </div>
                              <div class="times view-text" :class="isChanged(row, 'start' + w.id) || isChanged(row, 'end' + w.id) ? 'view-changed' : ''">{{ row['start' + w.id] || '-' }}<span class="tilde">~</span>{{ row['end' + w.id] || '-' }}</div>
                              <div class="work" :class="isChanged(row, 'workingHours' + w.id) ? 'view-changed' : ''">実働 <span class="font-bold">{{ row['workingHours' + w.id] || '0' }}</span> h</div>
                            </div>
                            <!-- 表示だけで、元は勤務があったのに無くした曜日 -->
                            <div v-else-if="row.original && !isDayOff(row.original, w)" class="day">
                              <div class="changed-off">勤務なしに変更</div>
                            </div>
                          </td>

                          <!-- 作業内容 -->
                          <td class="work-cell attr-start">
                            <template v-if="isEditable(con, row)">
                              <select v-model="row.cdContentsWork" :disabled="isReadOnly" class="cell-select" :class="[isChanged(row, 'cdContentsWork') ? 'changed' : '', isRequiredEmpty(row, 'cdContentsWork') ? 'required-empty' : '', isNg(site, con, row, 'cdContentsWork') ? 'ng' : '']" v-tooltip="orderTip(row, 'cdContentsWork')" @change="reload++">
                                <option v-for="wk in workSelections" :key="'wk' + wk.id" :value="wk.id">{{ wk.name }}</option>
                              </select>
                            </template>
                            <div v-else :class="isChanged(row, 'cdContentsWork') ? 'view-changed' : ''">{{ workName(row) }}</div>
                          </td>

                          <!-- 区分 -->
                          <template v-if="isEditable(con, row)">
                            <td class="attr">
                              <!-- 受注の祝日対応が「ゴミ出しのみ」：セルの上に印を出し、「時間入力」で時間を入れてもらう -->
                              <div v-if="row.fromOrder && row.orderHolidayGarbage" class="holiday-garbage" v-tooltip="'受注タスクの祝日対応は「ゴミ出しのみ」です。「時間入力」でゴミ出しの時間を入れてください'">※ゴミ出しのみ</div>
                              <!-- 受注タスク（見積）で決まっている区分は変えさせない（「時間入力」の時間は画面で入れる）
                                   選択肢の名前は「時間入力」（同じ表の「時間指定」の列と紛れないように。ユーザー 2026-10-09） -->
                              <select
                                :value="holidayKind(row)"
                                :disabled="isReadOnly || isOrderLocked(row, 'holiday')"
                                class="cell-select"
                                :class="[isChanged(row, 'kubunHolidayContract') ? 'changed' : '', isHolidayChangedFromDefault(row) ? 'order-holiday-changed' : '', isRequiredEmpty(row, 'kubunHolidayContract') ? 'required-empty' : '', isOrderLocked(row, 'holiday') ? 'order-locked' : '']"
                                v-tooltip="orderLockTip(row, 'holiday') || orderTip(row, 'kubunHolidayContract')"
                                @change="changeHolidayKind(row, $event.target.value)"
                              >
                                <option value="100">FULL</option>
                                <option value="hours">時間入力</option>
                                <option value="0">無</option>
                              </select>
                              <!-- 時間は小数も入れられるよう、普通の number 入力にする（NumericTextInput2 は整数に丸めてしまう） -->
                              <div v-if="holidayKind(row) === 'hours'" class="holiday-hours">
                                <input
                                  type="number"
                                  v-model="row.kubunHolidayContract"
                                  step="0.25"
                                  min="0"
                                  :disabled="isReadOnly"
                                  class="hours-input"
                                  :class="[isChanged(row, 'kubunHolidayContract') ? 'changed' : '', isHolidayChangedFromDefault(row) ? 'order-holiday-changed' : '', isRequiredEmpty(row, 'kubunHolidayContract') ? 'required-empty' : '', isNg(site, con, row, 'kubunHolidayContract') ? 'ng' : '']"
                                  @wheel="$event.target.blur()"
                                  @change="reload++"
                                />
                                <span class="unit">時間</span>
                              </div>
                            </td>
                            <!-- 有り｜無しは2つ並びのボタン（雇用指示の表の交通費区分と同じ作り）。選んだ側を塗り、変更ありは選んでいない側を黄色にする -->
                            <td class="attr">
                              <!-- 受注の祝日対応が「ゴミ出しのみ」ならゴミ搬出は「有り」で固定（変えさせない） -->
                              <div class="flag-kind" :class="[isChanged(row, 'hasCarryingoutGarbageContract') ? 'changed-kind' : '', isRequiredEmpty(row, 'hasCarryingoutGarbageContract') ? 'required-empty' : '', isNg(site, con, row, 'hasCarryingoutGarbageContract') ? 'ng-kind' : '', isOrderLocked(row, 'garbage') ? 'order-locked' : '']" v-tooltip="orderLockTip(row, 'garbage')">
                                <button v-for="y in yesNoList" :key="'g' + y.id" type="button" class="kind-btn" :class="flagIs(row.hasCarryingoutGarbageContract, y.id) ? 'selected' : ''" :disabled="isReadOnly || isOrderLocked(row, 'garbage')" @click="setFlag(row, 'hasCarryingoutGarbageContract', y.id)">{{ y.name }}</button>
                              </div>
                            </td>
                            <td class="attr">
                              <div class="flag-kind" :class="[isChanged(row, 'hasExportingCompanysHolidayContract') ? 'changed-kind' : '', isRequiredEmpty(row, 'hasExportingCompanysHolidayContract') ? 'required-empty' : '', isNg(site, con, row, 'hasExportingCompanysHolidayContract') ? 'ng-kind' : '']">
                                <button v-for="y in yesNoList" :key="'c' + y.id" type="button" class="kind-btn" :class="flagIs(row.hasExportingCompanysHolidayContract, y.id) ? 'selected' : ''" :disabled="isReadOnly" @click="setFlag(row, 'hasExportingCompanysHolidayContract', y.id)">{{ y.name }}</button>
                              </div>
                            </td>
                            <td class="attr grp-r">
                              <!-- 受注タスク（見積）で決まっている時間指定は変えさせない -->
                              <div class="flag-kind" :class="[isChanged(row, 'specificTime') ? 'changed-kind' : '', isOrderLocked(row, 'specificTime') ? 'order-locked' : '']" v-tooltip="orderLockTip(row, 'specificTime')">
                                <button v-for="y in yesNoList" :key="'s' + y.id" type="button" class="kind-btn" :class="flagIs(row.specificTime, y.id) ? 'selected' : ''" :disabled="isReadOnly || isOrderLocked(row, 'specificTime')" @click="setFlag(row, 'specificTime', y.id)">{{ y.name }}</button>
                              </div>
                            </td>
                          </template>
                          <template v-else>
                            <td class="attr">
                              <div v-if="row.fromOrder && row.orderHolidayGarbage" class="holiday-garbage" v-tooltip="'受注タスクの祝日対応は「ゴミ出しのみ」です'">※ゴミ出しのみ</div>
                              <span :class="[isChanged(row, 'kubunHolidayContract') ? 'view-changed' : '', isHolidayChangedFromDefault(row) ? 'order-holiday-changed' : '']" v-tooltip="orderTip(row, 'kubunHolidayContract')">{{ holidayText(row.kubunHolidayContract) }}</span>
                            </td>
                            <td class="attr"><span :class="isChanged(row, 'hasCarryingoutGarbageContract') ? 'view-changed' : ''">{{ flagText(row.hasCarryingoutGarbageContract) }}</span></td>
                            <td class="attr"><span :class="isChanged(row, 'hasExportingCompanysHolidayContract') ? 'view-changed' : ''">{{ flagText(row.hasExportingCompanysHolidayContract) }}</span></td>
                            <td class="attr grp-r"><span :class="isChanged(row, 'specificTime') ? 'view-changed' : ''">{{ flagText(row.specificTime) }}</span></td>
                          </template>
                        </tr>

                        <!-- 行に付く備考・クリーンプラス金額・ランク（契約詳細情報マスタの列。AKBS の契約詳細画面で書く欄と同じもの）
                             行（契約コード＋開始日）ごとの値なので、勤務と同じく入力できる行だけ直せる。変更（履歴残す）の変更後の行には変更前の行の値が写っている -->
                        <tr :key="'n' + con.key + row.key" class="note-row" :class="rowClass(con, row)">
                          <td colspan="14" class="note-cell grp-r">
                            <div class="note-fields">
                              <label class="note-label">備考</label>
                              <textarea
                                v-if="isEditable(con, row)"
                                v-model="row.note"
                                rows="1"
                                :disabled="isReadOnly"
                                class="note-input"
                                :class="isChanged(row, 'note') ? 'changed' : ''"
                                @change="reload++"
                              ></textarea>
                              <div v-else class="note-text" :class="[row.note ? '' : 'none', isChanged(row, 'note') ? 'view-changed' : '']">{{ row.note || '-' }}</div>

                              <label class="note-label">クリーンプラス金額</label>
                              <template v-if="isEditable(con, row)">
                                <input
                                  type="number"
                                  v-model="row.cleanPlus"
                                  step="1"
                                  min="0"
                                  :disabled="isReadOnly"
                                  class="clean-plus-input"
                                  :class="[isChanged(row, 'cleanPlus') ? 'changed' : '', isNg(site, con, row, 'cleanPlus') ? 'ng' : '']"
                                  @wheel="$event.target.blur()"
                                  @change="reload++"
                                />
                                <span class="note-unit">円</span>
                              </template>
                              <div v-else class="note-value" :class="[row.cleanPlus === '' || row.cleanPlus === null ? 'none' : '', isChanged(row, 'cleanPlus') ? 'view-changed' : '']">{{ cleanPlusText(row.cleanPlus) }}</div>

                              <label class="note-label">ランク</label>
                              <select v-if="isEditable(con, row)" v-model="row.rank" :disabled="isReadOnly" class="cell-select rank-select" :class="isChanged(row, 'rank') ? 'changed' : ''" @change="reload++">
                                <option value="">-</option>
                                <option v-for="r in rankList" :key="'rank' + r" :value="r">{{ r }}</option>
                              </select>
                              <div v-else class="note-value" :class="[row.rank ? '' : 'none', isChanged(row, 'rank') ? 'view-changed' : '']">{{ row.rank || '-' }}</div>
                            </div>
                          </td>
                        </tr>
                        </template>

                        <!-- 修正（上書き）のときだけ「行を追加」 -->
                        <tr v-if="con.mode === 'over' && !isReadOnly" :key="'add' + con.key" class="addrow">
                          <td colspan="14" class="grp-r">
                            <button type="button" class="add-btn" @click="addRow(site, con)">＋ 行を追加</button>
                            <span class="cap ml-3">今後、変わる予定がある場合など</span>
                          </td>
                        </tr>

                        <!-- 担当清掃員（閲覧のみ。誰が何曜日に入っているか） -->
                        <tr
                          v-for="(st, si) in con.staffs"
                          :key="'st' + con.key + st.sequenceNumber"
                          class="staff"
                          :class="[si === 0 ? 'staff-first' : '', si === con.staffs.length - 1 ? 'last' : '']"
                        >
                          <td class="con-cell">
                            <div class="staff-cap">（清掃員）</div>
                            <div class="staff-name">{{ st.nameStaff }}</div>
                          </td>
                          <td class="period">
                            <div class="field"><label>雇用指示の期間</label><div class="val">{{ formatDate(st.startDateWork) }} ～ {{ formatDate(st.endDateWork) }}</div></div>
                          </td>
                          <td v-for="w in weekList" :key="'sd' + st.sequenceNumber + w.id" class="staff-time">
                            <template v-if="!isDayOff(st, w)">
                              <!-- 勤務形態を上、時間帯を下。時間帯のあとにカッコで実働時間（数字だけ） -->
                              <div class="cap">{{ attendanceNames(st['attendance' + w.id]).join('・') }}</div>
                              <div>{{ st['start' + w.id] || '-' }} ~ {{ st['end' + w.id] || '-' }}（{{ st['workingHours' + w.id] || '0' }}）</div>
                            </template>
                            <span v-else class="text-gray-400">-</span>
                          </td>
                          <td class="work-cell attr-start">{{ workName(st) }}</td>
                          <!-- 雇用指示の祝日勤務・ゴミ搬出・全社休日出勤。時間指定は契約だけの項目なので空 -->
                          <td class="attr">{{ holidayText(st.cdKubunHoliday) }}</td>
                          <td class="attr">{{ flagText(st.hasCarryingoutGarbage) }}</td>
                          <td class="attr">{{ flagText(st.hasExportingCompanysHoliday) }}</td>
                          <td class="attr grp-r"></td>
                        </tr>
                        <!-- 担当清掃員がいないときは、太枠の下線のための行 -->
                        <tr v-if="!con.staffs.length" :key="'nost' + con.key" class="staff staff-first last">
                          <td colspan="14" class="text-gray-400 grp-r">この契約で働いている清掃員はいません</td>
                        </tr>

                        </template>
                      </template>
                    </tbody>
                  </table>
                </div>

                <!-- 契約コードを新規追加（この物件に新しい契約コードの契約詳細を足す。契約コードは自動採番、行は1件、開始日は変更日） -->
                <div v-if="!isReadOnly" class="add-contract">
                  <button type="button" class="add-btn" @click="addContract(site)">＋ 契約コードを新規追加</button>
                </div>
              </div>
            </div>
          </fieldset>

          <div class="text-transparent">{{ reload }}</div>
        </div>
      </div>
    </main>

  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import DropdownButton from '@components/DropdownButton.vue'
import PageHeader from '@components/PageHeader.vue'
import WmsTextInput from '@wmscomponents/WmsTextInput.vue'
import WmsSuggestInput from '@wmscomponents/WmsSuggestInput.vue'
import WmsDateField from '@wmscomponents/WmsDateField.vue'
import AkbsTimeInput from '../components/AkbsTimeInput.vue'
import OrderChangeSiteModal from '../../RequestPost/components/OrderChangeSiteModal.vue'
import Multiselect from 'vue-multiselect'
import * as utils from '@libs/utils'
import * as dialogs from '@libs/dialogs'
import * as backend from '@libs/backend'
import * as utilsAKBS from '@libs/utilsAKBS'
import * as requestPostManager from '@managers/requestPostManager'
import * as taskRequestManager from '@managers/taskRequestManager'
import { WEEK_LIST, AKBS_STATUS, ATTENDANCE, WORK_SELECTIONS, AKBS_SUB_TASK, isAkbsSubTask, isSameDepartmentForAkbs } from '@libs/constantsAKBS'
import { finishAfterAkbs } from '@libs/akbsMainTask'
import { contractsFromOrder, orderItems as itemsFromOrder, orderItemName, orderPeople as peopleFromOrder } from '@libs/akbsContractFromOrder'
import * as moment from 'moment'

import PageModel from '../../main/pages/PageModel.vue'

// 種別 → 表示名（req_akbs_contract.type_name に控える）
// 変更処理のメインタスクは classification（'1' 変更、'2' 解約）で決まる。受注処理のメインタスク（type が 'order'）は 3 新規
// 申請の種別名（req_akbs_contract.type_name に控える。申請一覧の CONTRACT_TYPE_NAMES と同じ短い名前。ユーザー 2026-09-22）
const TYPE_NAMES = { 1: '変更', 2: '解約', 3: '新規' }
const TYPE_CREATE = 3

// 右上の「一覧」ドロップダウンの項目
const LIST_MENU = [
  { value: 'akbs', text: 'AKBS申請一覧' },
  { value: 'main', text: 'メインタスク一覧' },
  { value: 'sub', text: 'サブタスク一覧' },
]

// 変更の方法（契約ごと。表の左の列）
const MODE_OPTIONS = [
  { value: 'none', text: '変更なし' },
  { value: 'over', text: '修正（上書き）' },
  { value: 'new', text: '変更（履歴残す）' },
  { value: 'end', text: '契約終了' },
  // この画面で足した契約コード（ボタンでは選ばない。「契約コードを追加」で入る）
  { value: 'add', text: '新規追加' },
]

// 清掃員の雇い入れタスクの宛先の初期値
// 部署は名前に「勤怠採用」を含み「デンタツ」を含まないもの（勤怠採用課。デンタツ用_勤怠採用課は除く）。担当者は「未定」
const TASK_DEPARTMENT = '勤怠採用'
const TASK_DEPARTMENT_EXCLUDE = 'デンタツ'
const TASK_STAFF = '未定'

// 清掃員の雇い入れタスクの種別（この画面で要るのは3つだけ。雇い入れと同じキーへの読み替えは requestPostManager.registerEmploymentFromContract）
const EMP_KINDS = [
  { id: 'add', name: '物件追加' },
  { id: 'change', name: '契約内容変更' },
  { id: 'end', name: '作業終了' },
]

// 曜日ごとの項目のキーの頭
const DAY_KEYS = ['attendance', 'start', 'end', 'workingHours']

// 受注タスクと契約詳細の月の時間・週の回数の、許す差の割合（これを超えると概要で黄色・申請時に確認。お客様との決まり）
// 週の回数に .5 が付くとき（週4.5回など）：.5 は勤務の形として決まっていない（隔週と第1・第3 で 2〜3% ずれる）ので 5%
// 週の回数が整数のとき（週4回など）：ほぼ一致を求める。浮動小数の誤差と、第n週の係数（÷4.3）の丸めだけを通す 1%
const TOLERANCE_HALF = 0.05
const TOLERANCE_INTEGER = 0.01
// 「明らかに違う」線（これを超えると申請・AKBS登録をエラーで止める。間は確認＋理由）
// 月の時間：受注タスクの 10% を超えて違う。週の回数：1日以上違う（週4.5回なら 4 も 5 も通り、3.5 以下・5.5 以上は止まる）
const HOURS_FAR = 0.10
const WEEK_FAR = 1

// 受注タスク（見積）で決まっていて変えられない欄のツールチップ
const ORDER_LOCK_TIP = '受注タスク（見積）で決まっているので、この画面では変えられません'

// メモに残す「受注タスクと違う理由」の段の見出しと、その段を見つける正規表現（見出しから、次の【見出し】か末尾まで）
const DIFF_REASON_HEAD = '【受注タスクと違う理由】'
const DIFF_REASON_RE = /【受注タスクと違う理由】\n?([\s\S]*?)(?=\n【|$)/

// 区分の項目（作業内容も含む）
const FLAG_KEYS = ['cdContentsWork', 'kubunHolidayContract', 'hasCarryingoutGarbageContract', 'hasExportingCompanysHolidayContract', 'specificTime']

// 有り・無しの選択肢（雇用指示の表と同じ表示。DBは integer の 1／0）
const FLAG_SELECTIONS = [
  { id: 1, name: '有り' },
  { id: 0, name: '無し' }
]

// 行に付く備考・クリーンプラス金額・ランク（契約詳細情報マスタの列 note・clean_plus・rank。AKBS の契約詳細画面で書く欄と同じもの。2026-09-23）
// 行（契約コード＋開始日）ごとの値で、勤務と同じく入力できる行だけ直せる。行のコピー・貼付では写さない（勤務内容ではないので）
const NOTE_KEYS = ['note', 'cleanPlus', 'rank']

// ランクの選択肢（AKBS の契約詳細画面と同じ A〜D）
const RANK_SELECTIONS = ['A', 'B', 'C', 'D']

// 画面で変えられる項目（元の値と比べてフラグを立てる項目）。契約期間は別に扱う
const EDIT_KEYS = [...FLAG_KEYS, ...NOTE_KEYS]
for (const w of WEEK_LIST) {
  for (const k of DAY_KEYS) {
    EDIT_KEYS.push(k + w.id)
  }
}
const DATE_KEYS = ['startDateContract', 'endDateContract']

// 受注画面で変わりうる項目（新規登録。受注と違う欄を黄色にするのはこれだけ。祝日勤務・時間指定は受注画面で変わらないので見ない。ユーザー 2026-09-22）
// 受注に勤務の行が無い分の契約コード（人数の方が多いときに足した空の行。row.orderBlank）は、勤務を入れてもらう行なので曜日の欄は見ない（ORDER_HEAD_KEYS だけ）
const ORDER_HEAD_KEYS = ['startDateContract', 'cdContentsWork']
const ORDER_KEYS = [...ORDER_HEAD_KEYS]
for (const w of WEEK_LIST) {
  for (const k of DAY_KEYS) {
    ORDER_KEYS.push(k + w.id)
  }
}
// ラウンドプラスの固定清掃の行（row.orderHolidayDefault）は、祝日勤務が「FULL」の初期値で画面で変えられるので、祝日勤務も見る（変えたら黄色＋申請・承認時に確認）
function orderKeysOf(row) {
  const keys = row.orderBlank ? ORDER_HEAD_KEYS : ORDER_KEYS
  return row.orderHolidayDefault ? [...keys, 'kubunHolidayContract'] : keys
}

// 項目の表示名（最新化の知らせ用）
function keyLabel(key) {
  const labels = { cdContentsWork: '作業内容', kubunHolidayContract: '祝日勤務', hasCarryingoutGarbageContract: 'ゴミ搬出', hasExportingCompanysHolidayContract: '全社休日出勤', specificTime: '時間指定', startDateContract: '契約開始日', endDateContract: '契約終了日', note: '備考', cleanPlus: 'クリーンプラス金額', rank: 'ランク' }
  if (labels[key]) {
    return labels[key]
  }
  for (const w of WEEK_LIST) {
    if (key.endsWith(w.id)) {
      const head = key.slice(0, -w.id.length)
      const names = { attendance: '勤務形態', start: '開始', end: '終了', workingHours: '実働' }
      return `${w.name}曜の${names[head] || head}`
    }
  }
  return key
}

/**
 * 動作確認用（確認が終わったら checkSaved と一緒に削除する）
 * 入れ子のオブジェクトを「キーの道筋 → 値」の1段に平らにする（例：sites.0.contracts.1.rows.0.startDateContract）
 * 空の配列・オブジェクトは '[]'・'{}' の文字にして、キーが消えないようにする
 * @param {*} value 平らにするもの
 * @param {String} path ここまでのキーの道筋
 * @param {Object} out 結果を溜めるもの
 */
function flatten(value, path = '', out = {}) {
  if (value !== null && typeof value === 'object') {
    const keys = Object.keys(value)
    if (!keys.length) {
      out[path] = Array.isArray(value) ? '[]' : '{}'
      return out
    }
    for (const key of keys) {
      flatten(value[key], path ? `${path}.${key}` : key, out)
    }
    return out
  }
  out[path] = value
  return out
}

/**
 * 申請（req_akbs_contract の1行）の入れ物。後から足すとリアクティブにならないため、使うキーはここで揃える
 */
function createValBase() {
  return {
    id: null, orderId: null, requestNo: '', type: null, typeName: '', status: '', changeDate: '',
    clientCode: '', clientName: '', siteNames: '',
    applicationDate: null, creator: '', creatorId: null, updateUser: null, updateDate: null,
    approver: '', approverId: null, approvedDate: null, returnReason: '',
    // 承認者などに伝えたいことを書くメモ
    memo: ''
  }
}

// 足した行のキー用の連番
let rowSeq = 0
// 足した契約コードのキー用の連番（契約コードは振り直すことがあるので、画面の中では key で契約を見分ける）
let conSeq = 0

/**
 * 開始日順に並べ替える（同じ配列を並べ替えて返す）
 */
function sortRows(rows) {
  return rows.sort((a, b) => (a.startDateContract < b.startDateContract ? -1 : a.startDateContract > b.startDateContract ? 1 : 0))
}

export default {
  extends: PageModel,
  components: {
    Icon,
    PrimaryButton,
    DropdownButton,
    PageHeader,
    WmsTextInput,
    WmsSuggestInput,
    WmsDateField,
    AkbsTimeInput,
    OrderChangeSiteModal,
    Multiselect
  },

  data() {
    return {
      // ローディング
      loading: false,
      // 対象のメインタスク（変更処理）
      rp: {},
      // 申請（req_akbs_contract の1行）
      val: createValBase(),
      // 画面の内容（物件ごと・契約コードごとに行）
      akbs: { sites: [] },
      // 物件の開閉状態（物件コード → 開いているか）
      opened: {},
      // 畳まれた「変更なし」の契約を手で開いたか（物件コード.契約コード → 開いたか）
      conOpened: {},
      // 終了した行も表示（物件コード → オンか）
      showEnded: {},
      // 右上の「一覧」ドロップダウンの項目
      listMenu: LIST_MENU,
      // 曜日
      weekList: WEEK_LIST,
      // 勤務形態の選択肢
      attendanceList: utils.clone(ATTENDANCE),
      // 作業内容の選択肢
      workSelections: WORK_SELECTIONS,
      // 有り・無しの選択肢
      yesNoList: FLAG_SELECTIONS,
      // ランクの選択肢
      rankList: RANK_SELECTIONS,
      // 保存時の矛盾チェックでNGになった欄（'物件コード.契約コード.行キー.キー'）
      errorKeys: [],
      // コピーした曜日の内容
      copied: null,
      // コピーした行の勤務内容（行ごとのコピー・貼付用。曜日のコピーとは別に持つ）
      copiedRow: null,
      // 物件追加モーダル
      siteModal: false,
      // 受注タスクと違う理由を書いてもらうモーダル（lines：違う作業内容と差、text：理由、resolve：閉じたときに返す）
      reasonModal: { open: false, lines: '', text: '', resolve: null },
      // 入力チェックでエラーのダイアログを出したか（確認ダイアログのキャンセルと区別する。承認者には「この画面で直せません」を足す）
      validateError: false,
      // 得意先の物件リスト（モーダルを開くときに取得）
      siteList: [],
      // 新規登録：得意先コード → その得意先の物件の候補（選んだ得意先ごとに取って控える）
      siteListByClient: {},
      // 新規登録：得意先の候補を作るための全物件（得意先の欄を初めて開いたときに取る）
      allSites: [],
      allSitesLoading: false,
      // 得意先変更のときの、変更後の物件コードの候補（物件コード → 候補の配列。変更後の得意先があれば全物件で同じ配列）
      newSiteLists: {},
      // メインタスクID（変更処理の _id）
      mainId: null,
      // このデータの postgres の id
      id: null,
      // 承認待ち一覧の「確認」から開いたか
      isApprover: false,
      // 削除した申請で、メインタスクが完了している（復活できない。閲覧のみ。バックの getData が返す）
      deletedFinal: false,
      // 今回の差戻理由
      newReturnReason: '',
      // 影響される清掃員の、AKBS登録前の雇い入れ（清掃員の申請 req_akbs。バックから取る）
      //   [{ staffCode, reqId: 清掃員の申請の id, reqStatus: その状況, employmentId, requestNo: 雇い入れメインタスクの問い合わせNo,
      //      created: この申請から作った雇い入れか, notified: 今回の変更内容を記載してあるか }]
      empTasks: [],
      // 未作成の行の入力（清掃員コード → { checked, kind, department, staff, limitDate }）
      // department / staff は候補リストの要素そのもの（multiselect の選択値）
      empEdits: {},
      // 種別の選択肢
      empKinds: EMP_KINDS,
      // 今日（行が終わっているか・まだ始まっていないかの判定）
      today: utils.getToday(),
      // 再描画用
      reload: 0
    }
  },

  async created() {
    this.mainId = this.$route.query.requestPostId
    this.id = this.$route.query.id
    this.isApprover = this.$route.query.approver === 'true'
    await this.getData()

    // 承認待ち一覧の「確認」から開いたのに承認待ちでなければ、申請者が取り消している
    if (this.isApprover && this.val.id && !this.isPending) {
      await dialogs.showWarningDialog('申請が取り消されています', '先ほど申請が取り消されました。申請一覧に戻ります。')
      this.toList()
      return
    }

    // 承認者は変わったところをすぐ見たいので、変更のある物件だけ開く
    if (this.isApprover) {
      this.openChangedOnly()
    }
  },

  mounted() {
    this.watchTopHeight()
  },

  beforeDestroy() {
    // 勤務形態の候補を開いたまま画面を離れたときのスクロールの見張りを外す
    this.attendanceClosed()
    this.unwatchTopHeight()
  },

  computed: {
    /**
     * 承認待ちか
     */
    isPending() {
      return this.val.status === AKBS_STATUS.PENDING
    },

    /**
     * AKBSへ登録済みか
     */
    isRegistered() {
      return this.val.status === AKBS_STATUS.REGISTERED
    },

    /**
     * 閲覧のみか（承認待ち・AKBS登録済み・復活できない削除済み）
     */
    isReadOnly() {
      return this.isPending || this.isRegistered || this.deletedFinal
    },

    /**
     * 承認の操作（差戻・AKBSへ登録）ができるか（承認者として開いていて、契約詳細権限がある）
     * 契約詳細の承認は AKBS 権限（雇用指示の承認）とは別の権限 contractDetailAuth（ユーザー決定 2026-10-09）
     */
    canApprove() {
      const auth = this.$store.getters.auth
      return this.isApprover && !!(auth && auth.contractDetailAuth) && !this.isMyRequest
    },

    /**
     * 自分が作成した申請か（作成者のユーザーID creatorId とログインユーザーの id を文字で比べる）
     * 作成者本人は承認（差戻・AKBSへ登録）できない。申請一覧の「確認」は出ないが、URL を直接開いたときのためにここでも止める（ユーザー指示）
     */
    isMyRequest() {
      const me = this.$store.getters.user && this.$store.getters.user.id
      return !!this.val.creatorId && !!me && String(this.val.creatorId) === String(me)
    },

    /**
     * 今回の差戻理由を書けるか
     */
    canWriteReturnReason() {
      return this.canApprove && this.isPending
    },

    /**
     * 解約か
     */
    isEnd() {
      return Number(this.val.type) === 2
    },

    /**
     * 種別の表示（メインタスクの種別）
     */
    typeText() {
      if (this.isCreate) return '新規'
      return this.isEnd ? '解約' : '変更'
    },

    /**
     * 新規登録か（受注処理のメインタスクから開いた。物件は1つで、受注の入力を初期値にした契約コードを足す）
     */
    isCreate() {
      return Number(this.val.type) === TYPE_CREATE
    },

    /**
     * 受注の入力から作った、契約コードごとの初期値（人数分。新規登録のときだけ）
     * rp は開くたびにバックが受注（メインタスク）を取り直したものなので、開いた時点の受注の値
     */
    orderEntries() {
      return this.isCreate ? contractsFromOrder(this.rp) : []
    },

    /**
     * 受注の対象の項目（作業内容ごと。概要の「受注の内容」に出す）
     */
    orderItems() {
      return this.isCreate ? itemsFromOrder(this.rp) : []
    },

    /**
     * 受注タスクの月の時間の合計（作業内容ごとの合計。比べの表の「合計」の行に見せるだけ。判定には使わない）
     */
    orderTotalHours() {
      return Math.round(this.orderItems.reduce((sum, item) => sum + (item.monthlyHours || 0), 0) * 100) / 100
    },

    /**
     * 契約詳細の月の時間の合計（作業内容ごとの合計。見せるだけ）
     */
    contractTotalHours() {
      return Math.round(this.orderItems.reduce((sum, item) => sum + this.contractMonthlyHours(item), 0) * 100) / 100
    },

    /**
     * 受注の人数（見積で決めた人数の合計。契約コードはこの数以上ないといけない）
     */
    orderPeople() {
      return this.isCreate ? peopleFromOrder(this.rp) : 0
    },

    /**
     * この画面で足した契約コードぜんぶ（受注から作ったものも、手で足したものも）
     */
    newContracts() {
      const list = []
      for (const site of this.akbs.sites) {
        for (const con of site.contracts) {
          if (con.isNewContract) list.push(con)
        }
      }
      return list
    },

    /**
     * 受注の作業内容ごとの、この画面の契約コードと月の時間・週の回数（表のセル・判定・ダイアログが何度も読むので、1回だけ計算して控える）
     * ・contracts：その作業内容に属する契約コード（受注から作った分と、手で足して作業内容を選んだ分。作業内容が1つだけなら手で足した分も入れる）
     * ・hours：月の時間の合計（曜日ごとの実働 × 勤務形態の係数。毎週 4.3・隔週 2.15・第n週 1・第5週 0.3）
     * ・weeks：勤務のある曜日の数（契約コードをまたいで同じ曜日は1つ。曜日ごとの重みは係数 ÷ 4.3 で最大 1。毎週4日＋隔週1日 ＝ 4.5）
     * 行は消していないもののうち開始日が一番遅い行（newestRow）
     * 行の欄は reload++ で描き直す作りなので、reload も読んで計算し直す
     */
    itemStats() {
      const tick = this.reload
      const stats = {}
      const single = this.orderItems.length === 1
      for (const item of this.orderItems) {
        const contracts = this.newContracts.filter(con => con.orderItem === item.key || (single && !con.orderItem))
        let hours = 0
        const byDay = {}
        for (const con of contracts) {
          const row = this.newestRow(con)
          if (!row) continue
          for (const w of WEEK_LIST) {
            const attendances = row['attendance' + w.id] || []
            hours += utilsAKBS.getHoursByMonth(Number(row['workingHours' + w.id]) || 0, attendances)
            if (!attendances.length) continue
            let weight = 0
            for (const at of attendances) {
              weight += utilsAKBS.attendanceCoef(at.id) / 4.3
            }
            byDay[w.id] = Math.max(byDay[w.id] || 0, Math.min(weight, 1))
          }
        }
        const weeks = Object.values(byDay).reduce((sum, v) => sum + v, 0)
        stats[item.key] = { contracts, hours: Math.round(hours * 100) / 100, weeks: Math.round(weeks * 100) / 100, tick }
      }
      return stats
    },

    /**
     * 手で足した契約コードで、受注の作業内容を選ぶ必要があるのに選んでいないもの（新規登録で、作業内容が2つ以上あるとき。申請時に止める）
     */
    unassignedContracts() {
      if (!this.isCreate || this.orderItems.length < 2) return []
      return this.newContracts.filter(con => !con.orderItem)
    },

    /**
     * 受注タスクで物件コードの入った物件が選んであるか（あれば得意先も物件もこの画面では変えられない）
     */
    orderSiteLocked() {
      return this.isCreate && !!this.rp.siteCode
    },

    /**
     * 受注タスクの物件名（受注画面で選んだ・貼った物件名。無ければ見積の物件名）
     */
    orderSiteName() {
      return String(this.rp.siteName || this.rp.buildingName || '').trim()
    },

    /**
     * 新規登録の得意先の候補（全物件から得意先ごとに1つ。受注タスクの得意先が無ければ足す）
     */
    clientOptions() {
      const map = {}
      for (const s of this.allSites) {
        const code = String(s.clientCode || '')
        if (!code || map[code]) continue
        map[code] = { clientCode: code, clientName: `${s.client1 || ''} ${s.client2 || ''}`.trim() }
      }
      if (this.rp.clientCode && !map[String(this.rp.clientCode)]) {
        map[String(this.rp.clientCode)] = { clientCode: String(this.rp.clientCode), clientName: this.rp.clientName || '' }
      }
      return Object.values(map)
    },

    /**
     * 変更後の物件コードを入れてもらう物件（得意先変更なら全部。それ以外は無い）
     */
    newSiteTargets() {
      return this.rp.changeClient ? this.akbs.sites : []
    },

    /**
     * モーダルで追加できる物件（得意先の物件から、既に入っている物件を除く）
     */
    addableSites() {
      return this.siteList.filter(b => !this.akbs.sites.some(s => s.siteCode == b.siteCode))
    },

    /**
     * 変更の内訳（修正・変更（履歴残す）・契約終了の契約の数）
     */
    tally() {
      const t = { over: 0, new: 0, end: 0, add: 0 }
      for (const site of this.akbs.sites) {
        for (const con of site.contracts) {
          if (t[con.mode] !== undefined) t[con.mode]++
        }
      }
      return t
    },

    /**
     * 変更の内訳の札（1件以上あるものだけ）
     */
    tallyList() {
      return [
        { mode: 'over', text: '修正（上書き）' },
        { mode: 'new', text: '変更（履歴残す）' },
        { mode: 'end', text: '契約終了' },
        { mode: 'add', text: '契約コード追加' }
      ]
        .map(t => ({ ...t, count: this.tally[t.mode] }))
        .filter(t => t.count > 0)
    },

    /**
     * 部署の候補（メインタスクの宛先）
     */
    departmentList() {
      return this.$store.getters.departmentList || []
    },

    /**
     * 雇い入れタスクの対象になる清掃員（変更のある契約で働いている人。清掃員1人に1件なので、人ごとにまとめる）
     * ・must：契約終了の契約にいる（シフトに影響が出るので、雇い入れタスクの作成が必須）
     * ・kind：種別の初期値。契約終了だけ → 作業終了、物件コードが変わる → 物件追加、それ以外 → 契約内容変更
     *   （サブタスクの初期値を決めるためのものなので、厳密でなくてよい。画面で変えられる）
     */
    empCandidates() {
      const map = {}
      const list = []
      for (const site of this.akbs.sites) {
        const cdChange = this.newSiteTargets.includes(site)
        for (const con of site.contracts) {
          if (con.mode === 'none') continue
          for (const st of con.staffs || []) {
            const code = String(st.staffDailyCleaning)
            if (!map[code]) {
              map[code] = { staffCode: code, staffName: st.nameStaff || '', cons: [] }
              list.push(map[code])
            }
            // 同じ契約に雇用指示が複数ある人は1行にまとめる
            if (map[code].cons.some(c => c.siteCode == site.siteCode && c.cdContract == con.cdContract)) continue
            map[code].cons.push({
              siteCode: site.siteCode,
              siteName: this.siteName(site),
              cdContract: con.cdContract,
              mode: con.mode,
              modeText: this.modeText(con.mode),
              cdChange: cdChange && con.mode !== 'end'
            })
          }
        }
      }
      for (const c of list) {
        c.must = c.cons.some(x => x.mode === 'end')
        c.kind = c.cons.every(x => x.mode === 'end') ? 'end' : (c.cons.some(x => x.cdChange) ? 'add' : 'change')
      }
      return list
    },

    /**
     * 対象の清掃員が変わったかを見るための文字（清掃員コード・必須か・種別の初期値）
     */
    empSignature() {
      return this.empCandidates.map(c => `${c.staffCode}:${c.must ? 1 : 0}:${c.kind}`).join(',')
    },

    /**
     * 雇い入れタスクの表の行
     * 並びは下の契約詳細情報と同じ（物件 → 契約コード → 清掃員の順。empCandidates がその順で拾っている）
     * 複数の契約にいる清掃員は、最初に出てくる契約の位置にまとめる。状態では並べ替えない（下の表と見比べやすくするため）
     * ・exist：同じ清掃員の、AKBS登録前の雇い入れ（清掃員の申請）が既にある（新しく作らない。この画面から作った分もここに入る）
     * ・todo ：無い（作れる）
     * 判定は清掃員の申請（req_akbs）だけで行う。AKBS登録済みになった雇い入れは終わった仕事なので、また変更があれば新しく作れる
     */
    empRows() {
      return this.empCandidates.map(c => {
        const task = this.empTasks.find(t => t.staffCode === c.staffCode) || null
        return { ...c, state: task ? 'exist' : 'todo', task, edit: this.empEdits[c.staffCode] || null }
      })
    },

    /**
     * チェックの付いた未作成の清掃員の数（雇い入れタスクを作る人数）
     */
    empCheckedCount() {
      return this.empRows.filter(r => r.state === 'todo' && r.edit && r.edit.checked).length
    },

    /**
     * メモに何か書いてあるか（空白だけは無しと見る。書いてあれば目立たせる）
     */
    hasMemo() {
      return !!String(this.val.memo || '').trim()
    },

    /**
     * 表の下のボタンの文字
     */
    empButtonText() {
      return this.empCheckedCount ? `チェックした ${this.empCheckedCount}人の雇い入れタスクを作る` : 'チェックした人の雇い入れタスクを作る'
    },

    /**
     * ログインユーザー名（フリガナ無し）
     */
    user() {
      return utils.deleteKana(this.$store.getters.user.user_name)
    },
  },

  watch: {
    // 対象の清掃員が変わったら、未作成の行の入力を揃えて、雇い入れタスクを取り直す
    empSignature() {
      this.syncEmpEdits()
      this.loadEmpTasks()
    }
  },

  methods: {
    // ------------------------------------------------------------
    // 取得と組み立て
    // ------------------------------------------------------------

    /**
     * データ取得
     * 未保存なら AKBS の今の値から画面の内容を作る。保存済みで登録前なら、触っていない行・欄を今の値に差し替える
     */
    async getData() {
      this.loading = true
      let notices = []
      // 受注の作業曜日・時間の行をまとめた契約コード（未保存で開いたときに知らせる）
      let mergedNotes = []
      let blankNotes = []
      try {
        const res = await backend.searchData('akbsContractForm/getData', { mainId: this.mainId, id: this.id })
        const data = res.data.data
        this.rp = data.rp || {}
        // 削除した申請で、メインタスクが完了している（復活できない）
        this.deletedFinal = !!data.deletedFinal

        // 種別はメインタスクから決める（受注処理なら 3 新規。変更処理は classification が '2' なら 2 解約、それ以外は 1 変更）
        // 受注処理の classification（新規・追加・変更・スポット）は別物なので、先に type で見分ける
        const isOrder = this.rp.type === 'order'
        const type = isOrder ? TYPE_CREATE : (this.rp.classification && this.rp.classification[0] == '2' ? 2 : 1)

        let val = data.val
        let akbs = null
        const saved = !!(val && val.id)

        if (saved) {
          akbs = val.akbs || { sites: [] }
          delete val.akbs
          this.toDisplayAkbs(akbs)

          // 保存済みで登録前なら、AKBS の今の値で「触っていないところ」を最新にする
          if (val.status !== AKBS_STATUS.REGISTERED && data.live) {
            notices = this.mergeLatest(akbs, data.live)
          }
          // 新規登録なら、受注の今の値を行の「元の値」に入れ直す（受注と違う欄が黄色になる。受注が親で、一時保存のあとに受注が直されることがある）
          if (val.status !== AKBS_STATUS.REGISTERED && isOrder) {
            this.syncOrder(akbs)
          }
        } else {
          val = {
            // ?id= の URL から開いた削除済みの申請は URL にメインタスクの id が無いので、バックが返したものを使う
            orderId: this.mainId || data.orderId,
            type,
            typeName: TYPE_NAMES[type],
          }
          akbs = isOrder ? this.buildAkbsFromOrder(data.live || []) : this.buildAkbs(data.live || [])
          if (isOrder) {
            mergedNotes = akbs.sites[0].contracts
              .filter(c => (c.orderMerged || []).length > 1)
              .map(c => `契約コード ${c.cdContract}：${c.orderName}`)
            blankNotes = akbs.sites[0].contracts
              .filter(c => c.orderBlank)
              .map(c => `契約コード ${c.cdContract}：${c.orderName}`)
          }
        }

        // 後から足すとリアクティブにならないため、読み込み時にキーを揃える
        this.val = { ...createValBase(), ...val, type: val.type || type, typeName: val.typeName || TYPE_NAMES[type] }
        // メモは未入力だと null で返るので、入力欄用に空文字へ揃える
        this.val.memo = this.val.memo || ''
        this.akbs = akbs

        // 開閉状態（初期値は全部開く。取り直しでは今の状態を残す）
        const opened = {}
        const showEnded = {}
        for (const site of this.akbs.sites) {
          opened[site.siteCode] = this.opened[site.siteCode] === undefined ? true : this.opened[site.siteCode]
          showEnded[site.siteCode] = !!this.showEnded[site.siteCode]
        }
        this.opened = opened
        this.showEnded = showEnded
        this.errorKeys = []

        // 未保存のときは、メインタスクの内容で「変更の方法」の初期値を入れる
        if (!saved) {
          this.setDefaultModes()
        }

        // 得意先変更なら、変更後の物件コードの候補を取る（閲覧のみなら不要）
        if (this.rp.changeClient && !this.isReadOnly) {
          await this.loadNewSiteLists()
        }
        // 新規登録なら、物件コードの候補（同じ得意先の物件）を取る（閲覧のみなら不要）
        if (this.isCreate && !this.isReadOnly) {
          await this.loadCreateSiteList()
        }

        // 清掃員の雇い入れタスク（対象の清掃員が前と同じだと watch が動かないので、ここでも取る）
        this.syncEmpEdits()
        await this.loadEmpTasks()
      } catch (e) {
        console.log(e)
        await this.errorDialog('データ取得エラー', 'データの取得に失敗しました。画面を再読み込みしてください。')
      } finally {
        this.loading = false
      }

      if (notices.length) {
        await dialogs.showInfoDialog('AKBSの内容を最新にしました', `申請のあとに本番のAKBSで変わっていたところがあります。\n触っていない行・欄は最新の値にし、触った欄はそのままにしています。\n${notices.map(n => `・${n}`).join('\n')}`)
      }
      // 受注の行は1行に開始〜終了が1つしか持てないので、曜日が重ならない行は1つの契約コードにまとめてある（akbsContractFromOrder.js の groupRemarks）
      if (mergedNotes.length) {
        await dialogs.showInfoDialog('受注の作業曜日・時間をまとめました', `曜日が重ならない作業曜日・時間の行は、1人の勤務として1つの契約コードにまとめています。\n別々の人の勤務なら、「契約コードを新規追加」で分けてください（作業内容が2つ以上あれば、足した契約コードの帯でどの作業内容の分かを選びます）。\n${mergedNotes.map(n => `・${n}`).join('\n')}`)
      }
      // 受注の人数より作業曜日・時間の行が少ない分は、勤務が空の契約コードにしてある（勝手に写さない。ユーザー決定 2026-09-23）
      if (blankNotes.length) {
        await dialogs.showInfoDialog('勤務が空の契約コードがあります', `受注の人数より作業曜日・時間の行が少ない作業内容があります。\n足りない分は勤務が空の契約コードにしてあるので、その人の勤務を入力してください。\n${blankNotes.map(n => `・${n}`).join('\n')}`)
      }
    },

    /**
     * AKBS の今の値から画面の内容を作る（未保存のとき）
     * @param {Array} live 対象物件の契約詳細の今の値
     */
    buildAkbs(live) {
      const sites = live.map(site => this.buildSite(site, (site.contracts || []).map(c => this.buildContract(c))))
      return { sites }
    },

    /**
     * 物件1件分の画面の内容を作る（キーを最初から持たせる）
     * @param {Object} site 物件（siteCode, site1, site2, agreementFlg）
     * @param {Array} contracts 契約
     */
    buildSite(site, contracts) {
      return {
        siteCode: site.siteCode,
        site1: site.site1 || '',
        site2: site.site2 || '',
        agreementFlg: site.agreementFlg || '',
        // 得意先変更・物件コード変更のときの変更後の物件コード（名前は表示用）
        newSiteCode: '',
        newSiteName: '',
        // 新規登録：物件を選ぶ得意先（初期値は受注タスクの得意先。この画面で選び直せる）
        clientCode: '',
        clientName: '',
        // 新規登録で、受注から作った契約コードのうち「取消」したものの控え（orderKey）。開き直したときに syncOrder が足し直さないため（2026-09-23）
        removedOrderKeys: [],
        contracts
      }
    },

    /**
     * 契約1件分の画面の内容を作る（行ごとに元の値 original を控える）
     * @param {Object} live 契約詳細の今の値（rows と staffs）
     */
    buildContract(live) {
      return {
        cdSite: live.cdSite,
        cdContract: live.cdContract,
        // 画面の中で契約を見分けるキー（AKBS 側の契約は契約コードそのもの。足した契約コードは振り直すので別の連番）
        key: String(live.cdContract),
        cdSalesOffice: live.cdSalesOffice || '',
        // 変更の方法（none・over・new・end）
        mode: 'none',
        // 申請を止める理由（変更日が最新の行より前、契約終了なのに未来の行がある）
        blocked: '',
        rows: sortRows((live.rows || []).map(r => this.buildRow(r))),
        staffs: (live.staffs || []).map(s => this.buildStaff(s))
      }
    },

    /**
     * 行1件分の画面の内容を作る
     * @param {Object} live 契約詳細の今の値（勤務形態はコードの配列）
     */
    buildRow(live) {
      const row = utils.clone(live)
      this.toDisplayRow(row)
      row.key = `${row.cdSite}-${row.cdContract}-${row.startDateContract}`
      // 行を特定するための元の値（物件コード・開始日）
      row.origSite = row.cdSite
      row.origStart = row.startDateContract
      row.isNew = false
      row.deleted = false
      row.notInAkbs = false
      row.original = this.snapshot(row)
      row.changed = {}
      return row
    },

    /**
     * 担当清掃員（雇用指示）1件分（勤務形態を配列に揃えるだけ。表示は attendanceNames で）
     */
    buildStaff(live) {
      const st = utils.clone(live)
      for (const w of WEEK_LIST) {
        st['attendance' + w.id] = Array.isArray(st['attendance' + w.id]) ? st['attendance' + w.id] : []
      }
      return st
    },

    /**
     * 比べる用に、行の値だけを写す（画面用のキーは含めない）
     */
    snapshot(row) {
      const s = {}
      for (const key of [...EDIT_KEYS, ...DATE_KEYS, 'cdSite']) {
        s[key] = utils.clone(row[key])
      }
      return s
    },

    /**
     * 勤務形態をコードの配列から選択肢の配列に直す（行1件。original も）
     */
    toDisplayRow(row) {
      for (const w of WEEK_LIST) {
        row['attendance' + w.id] = utilsAKBS.toAttendance(row['attendance' + w.id])
      }
      if (row.original) {
        for (const w of WEEK_LIST) {
          row.original['attendance' + w.id] = utilsAKBS.toAttendance(row.original['attendance' + w.id])
        }
      }
    },

    /**
     * 保存してあった内容を画面の形にする（勤務形態の変換とキーの補完）
     */
    toDisplayAkbs(akbs) {
      akbs.sites = akbs.sites || []
      for (const site of akbs.sites) {
        site.contracts = site.contracts || []
        if (site.newSiteCode === undefined) site.newSiteCode = ''
        if (site.newSiteName === undefined) site.newSiteName = ''
        if (!Array.isArray(site.removedOrderKeys)) site.removedOrderKeys = []
        if (site.clientCode === undefined) site.clientCode = ''
        if (site.clientName === undefined) site.clientName = ''
        for (const con of site.contracts) {
          if (!con.mode) con.mode = 'none'
          if (con.blocked === undefined) con.blocked = ''
          if (!con.key) con.key = con.isNewContract ? 'newcon' + (++conSeq) : String(con.cdContract)
          // 受注から作った契約コードの控え（新規登録。受注のどの1件から作ったか・どの作業内容か・表示名・まとめた行）
          if (con.orderKey === undefined) con.orderKey = ''
          if (con.orderItem === undefined) con.orderItem = ''
          if (con.orderName === undefined) con.orderName = ''
          if (con.orderMerged === undefined) con.orderMerged = []
          if (con.orderBlank === undefined) con.orderBlank = false
          if (con.orderNth === undefined) con.orderNth = 0
          con.rows = con.rows || []
          con.staffs = (con.staffs || []).map(s => this.buildStaff(s))
          for (const row of con.rows) {
            this.toDisplayRow(row)
            if (!row.key) row.key = row.isNew ? 'new' + (++rowSeq) : `${row.origSite}-${con.cdContract}-${row.origStart}`
            if (row.isNew === undefined) row.isNew = false
            if (row.deleted === undefined) row.deleted = false
            if (row.notInAkbs === undefined) row.notInAkbs = false
            if (!row.changed) row.changed = {}
            if (!row.original) row.original = this.snapshot(row)
            // 受注から作った契約コードの行（受注と違う欄を黄色にする）
            if (row.fromOrder === undefined) row.fromOrder = !!con.orderKey
            if (!row.orderLock) row.orderLock = { specificTime: false, holiday: false, garbage: false }
            if (row.orderBlank === undefined) row.orderBlank = !!con.orderBlank
            if (row.orderHolidayDefault === undefined) row.orderHolidayDefault = false
            if (row.orderHolidayGarbage === undefined) row.orderHolidayGarbage = false
          }
        }
      }
    },

    /**
     * AKBS の今の値で「触っていないところ」を最新にする（保存済みで登録前に開いたとき）
     * ・触っていない欄（元の値と同じ）：今の値にする
     * ・触った欄：申請の値を残して元の値だけ最新にし、知らせる
     * ・今の値に無い行（AKBS側で消えた・開始日が変わった）：残して印を付ける。今の値にだけある行：足す
     * ・担当清掃員は常に今の値
     * @returns 知らせる文（無ければ空）
     */
    mergeLatest(akbs, live) {
      const notices = []
      for (const liveSite of live) {
        let site = akbs.sites.find(s => s.siteCode == liveSite.siteCode)
        if (!site) {
          site = this.buildSite(liveSite, [])
          akbs.sites.push(site)
        }
        for (const liveCon of liveSite.contracts || []) {
          // この画面で足した契約コード（暫定）と同じ契約コードが AKBS 側にできていても、AKBS 側の契約として足す（足した方はあとで振り直す）
          const con = site.contracts.find(c => !c.isNewContract && c.cdContract == liveCon.cdContract)
          if (!con) {
            site.contracts.push(this.buildContract(liveCon))
            continue
          }
          con.staffs = (liveCon.staffs || []).map(s => this.buildStaff(s))
          con.cdSalesOffice = liveCon.cdSalesOffice || con.cdSalesOffice

          for (const liveRow of liveCon.rows || []) {
            const row = con.rows.find(r => !r.isNew && r.origSite == liveRow.cdSite && r.origStart == liveRow.startDateContract)
            if (!row) {
              con.rows.push(this.buildRow(liveRow))
              continue
            }
            const latest = this.buildRow(liveRow)
            for (const key of [...EDIT_KEYS, ...DATE_KEYS]) {
              const touched = !this.same(row[key], row.original[key])
              if (!touched) {
                row[key] = utils.clone(latest[key])
              }
              if (!this.same(row.original[key], latest[key])) {
                row.original[key] = utils.clone(latest[key])
                if (touched) {
                  notices.push(`${this.siteName(site)} 契約コード ${con.cdContract}（${this.formatDate(latest.origStart)}〜）の ${keyLabel(key)}（申請の値はそのまま）`)
                }
              }
            }
            row.contentsWorkName = latest.contentsWorkName
            row.notInAkbs = false
          }
          // 今の値に無い行
          for (const row of con.rows) {
            if (!row.isNew && !(liveCon.rows || []).some(r => r.cdSite == row.origSite && r.startDateContract == row.origStart)) {
              row.notInAkbs = true
              notices.push(`${this.siteName(site)} 契約コード ${con.cdContract}（${this.formatDate(row.origStart)}〜）の行はAKBS側にありません`)
            }
          }
          sortRows(con.rows)
        }
        // AKBS 側の契約が増えていれば、足した契約コード（暫定）をその続きから振り直す（同じ契約コードで止めない。ユーザー決定 2026-09-23）
        if (site.contracts.some(c => c.isNewContract)) {
          this.renumberContracts(site)
        }
      }
      return notices
    },

    /**
     * 変更の方法の初期値（未保存で開いたとき）
     * 得意先変更あり・物件コード変更にチェックした物件 → 変更（履歴残す）、解約 → 契約終了、それ以外 → 変更なし
     */
    setDefaultModes() {
      for (const site of this.akbs.sites) {
        for (const con of site.contracts) {
          this.applyMode(site, con, this.defaultMode(site))
        }
      }
    },

    defaultMode() {
      if (this.isEnd) return 'end'
      if (this.rp.changeClient) return 'new'
      return 'none'
    },

    // ------------------------------------------------------------
    // 変更の方法
    // ------------------------------------------------------------

    /**
     * 変更の方法の表示名（表の左の列。選んだあとに状態として出す）
     */
    modeText(mode) {
      const o = MODE_OPTIONS.find(m => m.value === mode)
      return o ? o.text : ''
    },

    /**
     * 変更の方法を変えた（表の左の列のボタン。取消は 'none' に戻す）
     * 修正（上書き）は変更前が残らないので確認する（雇用指示の表と同じ文）
     */
    async changeMode(site, con, mode) {
      // この画面で足した契約コードの「取消」は、契約ごと取り除く
      if (con.isNewContract) {
        if (mode !== 'none') return
        const res = await dialogs.showConfirmDialog('契約コードの追加を取り消します', `契約コード ${con.cdContract} の追加をやめて、入力した内容を消します。よろしいですか？`, '取り消す')
        if (res !== 'YES') return
        const i = site.contracts.indexOf(con)
        if (i > -1) site.contracts.splice(i, 1)
        // 受注から作った契約コードなら、取り消したことを控える（開き直したときに syncOrder が受注から足し直さないように。一時保存で akbs に一緒に入る）
        if (con.orderKey && !site.removedOrderKeys.includes(con.orderKey)) {
          site.removedOrderKeys.push(con.orderKey)
        }
        // 残った足した契約コードを上から詰めて振り直す（欠番を作らない。ユーザー決定 2026-09-23）
        this.renumberContracts(site)
        this.reload++
        return
      }
      if (mode === 'over') {
        const res = await dialogs.showConfirmDialog(
          '修正（上書き）でよろしいですか？',
          'この契約の内容を直接書き換えます。変更前の内容は残りません。\n\nよろしいですか？'
        )
        if (res !== 'YES') {
          this.reload++
          return
        }
      }
      this.applyMode(site, con, mode)
      this.reload++
    },

    /**
     * 変更の方法に沿って行を作り直す
     * 一度元の状態（AKBSの今の値）に戻してから、変更（履歴残す）なら行を足し、契約終了なら終了日を入れる
     */
    applyMode(site, con, mode) {
      // この画面で足した契約コードは行が全部「足した行」なので、作り直さない
      if (con.isNewContract) return
      // 元に戻す（足した行は消す、消した行は戻す、値は元の値）
      con.rows = con.rows.filter(r => !r.isNew)
      for (const row of con.rows) {
        row.deleted = false
        for (const key of [...EDIT_KEYS, ...DATE_KEYS]) {
          row[key] = utils.clone(row.original[key])
        }
      }
      sortRows(con.rows)
      con.mode = mode
      con.blocked = ''

      const changeDate = this.rp.changeDate || ''
      const last = this.latestRow(con)

      if (mode === 'new' && last) {
        if (changeDate && changeDate <= last.startDateContract) {
          // 変更日が最新の行の開始日以前 → 行が重なるので止める
          con.blocked = `この契約は ${this.formatDate(last.startDateContract)} から変わる予定の行があります。変更日を ${this.formatDate(last.startDateContract)} より後にするか、修正（上書き）でその行を削除してください。`
        } else {
          last.endDateContract = changeDate ? this.dayBefore(changeDate) : ''
          const row = this.copyRow(last)
          row.startDateContract = changeDate
          row.cdSite = site.newSiteCode || last.cdSite
          con.rows.push(row)
        }
      }

      if (mode === 'end' && last) {
        const futures = con.rows.filter(r => this.isFuture(r))
        if (futures.length) {
          con.blocked = `まだ始まっていない行（${futures.map(r => this.formatDate(r.startDateContract) + '〜').join('、')}）があります。修正（上書き）で先に削除してから契約終了にしてください。`
        } else {
          last.endDateContract = this.isEnd ? changeDate : ''
        }
      }
    },

    /**
     * 行を写して「足した行」を作る（変更（履歴残す）の新しい行、修正（上書き）の行を追加）
     * 備考・クリーンプラス金額・ランクも写す（新しい行にも備考が付いてまわるように。直したければ足した行で直す）
     * 変更前と違う欄が分かるよう、元の値には写した元の行の値を控える
     * 契約終了日だけは写さず空にする（元の行の終了日はその行のもので、足した行には関係ない）
     * 元の値も空にしておく。元の行の終了日を控えると、何も入れていないのに「変更あり」の黄色になる
     * （変更（履歴残す）では、元の行に変更日の前日を入れたあとで写すので、必ず食い違っていた）
     */
    copyRow(src) {
      const row = utils.clone(src)
      row.key = 'new' + (++rowSeq)
      row.isNew = true
      row.deleted = false
      row.notInAkbs = false
      row.origSite = ''
      row.origStart = ''
      row.changed = {}
      row.endDateContract = ''
      row.original = this.snapshot(src)
      row.original.endDateContract = ''
      return row
    },

    /**
     * 次の契約コード（自動採番）
     * この物件の契約コード（終了した契約・この画面で足した契約も含む）の中で一番大きい数字＋1。
     * 契約コードは文字列で、2桁の 0 詰め（01・02…）。今ある契約コードがそれより長ければ、その桁数に合わせる。契約が無ければ 01
     */
    nextContractCode(site) {
      const codes = site.contracts.map(c => String(c.cdContract || '').trim())
      const nums = codes.filter(c => /^\d+$/.test(c))
      const max = nums.length ? Math.max(...nums.map(Number)) : 0
      const width = Math.max(2, ...nums.map(c => c.length))
      let n = max + 1
      let code = String(n).padStart(width, '0')
      while (codes.includes(code)) {
        n++
        code = String(n).padStart(width, '0')
      }
      return code
    },

    /**
     * この画面で足した契約コードを、上から順に振り直す（暫定の採番。AKBS に登録するまで確定しない。ユーザー決定 2026-09-23）
     * AKBS 側の契約コード（終了した契約も含む）の一番大きい数字の次から、足した契約コードの並び順に 1 ずつ。桁数は nextContractCode と同じ
     * 呼ぶところ：取消で契約コードを取り除いたとき、開いたとき（AKBS 側の契約が増えていることがある）、新規登録で物件コードを決めたとき
     * 並びは AKBS 側の契約（契約コード順）→ 足した契約コード。承認のときに本番と同じ契約コードがあれば、バックが最大値の次へ振り直して書く
     * （akbsContractForm.js の renumberNewContracts）。契約の key は変わらないので、NG の赤い枠・開閉の状態は残る
     */
    renumberContracts(site) {
      const existing = site.contracts.filter(c => !c.isNewContract)
      const added = site.contracts.filter(c => c.isNewContract)
      existing.sort((a, b) => String(a.cdContract).localeCompare(String(b.cdContract)))
      const nums = existing.map(c => String(c.cdContract || '').trim()).filter(c => /^\d+$/.test(c))
      let n = nums.length ? Math.max(...nums.map(Number)) : 0
      const width = Math.max(2, ...nums.map(c => c.length))
      for (const con of added) {
        n++
        con.cdContract = String(n).padStart(width, '0')
        for (const row of con.rows) {
          row.cdContract = con.cdContract
        }
      }
      site.contracts = [...existing, ...added]
    },

    /**
     * 契約コードを新規追加（この物件に新しい契約コードの契約詳細を足す。契約コードは自動採番）
     * 行は空の1件（開始日は変更日）。営業所コードは同じ物件の契約から写す
     */
    async addContract(site) {
      const code = this.nextContractCode(site)
      if (site.contracts.some(c => String(c.cdContract) === code)) {
        await this.errorDialog('同じ契約コードがあります', `契約コード ${code} はこの物件に既にあります（終了した契約も含みます）。`)
        return
      }
      site.contracts.push(this.newContract(site, code))
      this.opened[site.siteCode] = true
      this.reload++
    },

    /**
     * 足す契約コード1件分（行は空の1件）。営業所コードは同じ物件の契約から写す
     */
    newContract(site, code) {
      const first = site.contracts[0]
      const con = {
        cdSite: site.siteCode,
        // 暫定の契約コード（AKBS に登録するまで確定しない。取消で減れば上から振り直し、承認のときに本番とぶつかればバックが振り直す）
        cdContract: code,
        key: 'newcon' + (++conSeq),
        cdSalesOffice: first ? first.cdSalesOffice || '' : '',
        mode: 'add',
        blocked: '',
        rows: [],
        staffs: [],
        // この画面で足した契約コード（取消で契約ごと取り除く。行は全部「足した行」で承認時に INSERT）
        isNewContract: true,
        // 受注から作った契約コードの控え（新規登録で受注の1件から作ったときだけ入る。手で足した契約コードは空）
        orderKey: '',
        orderItem: '',
        orderName: '',
        // まとめた受注の作業曜日・時間の行の番号（2つ以上ならまとめている）
        orderMerged: [],
        // 受注に勤務の行が無い分か（人数の方が多いときに足した空の契約コード）と、何人目か
        orderBlank: false,
        orderNth: 0
      }
      con.rows.push(this.blankRow(site, code))
      return con
    },

    // ------------------------------------------------------------
    // 新規登録（受注処理のメインタスクから開いたとき）
    // ------------------------------------------------------------

    /**
     * 受注の入力から画面の内容を作る（未保存のとき）
     * 物件は受注で選んである物件1つ（新しい物件でまだ物件コードが無ければ空。物件コードはこの画面の概要で入れる）
     * その物件に今ある契約はそのまま出し（契約コードの採番と重複の確認に要る）、受注の作業曜日・時間の1件（人数分）ごとに契約コードを足す
     * 初期値の作り方は src/libs/akbsContractFromOrder.js
     * @param {Array} live 受注で選んである物件の契約詳細の今の値（物件コードが無ければ空）
     */
    buildAkbsFromOrder(live) {
      const liveSite = live[0] || { siteCode: String(this.rp.siteCode || ''), site1: this.rp.siteName || this.rp.buildingName || '' }
      const site = this.buildSite(liveSite, (liveSite.contracts || []).map(c => this.buildContract(c)))
      // getData の中で呼ばれる時点では this.val（種別）がまだ入っていないので、computed の orderEntries（isCreate を見る）は使わず直接作る
      for (const entry of contractsFromOrder(this.rp)) {
        site.contracts.push(this.contractFromOrder(site, entry))
      }
      return { sites: [site] }
    },

    /**
     * 受注の項目の表示名（「受注1 日常清掃 【…】」。選択欄・ダイアログ用。概要はバッジと orderItemName を別々に出す）
     */
    orderItemText(item) {
      return `受注${item.no} ${orderItemName(item)}`
    },

    orderItemName(item) {
      return orderItemName(item)
    },

    /**
     * 受注に勤務の行が無い分の契約コードで、まだ勤務が入っていないか（概要の赤字の件数と、保存・申請・登録時の確認ダイアログに使う。勤務を入れたら外れる）
     */
    isBlankUnfilled(con) {
      return !!con.orderBlank && (con.rows || []).every(row => row.deleted || WEEK_LIST.every(w => this.isDayOff(row, w)))
    },

    /**
     * 受注の項目の、勤務がまだ空の契約コードの数（人数が作業曜日・時間のまとまりより多い分。概要の「受注の内容」に赤く出す。勤務を入れたら減る）
     */
    blankCount(item) {
      let n = 0
      for (const site of this.akbs.sites) {
        n += site.contracts.filter(c => c.isNewContract && c.orderItem === item.key && this.isBlankUnfilled(c)).length
      }
      return n
    },

    /**
     * 足した契約コードの、消していない行のうち開始日が一番遅いもの（足した契約コードの行は全部「足した行」なので latestRow は使えない）
     */
    newestRow(con) {
      const rows = (con.rows || []).filter(r => !r.deleted)
      if (!rows.length) return null
      return rows.reduce((a, b) => ((b.startDateContract || '') > (a.startDateContract || '') ? b : a))
    },

    /**
     * 受注の条件の文字（「週4.5回 3時間 2名」。見積の内容の文字と同じ並び）
     */
    orderCondition(item) {
      return `週${item.weekTimes}回 ${item.hours}時間 ${item.people}名`
    },

    /**
     * 受注の作業内容に属する契約コード（受注から作った分と、手で足して作業内容を選んだ分。控え orderItem が同じもの）
     * 作業内容が1つだけなら、手で足した分は選ばなくてもその作業内容に入れる
     */
    orderItemContracts(item) {
      return (this.itemStats[item.key] || { contracts: [] }).contracts
    },

    /**
     * 受注タスクの週の回数（「週4.5回 2名」なら 4.5。人数は掛けない。無ければ 0＝比べない）
     * 人数分を足した「週8回」は人には変に見えるので、週の回数は物件として週に何日勤務があるかで比べる
     */
    orderWeekTimes(item) {
      return item.weekTimes || 0
    },

    /**
     * 作業内容に属する契約コードの月の時間の合計（曜日ごとの実働 × 勤務形態の係数。毎週 4.3・隔週 2.15・第n週 1・第5週 0.3）
     * 受注の「週4.5回」は 4.5 × 4.3 日分なので、毎週4＋隔週1 でも 毎週4＋第1・第3 でもほぼ同じ値になる
     * 契約コードに行が複数あれば、消していない行のうち開始日が一番遅い行
     */
    contractMonthlyHours(item) {
      return (this.itemStats[item.key] || { hours: 0 }).hours
    },

    /**
     * 作業内容に属する契約コードの、勤務のある曜日の数（週の回数）
     * 契約コードをまたいで同じ曜日は1つに数える（月火水 と 月火金 → 月火水金 ＝ 週4回）
     * 曜日ごとの重みは勤務形態の係数 ÷ 4.3（毎週 1・隔週 0.5・第n週 0.23）で、同じ曜日は一番大きい重み・最大 1（毎週4日＋隔週1日 ＝ 4.5、月の毎週と月の隔週 ＝ 1）
     */
    contractWeekTimes(item) {
      return (this.itemStats[item.key] || { weeks: 0 }).weeks
    },

    /**
     * 作業内容の許す差の割合（週の回数が整数なら 1%、.5 が付けば 5%）
     */
    tolerance(item) {
      return Number.isInteger(item.weekTimes) ? TOLERANCE_INTEGER : TOLERANCE_HALF
    },

    /**
     * 許す差の表示（「1%」「5%」）
     */
    toleranceText(item) {
      return `${Math.round(this.tolerance(item) * 100)}%`
    },

    /**
     * 受注タスクとの差が許す差を超えているか（受注タスクの値が出せなければ比べない）
     * 5%（.5 が付くとき）：隔週と第1・第3のような .5 の解釈違い（最大 2〜3%）は通り、1日・1人の違い（週7回でも 14%）は引っかかる
     * 1%（整数のとき）：第n週の係数の丸め（週4回 2名 なら 7.93 対 8）だけを通す。合計で比べるので、週4回 2名 を 週5＋週3 に分けても 8 で合う
     */
    over(item, order, contract) {
      return !!order && Math.abs(contract - order) > order * this.tolerance(item)
    },

    /**
     * 月の時間・週の回数それぞれが受注タスクと許す差以上違うか（表のセルの黄色）
     */
    hoursMismatch(item) {
      return this.over(item, item.monthlyHours, this.contractMonthlyHours(item))
    },

    weekMismatch(item) {
      return this.over(item, this.orderWeekTimes(item), this.contractWeekTimes(item))
    },

    /**
     * 作業内容の月の時間か週の回数が受注タスクと許す差以上違うか（表の判定と、申請時の確認）
     */
    itemMismatch(item) {
      return this.hoursMismatch(item) || this.weekMismatch(item)
    },

    /**
     * 明らかに違うか（月の時間が 10% 超、または週の回数が1日以上違う。申請・AKBS登録をエラーで止める）
     */
    hoursFar(item) {
      return !!item.monthlyHours && Math.abs(this.contractMonthlyHours(item) - item.monthlyHours) > item.monthlyHours * HOURS_FAR
    },

    weekFar(item) {
      const order = this.orderWeekTimes(item)
      // 浮動小数の誤差で 1 が 0.9999 になることがあるので少し緩める
      return !!order && Math.abs(this.contractWeekTimes(item) - order) >= WEEK_FAR - 0.001
    },

    itemFar(item) {
      return this.hoursFar(item) || this.weekFar(item)
    },

    /**
     * 表の判定の文（受注タスクに対して契約詳細が多いか少ないかを数で。「−51.6時間・週−2回」。割合は人には分かりにくいので出さない）
     */
    /**
     * 差の表示（+12.9 / −12.9。小数2桁）
     */
    signedHours(n) {
      const v = Math.round(Math.abs(n) * 100) / 100
      return (n < 0 ? '−' : '+') + v
    },

    judgeText(item) {
      const signed = n => (n > 0 ? '+' : '−') + Math.round(Math.abs(n) * 100) / 100
      const parts = []
      if (this.hoursMismatch(item)) {
        parts.push(`${signed(this.contractMonthlyHours(item) - item.monthlyHours)}時間`)
      }
      if (this.weekMismatch(item)) {
        parts.push(`週${signed(this.contractWeekTimes(item) - this.orderWeekTimes(item))}回`)
      }
      return parts.join('・')
    },

    /**
     * 作業内容の比べのツールチップ（式と許す差と、黄色のときの差）
     */
    compareTip(item) {
      const lines = ['受注タスク：月の時間 ＝ 週の回数 × 1回の時間 × 人数 × 4.3、週の回数 ＝ 受注の週の回数（人数は掛けない）', '契約詳細：月の時間 ＝ 曜日ごとの実働 × 勤務形態（毎週 4.3・隔週 2.15・第n週 1）の合計、週の回数 ＝ 勤務のある曜日の数（契約コードをまたいで同じ曜日は1つ。隔週 0.5・第n週 0.23）']
      lines.push(Number.isInteger(item.weekTimes) ? `許す差 ${this.toleranceText(item)}（週の回数が整数なので、ほぼ一致を求めます）` : `許す差 ${this.toleranceText(item)}（週の回数に .5 が付くので、隔週・第n週の違いを通します）`)
      if (this.over(item, item.monthlyHours, this.contractMonthlyHours(item))) {
        lines.push(`月の時間が受注タスクと ${Math.round(Math.abs(this.contractMonthlyHours(item) - item.monthlyHours) * 100) / 100}時間 違います（${this.toleranceText(item)} 以上）`)
      }
      if (this.over(item, this.orderWeekTimes(item), this.contractWeekTimes(item))) {
        lines.push(`週の回数が受注タスクと ${Math.round(Math.abs(this.contractWeekTimes(item) - this.orderWeekTimes(item)) * 100) / 100}回 違います（${this.toleranceText(item)} 以上）`)
      }
      return lines.join('<br>')
    },

    /**
     * 受注タスクと違う理由を書いてもらう（モーダル。書かなければ進めない）
     * 書いた理由はメモの「【受注タスクと違う理由】」の段に入れる（req_akbs_contract.memo。承認者に伝わる）。前に書いた理由があれば初期値にする
     * @param {String} lines 違う作業内容と差（表の判定と同じ文）
     * @returns {Promise<String>} 理由（キャンセルは空）
     */
    askDiffReason(lines) {
      return new Promise(resolve => {
        this.reasonModal = { open: true, lines, text: this.diffReasonFromMemo(), resolve }
      })
    },

    closeDiffReason(ok) {
      const { resolve, text } = this.reasonModal
      this.reasonModal = { ...this.reasonModal, open: false, resolve: null }
      if (resolve) resolve(ok ? String(text || '').trim() : '')
    },

    /**
     * メモに書いてある「受注タスクと違う理由」（無ければ空）
     */
    diffReasonFromMemo() {
      const m = String(this.val.memo || '').match(DIFF_REASON_RE)
      return m ? m[1].trim() : ''
    },

    /**
     * メモの「受注タスクと違う理由」の段を入れ替える（無ければ末尾に足す）
     */
    setDiffReason(reason) {
      const block = `${DIFF_REASON_HEAD}\n${reason}`
      const memo = String(this.val.memo || '')
      if (DIFF_REASON_RE.test(memo)) {
        // 置き換えは関数で渡す（文字で渡すと理由の中の「$」が特別な意味になる）
        this.val.memo = memo.replace(DIFF_REASON_RE, () => block).trim()
      } else {
        this.val.memo = (memo.trim() ? memo.trim() + '\n' : '') + block
      }
    },

    /**
     * メモの「受注タスクと違う理由」の段を消す（違いが無くなったのに前の理由が残らないように）
     */
    clearDiffReason() {
      const memo = String(this.val.memo || '')
      if (DIFF_REASON_RE.test(memo)) {
        this.val.memo = memo.replace(DIFF_REASON_RE, '').trim()
      }
    },

    /**
     * 手で足した契約コードの、受注の作業内容の選択（帯の multiselect）
     */
    orderItemOf(con) {
      return this.orderItems.find(item => item.key === con.orderItem) || null
    },
    setOrderItem(con, item) {
      con.orderItem = item ? item.key : ''
      this.reload++
    },

    /**
     * 受注の1件（作業曜日・時間）から契約コードを1つ足す（受注のどの1件かの控え付き）
     */
    contractFromOrder(site, entry) {
      const con = this.newContract(site, this.nextContractCode(site))
      this.setOrderInfo(con, entry)
      this.fillRowFromOrder(con.rows[0], entry)
      return con
    },

    /**
     * 契約コードに、受注のどの1件から作ったかの控えを入れる
     */
    setOrderInfo(con, entry) {
      con.orderKey = entry.orderKey
      con.orderItem = entry.itemKey
      con.orderName = entry.name
      con.orderMerged = entry.merged
      // 受注に勤務の行が無い分（人数の方が多いときに足した空の契約コード。保存・申請・登録時の確認に「○人目の手配が必要と思われます」と出す）
      con.orderBlank = !!entry.blank
      con.orderNth = entry.nth || 0
    },

    /**
     * 受注の1件の値を、行の欄の形にしたもの（初期値にする欄。受注に無い曜日は空）
     */
    orderRowValues(entry) {
      const v = {
        startDateContract: entry.startDate || '',
        cdContentsWork: entry.cdContentsWork,
        specificTime: entry.specificTime,
        kubunHolidayContract: entry.kubunHolidayContract === null ? '' : entry.kubunHolidayContract
      }
      // ゴミ搬出は受注の祝日対応が「ゴミ出しのみ」のときだけ「有り」で決まる（それ以外は受注に欄が無いので触らない）
      if (entry.hasCarryingoutGarbageContract !== null && entry.hasCarryingoutGarbageContract !== undefined) {
        v.hasCarryingoutGarbageContract = entry.hasCarryingoutGarbageContract
      }
      for (const w of WEEK_LIST) {
        const day = entry.days[w.id]
        v['attendance' + w.id] = day ? day.attendance.slice() : []
        v['start' + w.id] = day ? day.start : ''
        v['end' + w.id] = day ? day.end : ''
        v['workingHours' + w.id] = day ? day.workingHours : ''
      }
      return v
    },

    /**
     * 足した契約コードの行に、受注の入力を初期値として入れる
     * 元の値（original）も同じにして、黄色の変更表示をしない（足した契約コードの行はもともと出さない）
     * 元の値は開くたびに受注の今の値に入れ直す（syncOrder）ので、今の値 ≠ 元の値 ＝ 受注と違う欄（黄色）になる
     */
    fillRowFromOrder(row, entry) {
      Object.assign(row, this.orderRowValues(entry))
      row.fromOrder = true
      // 受注に勤務の行が無い分（勤務は空。曜日の欄は受注と比べない）
      row.orderBlank = !!entry.blank
      // 受注で決まっていて変えさせない欄（時間指定・祝日対応の区分）
      row.orderLock = { ...entry.fixed }
      // ラウンドプラスの固定清掃：祝日勤務は「FULL」の初期値で変えられる（変えたら黄色＋確認）
      row.orderHolidayDefault = !!entry.holidayDefault
      // 受注の祝日対応が「ゴミ出しのみ」（祝日勤務の欄に出す）
      row.orderHolidayGarbage = !!entry.holidayGarbage
      row.original = this.snapshot(row)
    },

    /**
     * 受注の今の値を、受注から作った契約コードの行の「元の値」に入れ直す（新規登録を保存済みで登録前に開いたとき）
     * 受注が親で、この画面は足りない情報を補う側。画面の値は変えず、元の値（＝受注の今の値）と違う欄が黄色になる（isChanged）
     * 一時保存のあとに受注が直された分も、この画面で直した分も、どちらも「今の受注と違う」ので同じ黄色。文字の知らせは出さない（ユーザー決定 2026-09-22）
     * 黄色の欄は「初期状態に戻す」で受注の今の値に戻せる（resetFromOrder）
     * 受注の1件と契約コードの対応：まず控え（orderKey）が同じもの。無ければ同じ作業内容（orderItem）の中で並び順
     * （受注の行の足し引き・まとめ方の変わりで orderKey が変わったとき用）。受注の方が多ければ契約コードを足す。
     * 契約コードの方が多ければそのまま残す（人数より多いのは OK）。手で足した契約コード（orderKey が空）は触らない
     * この画面で「取消」した契約コード（site.removedOrderKeys）は、受注にあっても足し直さない（2026-09-23。取り消したのに開き直すと増えていたため）
     */
    syncOrder(akbs) {
      const site = akbs.sites[0]
      if (!site) return
      // getData の中で呼ばれる時点では this.val（種別）がまだ入っていないので、computed の orderEntries は使わず直接作る
      const removed = site.removedOrderKeys || []
      const entries = contractsFromOrder(this.rp).filter(e => !removed.includes(e.orderKey))
      const fromOrder = site.contracts.filter(c => c.isNewContract && c.orderKey)
      const used = []

      // 控えが同じもの
      const pairs = []
      const rest = []
      for (const entry of entries) {
        const con = fromOrder.find(c => !used.includes(c) && c.orderKey === entry.orderKey)
        if (con) {
          used.push(con)
          pairs.push({ con, entry })
        } else {
          rest.push(entry)
        }
      }
      // 同じ作業内容の中で並び順
      for (const entry of rest) {
        const con = fromOrder.find(c => !used.includes(c) && c.orderItem === entry.itemKey)
        if (con) {
          used.push(con)
          pairs.push({ con, entry })
        } else {
          site.contracts.push(this.contractFromOrder(site, entry))
        }
      }

      for (const { con, entry } of pairs) {
        this.setOrderInfo(con, entry)
        const row = con.rows[0]
        if (!row) continue
        row.fromOrder = true
        row.orderBlank = !!entry.blank
        row.orderLock = { ...entry.fixed }
        row.orderHolidayDefault = !!entry.holidayDefault
        row.orderHolidayGarbage = !!entry.holidayGarbage
        const latest = this.orderRowValues(entry)
        for (const key of orderKeysOf(row)) {
          row.original[key] = utils.clone(latest[key])
        }
      }
    },

    /**
     * 受注と違う欄（黄色）があるか（契約コード1件）
     */
    hasOrderDiff(con) {
      return con.rows.some(row => row.fromOrder && orderKeysOf(row).some(key => this.isChanged(row, key)))
    },

    /**
     * 受注から作った契約コードの行を、受注の今の値（元の値）に戻す（帯の「初期状態に戻す」）
     */
    resetFromOrder(con) {
      for (const row of con.rows) {
        if (!row.fromOrder) continue
        for (const key of orderKeysOf(row)) {
          row[key] = utils.clone(row.original[key])
        }
      }
      this.reload++
    },

    /**
     * 黄色の欄に出す、受注の今の値（受注から作った契約コードの行だけ。違わなければ空）
     */
    orderTip(row, key) {
      if (!row.fromOrder || !this.isChanged(row, key)) return ''
      const v = row.original[key]
      let text = ''
      if (key === 'startDateContract') {
        text = this.formatDate(v)
      } else if (key === 'cdContentsWork') {
        text = this.workName({ cdContentsWork: v })
      } else if (key.startsWith('attendance')) {
        text = this.attendanceNames(v)
      } else if (key === 'kubunHolidayContract') {
        // ラウンドプラスの固定清掃の祝日勤務（受注には欄が無く「FULL」が初期値）
        return `初期値：${this.holidayText(v)}（ラウンドプラスの固定清掃は祝日勤務「FULL」が初期値です。変えると申請・承認のときに確認が出ます）`
      } else {
        text = v === null || v === undefined ? '' : String(v)
      }
      return `受注：${text || '（なし）'}`
    },

    /**
     * ラウンドプラスの固定清掃の祝日勤務を初期値の「FULL」から変えた行か（目立つ黄色にして、申請・承認時に確認する）
     */
    isHolidayChangedFromDefault(row) {
      return !!row.orderHolidayDefault && this.isChanged(row, 'kubunHolidayContract')
    },

    /**
     * 祝日勤務を初期値の「FULL」から変えた行の一覧（申請・承認時の確認に出す）
     */
    holidayChangedRows() {
      const list = []
      for (const site of this.akbs.sites) {
        for (const con of site.contracts) {
          for (const row of con.rows) {
            if (row.deleted || !this.isHolidayChangedFromDefault(row)) continue
            list.push(`・契約コード ${con.cdContract}（${con.orderName || ''}）：祝日勤務「FULL」→「${this.holidayText(row.kubunHolidayContract)}」`)
          }
        }
      }
      return list
    },

    /**
     * 新規登録の物件コードの候補（同じ得意先の物件）を取る
     * API: akbsTable/getSiteListForEstimate（得意先コードで絞る）
     */
    async loadCreateSiteList() {
      for (const site of this.akbs.sites) {
        if (this.orderSiteLocked) {
          // 受注タスクで物件が選んであれば受注タスクが親：控えと違えば（一時保存のあとに受注タスクで物件を選んだ・選び直した）受注タスクの物件に合わせる
          if (this.rp.clientCode) {
            site.clientCode = String(this.rp.clientCode)
            site.clientName = this.rp.clientName || ''
          }
          if (String(site.siteCode || '') !== String(this.rp.siteCode)) {
            await this.applyCreateSite(site, String(this.rp.siteCode), null)
          }
        } else if (!site.clientCode && this.rp.clientCode) {
          // 得意先の初期値は受注タスクの得意先（保存してあればその値）
          site.clientCode = String(this.rp.clientCode)
          site.clientName = this.rp.clientName || ''
        }
        await this.loadSitesOf(site.clientCode)
        // 物件名は候補から写す（受注タスクから合わせた物件や、前に直接入力した控えには名前が無い）
        const item = this.createSiteItem(site)
        if (item) {
          site.site1 = item.site1 || ''
          site.site2 = item.site2 || ''
          site.agreementFlg = item.agreementFlg || ''
        }
      }
    },

    /**
     * 得意先の物件の候補を取って控える（得意先ごとに1回）
     * API: akbsTable/getSiteListForEstimate
     */
    async loadSitesOf(clientCode) {
      if (!clientCode || this.siteListByClient[clientCode]) return
      try {
        const res = await backend.searchData('akbsTable/getSiteListForEstimate', { clientCode })
        this.$set(this.siteListByClient, clientCode, (res.data && res.data.data) || [])
      } catch (e) {
        console.log(e)
      }
    },

    /**
     * 得意先の候補を作るための全物件（得意先の欄を初めて開いたときに取る。新しい得意先は AKBS に取り込まれていれば出る）
     * API: akbsTable/getSiteListForEstimate
     */
    async loadAllSites() {
      if (this.allSites.length || this.allSitesLoading) return
      this.allSitesLoading = true
      try {
        const res = await backend.searchData('akbsTable/getSiteListForEstimate', {})
        this.allSites = (res.data && res.data.data) || []
      } catch (e) {
        console.log(e)
      } finally {
        this.allSitesLoading = false
      }
    },

    /**
     * 新規登録の物件の候補（その物件で選んでいる得意先の物件）
     */
    siteListFor(site) {
      return (site.clientCode && this.siteListByClient[site.clientCode]) || []
    },

    /**
     * 得意先の候補の表示（検索はこの文字に対して効く）
     */
    clientLabel(item) {
      return `${item.clientCode} ${item.clientName || ''}`.trim()
    },

    /**
     * 新規登録の物件で今選んでいる得意先（候補の形）
     */
    clientItem(site) {
      if (!site.clientCode) return null
      return this.clientOptions.find(c => c.clientCode === String(site.clientCode)) || { clientCode: String(site.clientCode), clientName: site.clientName || '' }
    },

    /**
     * 受注タスクの物件名と、物件マスタの物件名（候補の名前）が違うか
     * 受注タスクで物件を選んだあとに物件名を貼り替えると、物件コードと画面の物件名がずれるので確かめる
     * 空白と全角・半角の違いは無視する。候補に無ければ比べない（別の赤字で知らせる）
     */
    orderSiteNameDiff(site) {
      if (!this.orderSiteLocked) return false
      const item = this.createSiteItem(site)
      if (!item || !this.orderSiteName) return false
      const norm = s => String(s || '').normalize('NFKC').replace(/\s/g, '')
      return norm(this.orderSiteName) !== norm(this.siteName(item))
    },

    /**
     * 選んでいる得意先が受注タスクの得意先と違うか（受注タスクにコードが無ければ「違う」とはしない。案内は別に出す）
     */
    isClientDiff(site) {
      return !!this.rp.clientCode && !!site.clientCode && String(site.clientCode) !== String(this.rp.clientCode)
    },

    /**
     * 新規登録の得意先を選び直した。受注タスクの得意先と違えば確認を取る（キャンセルで元のまま）。変えたら物件は選び直し
     */
    async clientChanged(site, item) {
      if (!item || String(item.clientCode) === String(site.clientCode)) return
      if (this.rp.clientCode && String(item.clientCode) !== String(this.rp.clientCode)) {
        const dialog = await dialogs.showConfirmDialog('受注タスクの得意先と違います', `受注タスクの得意先は「${this.rp.clientName}（${this.rp.clientCode}）」ですが、選んだのは「${item.clientName}（${item.clientCode}）」です。\nこの得意先の物件に契約詳細を登録してよいですか？`)
        if (dialog !== 'YES') {
          this.reload++
          return
        }
      }
      site.clientCode = String(item.clientCode)
      site.clientName = item.clientName || ''
      await this.applyCreateSite(site, '', null)
      await this.loadSitesOf(site.clientCode)
      this.reload++
    },

    /**
     * 新規登録の物件の候補の表示（検索はこの文字に対して効く）
     */
    siteLabel(item) {
      return `${item.siteCode} ${item.site1 || ''} ${item.site2 || ''} ${item.agreementFlg || ''}`.trim()
    },

    /**
     * 新規登録の物件で今選んでいる候補（控えてある物件コードが候補に無ければ null）
     */
    createSiteItem(site) {
      if (!site.siteCode) return null
      return this.siteListFor(site).find(b => String(b.siteCode) === String(site.siteCode)) || null
    },

    /**
     * 新規登録の物件を候補から選んだ・外した（候補からしか選べない。直接入力は無し）
     */
    async createSiteChanged(site, item) {
      await this.applyCreateSite(site, item ? String(item.siteCode) : '', item || null)
    },

    /**
     * 新規登録の物件コードを決める
     * その物件に今ある契約を取り直し、この画面で足した契約コードは、今ある契約とぶつからないように採番し直す
     * @param {Object} site 物件
     * @param {String} code 物件コード（空なら未定）
     * @param {Object} info 候補から選んだ物件（物件名を写す。外したときは null）
     */
    async applyCreateSite(site, code, info) {
      if (String(site.siteCode || '') === code) return
      this.loading = true
      try {
        let contracts = []
        if (code) {
          // API: akbsContractForm/getSiteContracts
          const res = await backend.searchData('akbsContractForm/getSiteContracts', { siteCode: code })
          contracts = ((res.data && res.data.data) || []).map(c => this.buildContract(c))
        }
        const added = site.contracts.filter(c => c.isNewContract)
        site.siteCode = code
        if (info) {
          site.site1 = info.site1 || ''
          site.site2 = info.site2 || ''
          site.agreementFlg = info.agreementFlg || ''
        }
        site.contracts = contracts
        for (const con of added) {
          con.cdSite = code
          con.cdSalesOffice = contracts.length ? contracts[0].cdSalesOffice || '' : con.cdSalesOffice
          con.blocked = ''
          for (const row of con.rows) {
            row.cdSite = code
          }
          site.contracts.push(con)
        }
        // 足した契約コードは、その物件の契約の続きから上から順に振り直す
        this.renumberContracts(site)
        this.$set(this.opened, code, true)
        this.$set(this.showEnded, code, false)
        this.reload++
      } catch (e) {
        console.log(e)
        await this.errorDialog('取得エラー', '物件の契約詳細の取得に失敗しました。')
      } finally {
        this.loading = false
      }
    },

    /**
     * 空の行（契約コード追加用）。元の値も空にしておき、入れた値を「変更」として黄色にはしない
     */
    blankRow(site, code) {
      const row = {
        key: 'new' + (++rowSeq),
        isNew: true,
        deleted: false,
        notInAkbs: false,
        origSite: '',
        origStart: '',
        cdSite: site.newSiteCode || site.siteCode,
        cdContract: code,
        startDateContract: this.rp.changeDate || '',
        endDateContract: '',
        // 足した契約コードの行（黄色の変更表示をしない）
        addedContract: true,
        changed: {}
      }
      for (const key of [...FLAG_KEYS, ...NOTE_KEYS]) {
        row[key] = ''
      }
      for (const w of WEEK_LIST) {
        row['attendance' + w.id] = []
        row['start' + w.id] = ''
        row['end' + w.id] = ''
        row['workingHours' + w.id] = ''
      }
      row.original = this.snapshot(row)
      return row
    },

    /**
     * 修正（上書き）で行を追加（最新の行を写す。開始日は空）
     */
    addRow(site, con) {
      const last = this.latestRow(con) || con.rows[con.rows.length - 1]
      if (!last) {
        return
      }
      const row = this.copyRow(last)
      row.startDateContract = ''
      row.cdSite = site.newSiteCode || last.cdSite
      con.rows.push(row)
      this.reload++
    },

    /**
     * 行を削除（足した行は取り除く。まだ始まっていない本番の行は消す印を付けて承認時に DELETE）
     */
    async deleteRow(con, row) {
      const res = await dialogs.showConfirmDialog('行を削除します', `${this.formatDate(row.startDateContract) || '開始日未入力'} からの行を削除します。よろしいですか？`, '削除する')
      if (res !== 'YES') {
        return
      }
      if (row.isNew) {
        const i = con.rows.indexOf(row)
        if (i > -1) con.rows.splice(i, 1)
      } else {
        row.deleted = true
      }
      this.reload++
    },

    // ------------------------------------------------------------
    // 行の判定
    // ------------------------------------------------------------

    /**
     * 最新の行（消していない本番の行のうち、開始日が一番遅いもの。足した行は除く）
     */
    latestRow(con) {
      const rows = con.rows.filter(r => !r.deleted && !r.isNew)
      if (!rows.length) return null
      return rows.reduce((a, b) => (b.startDateContract > a.startDateContract ? b : a))
    },

    /**
     * 画面に出す行（消した行は出さない。終わった行はチェックを入れたときだけ）
     */
    visibleRows(site, con) {
      return con.rows.filter(r => !r.deleted && (r.isNew || this.showEnded[site.siteCode] || !this.isEnded(r)))
    },

    /**
     * 物件の中の終了した行の数（「終了した行も表示」の件数。消した行は数えない）
     */
    endedCount(site) {
      let n = 0
      for (const con of site.contracts) {
        n += con.rows.filter(r => !r.deleted && this.isEnded(r)).length
      }
      return n
    },

    /**
     * 契約1件の左の操作セルがまたがる行数（行 ＋ 行を追加 ＋ 担当清掃員 ＋ 止める文）
     */
    groupRows(site, con) {
      // 行1件につき、勤務の行と備考の行の2行
      return this.visibleRows(site, con).length * 2 + (con.mode === 'over' && !this.isReadOnly ? 1 : 0) + Math.max(con.staffs.length, 1) + (con.blocked ? 1 : 0)
    },

    /**
     * まだ始まっていない行か（開始日が今日より後）
     */
    isFuture(row) {
      return !!row.startDateContract && row.startDateContract > this.today
    },

    /**
     * 終わった行か（AKBSの今の終了日が今日より前）
     * 画面で入れた終了日ではなく元の値で見る。変更（履歴残す）で変更前の行に過去の終了日が自動で入っても、隠れないようにするため
     */
    isEnded(row) {
      const end = row.original ? row.original.endDateContract : row.endDateContract
      return !row.isNew && !!end && end < this.today
    },

    /**
     * 最新の行か
     */
    isLatest(con, row) {
      return this.latestRow(con) === row
    },

    /**
     * 変更（履歴残す）で終了日を自動で入れた行か（足した行の1つ前＝最新の行）
     */
    isPrevOfNew(con, row) {
      return con.mode === 'new' && !con.blocked && !row.isNew && this.isLatest(con, row)
    },

    /**
     * 中身を直せる行か（修正（上書き）の行、足した行）
     */
    isEditable(con, row) {
      return !this.isReadOnly && (con.mode === 'over' || row.isNew)
    },

    /**
     * 終了日だけ直せる行か（変更（履歴残す）の変更前の行、契約終了の最新の行）
     */
    isEndEditable(con, row) {
      if (this.isReadOnly || row.isNew) return false
      // 変更（履歴残す）の変更前の行の終了日は、変更後の開始日の前日が自動で入るので手では直せない
      if (con.mode === 'end') return this.isLatest(con, row) && !con.blocked
      return false
    },

    /**
     * 契約開始日を変えた
     * 変更（履歴残す）で足した行なら、変更前の行の終了日をその前日に合わせる
     */
    startChanged(con, row) {
      if (con.mode === 'new' && row.isNew) {
        const prev = this.latestRow(con)
        if (prev) {
          prev.endDateContract = row.startDateContract ? this.dayBefore(row.startDateContract) : ''
        }
      }
      this.reload++
    },

    /**
     * 行の見た目のクラス
     */
    rowClass(con, row) {
      if (row.isNew) return 'newrow'
      if (con.mode === 'over') return 'edit'
      // 変更（履歴残す）の変更前の行は全体を薄いグレーにする
      if (this.isPrevOfNew(con, row)) return 'view prev'
      return this.isEnded(row) ? 'view ended' : 'view'
    },

    badgeText(row) {
      if (row.isNew) return '追加する行'
      if (this.isEnded(row)) return '終了した行'
      return this.isFuture(row) ? 'これからの行' : '適用中'
    },

    badgeClass(row) {
      if (row.isNew || this.isFuture(row)) return 'future'
      return this.isEnded(row) ? 'ended' : 'now'
    },

    /**
     * 作業内容の名前（選択肢に無いコードは取得時の名前かコード）
     */
    workName(row) {
      const w = WORK_SELECTIONS.find(x => x.id == row.cdContentsWork)
      return w ? w.name : (row.contentsWorkName || row.cdContentsWork || '')
    },

    attendanceNames(codes) {
      return utilsAKBS.attendanceNames(Array.isArray(codes) ? codes.map(a => (a && a.id !== undefined ? a.id : a)) : codes)
    },

    // ------------------------------------------------------------
    // 変わったところ
    // ------------------------------------------------------------

    /**
     * 同じ値か（勤務形態はコードの並び、それ以外は文字にして比べる。未入力どうしは同じ）
     */
    same(a, b) {
      return this.compareText(a) === this.compareText(b)
    },

    /**
     * 比べるための文字（勤務形態は id を並べる。数値・空は揃える）
     */
    compareText(v) {
      if (Array.isArray(v)) {
        return v.map(a => (a && a.id !== undefined ? a.id : a)).join(',')
      }
      if (v === null || v === undefined || v === '') {
        return ''
      }
      if (!isNaN(v) && String(v).trim() !== '') {
        return String(Number(v))
      }
      return String(v)
    },

    /**
     * 元の値から変わった欄か
     */
    isChanged(row, key) {
      // 足した契約コードの行は全部が新しい値なので「変更」扱いにしない
      // ただし受注から作った行は、受注画面で変わりうる欄（ORDER_KEYS）だけ、受注の今の値（元の値）と違えば黄色にする
      if (row.addedContract) return !!row.fromOrder && orderKeysOf(row).includes(key) && !this.same(row[key], row.original[key])
      return !this.same(row[key], row.original[key])
    },

    /**
     * 行1件の変更箇所の数（画面で黄色になっているものの数）
     * ・勤務ありを勤務なしにした曜日：「勤務なしに変更」の印1つ
     * ・それ以外の曜日：勤務形態・開始・終了・実働の変わった欄をそれぞれ1つ
     * ・区分は1項目1つ、契約期間も1つずつ
     */
    rowChangeCount(row) {
      let n = 0
      for (const w of WEEK_LIST) {
        if (this.isDayOff(row, w)) {
          if (!this.isDayOff(row.original, w)) n++
          continue
        }
        n += DAY_KEYS.filter(k => this.isChanged(row, k + w.id)).length
      }
      n += FLAG_KEYS.filter(key => this.isChanged(row, key)).length
      n += NOTE_KEYS.filter(key => this.isChanged(row, key)).length
      n += DATE_KEYS.filter(key => this.isChanged(row, key)).length
      return n
    },

    /**
     * 物件の中の変更のある契約の数
     */
    siteChangeCount(site) {
      return site.contracts.filter(c => c.mode !== 'none').length
    },

    /**
     * 物件の中の変更箇所の数（物件の見出しに出す。雇用指示の表の「n箇所変更」と同じ数え方）
     * 画面で黄色になっている欄の数。修正（上書き）で消した行は画面から消えるので、1行を1箇所に数える
     * 足した契約コードは変更ではなく追加として数える（siteAddedCount）
     */
    siteChangedFieldCount(site) {
      let n = 0
      for (const con of site.contracts) {
        if (con.mode === 'none' || con.isNewContract) continue
        for (const row of con.rows) {
          n += row.deleted ? 1 : this.rowChangeCount(row)
        }
      }
      return n
    },

    /**
     * 物件の中の足した契約コードの数（物件の見出しに出す）
     */
    siteAddedCount(site) {
      return site.contracts.filter(c => c.isNewContract).length
    },

    /**
     * 畳める契約か（変更なしで、同じ物件のほかの契約に変更がある）
     * どの契約もまだ触っていない物件は、どこを押せばよいか分かるように全部開いたままにする
     */
    canFold(site, con) {
      return con.mode === 'none' && this.siteChangeCount(site) > 0
    },

    /**
     * 畳んでいるか（畳める契約は、手で開くまで見出しの帯の1行だけにする）
     */
    isFolded(site, con) {
      return this.canFold(site, con) && !this.conOpened[site.siteCode + '.' + con.key]
    },

    /**
     * 契約の見出しの帯を押した（畳める契約だけ開閉する）
     * キーを後から足すのでオブジェクトごと入れ替える
     */
    toggleCon(site, con) {
      if (!this.canFold(site, con)) return
      const key = site.siteCode + '.' + con.key
      this.conOpened = { ...this.conOpened, [key]: !this.conOpened[key] }
    },

    /**
     * 変わったところにフラグを立てる（バックが書く項目を絞る。承認者・メインタスクにも使う）
     */
    markChanged() {
      for (const site of this.akbs.sites) {
        for (const con of site.contracts) {
          for (const row of con.rows) {
            const changed = {}
            for (const key of [...EDIT_KEYS, ...DATE_KEYS]) {
              if (this.isChanged(row, key)) changed[key] = true
            }
            if (row.isNew) {
              changed.isNew = true
              if (site.newSiteCode && site.newSiteCode != con.cdSite) {
                row.cdSite = site.newSiteCode
                changed.cdSite = true
              }
            }
            row.changed = changed
          }
        }
      }
    },

    // ------------------------------------------------------------
    // 入力の補助（雇用指示の表と同じ）
    // ------------------------------------------------------------

    /**
     * 受注タスク（見積）で決まっていて、この画面では変えさせない欄か（受注から作った行の、受注で選んである時間指定・祝日対応の区分）
     * 見積から受注タスクに写された欄は受注画面でも変えられないので、ここでも変えさせない
     */
    isOrderLocked(row, key) {
      return !!row.fromOrder && !!row.orderLock && !!row.orderLock[key]
    },

    /**
     * 変えられない欄のツールチップ（変えられる欄は空）
     */
    orderLockTip(row, key) {
      if (!this.isOrderLocked(row, key)) return ''
      if (key === 'garbage') return '受注タスクの祝日対応が「ゴミ出しのみ」なので、ゴミ搬出は「有り」です（この画面では変えられません）'
      return ORDER_LOCK_TIP
    },

    holidayKind(row) {
      const value = row.kubunHolidayContract
      // 空は「時間入力」の入力途中（時間を消して入れ直すとき）として扱い、入力欄を残す
      if (value === null || value === undefined || value === '') return 'hours'
      if (Number(value) === 100) return '100'
      return Number(value) === 0 ? '0' : 'hours'
    },

    changeHolidayKind(row, kind) {
      row.kubunHolidayContract = kind === 'hours' ? 1 : Number(kind)
      this.reload++
    },

    holidayText(value) {
      if (value === null || value === undefined || value === '') return '-'
      const n = Number(value)
      if (n === 100) return 'FULL'
      if (n === 0) return '無'
      return `${n}時間`
    },

    flagText(value) {
      if (value === null || value === undefined || value === '') return '-'
      return Number(value) === 1 ? '有り' : '無し'
    },

    /**
     * クリーンプラス金額の表示（3桁区切り＋円。空は「-」）
     */
    cleanPlusText(value) {
      if (value === null || value === undefined || value === '') return '-'
      return Number(value).toLocaleString() + '円'
    },

    /**
     * 有り・無しの値が id（1／0）か。空は 0 と見なさない（どちらのボタンも塗らない）
     */
    flagIs(value, id) {
      if (value === null || value === undefined || value === '') return false
      return Number(value) === id
    },

    /**
     * 有り・無しのボタンを押した
     */
    setFlag(row, key, id) {
      row[key] = id
      this.reload++
    },

    /**
     * 必須の欄が空か（入力できる行の入力欄を薄い赤にする。清掃員の申請画面の必須の欄と同じ色）
     * ・契約開始日・作業内容・祝日勤務・ゴミ搬出・全社休日出勤：空なら
     * ・開始時間・終了時間・実働：その曜日に勤務形態が入っているのに空なら（実働は 0 も空と見る。保存時の確認と同じ）
     * 時間指定（有り・無し）と契約終了日は必須ではない
     * @param {Object} row 行
     * @param {String} key 欄のキー（曜日の欄は 'start' + 曜日の id など）
     */
    isRequiredEmpty(row, key) {
      const empty = v => v === '' || v === null || v === undefined
      if (['startDateContract', 'cdContentsWork', 'kubunHolidayContract', 'hasCarryingoutGarbageContract', 'hasExportingCompanysHolidayContract'].includes(key)) {
        return empty(row[key])
      }
      const day = WEEK_LIST.find(w => ['start' + w.id, 'end' + w.id, 'workingHours' + w.id].includes(key))
      if (!day || !(row['attendance' + day.id] || []).length) {
        return false
      }
      return key === 'workingHours' + day.id ? !Number(row[key]) : empty(row[key])
    },

    /**
     * その曜日は勤務なしか（勤務形態が無く、時間・実働も無い）
     */
    isDayOff(row, w) {
      const at = row['attendance' + w.id]
      const hours = Number(row['workingHours' + w.id]) || 0
      return (!Array.isArray(at) || !at.length) && !hours && !row['start' + w.id] && !row['end' + w.id]
    },

    /**
     * 勤務形態を変えたとき（全部外したら休みにして、時間・実働も消す）
     */
    attendanceChanged(row, wId) {
      if (!row['attendance' + wId] || !row['attendance' + wId].length) {
        row['start' + wId] = ''
        row['end' + wId] = ''
        row['workingHours' + wId] = ''
      }
      this.reload++
    },

    /**
     * 勤務形態の候補を開いたとき、候補を画面基準（fixed）の位置に出す
     * （表は横スクロールの枠の中にあり、そのままだと候補が枠で切れて行の高さまでしか見えない）
     * @param {String} refName 開いた multiselect の ref 名
     */
    attendanceOpened(refName) {
      const ms = (this.$refs[refName] || [])[0]
      if (!ms) {
        return
      }

      this.$nextTick(() => {
        const rect = ms.$el.getBoundingClientRect()
        const style = ms.$refs.list.style
        style.position = 'fixed'
        style.left = rect.left + 'px'
        style.width = rect.width + 'px'
        // 下に余白が無いときは部品が上向きに開く（isAbove）
        style.top = ms.isAbove ? 'auto' : rect.bottom + 'px'
        style.bottom = ms.isAbove ? window.innerHeight - rect.top + 'px' : 'auto'
      })

      // 画面基準の位置なので、スクロールや画面幅の変更で欄とずれる。そのときは閉じる（候補の中のスクロールは除く）
      this.attendanceClosed()
      this.closeAttendance = (e) => {
        if (e.target instanceof Node && ms.$refs.list.contains(e.target)) {
          return
        }
        ms.deactivate()
      }
      window.addEventListener('scroll', this.closeAttendance, true)
      window.addEventListener('resize', this.closeAttendance)
    },

    /**
     * 勤務形態の候補を閉じたとき（スクロールの見張りをやめる）
     */
    attendanceClosed() {
      if (this.closeAttendance) {
        window.removeEventListener('scroll', this.closeAttendance, true)
        window.removeEventListener('resize', this.closeAttendance)
        this.closeAttendance = null
      }
    },

    /**
     * 時間を変えたとき、実働時間を入れる（6時間超は休憩1時間を引く。雇用指示の表と同じ）
     */
    async setTime(row, wId) {
      await this.$nextTick()
      let hours = utils.getHours(row['start' + wId], row['end' + wId])
      if (hours > 6) {
        hours--
      }
      row['workingHours' + wId] = hours ? String(hours) : ''
      this.reload++
    },

    copyDay(row, wId) {
      this.copied = {
        attendance: utils.clone(row['attendance' + wId] || []),
        start: row['start' + wId],
        end: row['end' + wId],
        workingHours: row['workingHours' + wId]
      }
    },

    pasteDay(row, wId) {
      if (!this.copied) return
      row['attendance' + wId] = utils.clone(this.copied.attendance)
      row['start' + wId] = this.copied.start
      row['end' + wId] = this.copied.end
      row['workingHours' + wId] = this.copied.workingHours
      this.reload++
    },

    /**
     * 行ごとのコピー・貼付で写す欄（月〜日の7曜日分の勤務だけ。ユーザー指示 2026-10-09）
     * 作業内容・祝日勤務・ゴミ搬出・全社休日出勤・時間指定・契約コード・物件コード・契約開始日・契約終了日は写さない
     */
    rowCopyKeys() {
      const keys = []
      for (const w of WEEK_LIST) {
        for (const k of DAY_KEYS) {
          keys.push(k + w.id)
        }
      }
      return keys
    },

    /**
     * 行の勤務内容をまるごとコピーする（見るだけの行からもできる。物件をまたいで貼れる）
     */
    copyRowValues(row) {
      const values = {}
      for (const key of this.rowCopyKeys()) {
        values[key] = utils.clone(row[key])
      }
      this.copiedRow = values
    },

    /**
     * コピーした行の勤務内容をこの行へ貼り付ける（何か入っていても確認なしで上書きする。曜日の貼付と同じ）
     */
    pasteRowValues(row) {
      if (!this.copiedRow) return
      for (const key of this.rowCopyKeys()) {
        row[key] = utils.clone(this.copiedRow[key])
      }
      this.reload++
    },

    // ------------------------------------------------------------
    // 物件
    // ------------------------------------------------------------

    /**
     * 物件追加モーダルを開く（得意先の物件リストは初回だけ取る）
     */
    async openSiteModal() {
      if (!this.rp.clientCode) {
        await this.errorDialog('得意先がありません', 'メインタスクに得意先が入っていないため、物件を追加できません。')
        return
      }
      if (!this.siteList.length) {
        this.loading = true
        try {
          // API: akbsTable/getSiteListForEstimate（得意先コードで絞る）
          const res = await backend.searchData('akbsTable/getSiteListForEstimate', { clientCode: this.rp.clientCode })
          this.siteList = (res.data && res.data.data) || []
        } catch (e) {
          console.log(e)
          await this.errorDialog('取得エラー', '得意先の物件リストの取得に失敗しました。')
          return
        } finally {
          this.loading = false
        }
      }
      this.siteModal = true
    },

    /**
     * モーダルで選んだ物件を足す（契約詳細の今の値を取る）
     */
    async sitesAdded(list) {
      this.siteModal = false
      if (!list || !list.length) return
      this.loading = true
      try {
        for (const b of list) {
          if (this.akbs.sites.some(s => s.siteCode == b.siteCode)) continue
          // API: akbsContractForm/getSiteContracts
          const res = await backend.searchData('akbsContractForm/getSiteContracts', { siteCode: b.siteCode })
          const contracts = ((res.data && res.data.data) || []).map(c => this.buildContract(c))
          const site = this.buildSite(b, contracts)
          this.akbs.sites.push(site)
          this.$set(this.opened, b.siteCode, true)
          this.$set(this.showEnded, b.siteCode, false)
          for (const con of contracts) {
            this.applyMode(site, con, this.defaultMode(site))
          }
        }
        if (this.rp.changeClient) {
          await this.loadNewSiteLists()
        }
      } catch (e) {
        console.log(e)
        await this.errorDialog('取得エラー', '物件の契約詳細の取得に失敗しました。')
      } finally {
        this.loading = false
      }
    },

    /**
     * 得意先変更のときの、変更後の物件コードの候補を取る
     * 変更後の得意先コードがあれば、その得意先の物件（全物件で同じ候補）。無ければ、物件ごとに契約中で同じ物件名の物件
     */
    async loadNewSiteLists() {
      const lists = {}
      try {
        if (this.rp.clientCode2) {
          // API: akbsTable/getSiteListForEstimate（得意先コードで絞る）
          const res = await backend.searchData('akbsTable/getSiteListForEstimate', { clientCode: this.rp.clientCode2 })
          const list = (res.data && res.data.data) || []
          for (const site of this.akbs.sites) {
            lists[site.siteCode] = list.filter(b => b.siteCode != site.siteCode)
          }
        } else {
          for (const site of this.akbs.sites) {
            if (this.newSiteLists[site.siteCode]) {
              lists[site.siteCode] = this.newSiteLists[site.siteCode]
              continue
            }
            const name = site.site1 || site.site2
            if (!name) {
              lists[site.siteCode] = []
              continue
            }
            // API: akbsTable/getSiteListForEstimate（物件名で絞る）。契約中のものだけ
            const res = await backend.searchData('akbsTable/getSiteListForEstimate', { site: name })
            const list = (res.data && res.data.data) || []
            lists[site.siteCode] = list.filter(b => b.siteCode != site.siteCode && b.agreementFlg && b.agreementFlg.indexOf('契約中') > -1)
          }
        }
      } catch (e) {
        console.log(e)
        await this.errorDialog('取得エラー', '変更後の物件コードの候補の取得に失敗しました。')
      }
      this.newSiteLists = lists
    },

    newSiteCandidates(site) {
      return this.newSiteLists[site.siteCode] || []
    },

    newSiteSelected(site, e) {
      site.newSiteCode = e.siteCode
      // 選んだあとの表示は物件コードだけ
      site.newSiteName = e.siteCode
      this.syncNewSite(site)
    },

    newSiteChanged(site) {
      if (!site.newSiteName) {
        site.newSiteCode = ''
      }
      this.syncNewSite(site)
    },

    /**
     * 変更後の物件コードを、足した行の物件コードに写す
     */
    syncNewSite(site) {
      for (const con of site.contracts) {
        for (const row of con.rows) {
          if (row.isNew) row.cdSite = site.newSiteCode || con.cdSite
        }
      }
    },

    /**
     * 物件を外す（変更があれば確認する）
     */
    async removeSite(site) {
      const n = this.siteChangeCount(site)
      const message = n
        ? `${this.siteName(site)} には変更が ${n}件 あります。入力した内容ごと申請の対象から除外しますか？`
        : `${this.siteName(site)} を申請の対象から除外しますか？`
      const dialog = await dialogs.showConfirmDialog('物件を除外します', message, '除外する')
      if (dialog !== 'YES') return
      const i = this.akbs.sites.indexOf(site)
      if (i > -1) this.akbs.sites.splice(i, 1)
    },

    toggle(site) {
      this.opened[site.siteCode] = !this.opened[site.siteCode]
    },

    allOpen() {
      for (const site of this.akbs.sites) this.opened[site.siteCode] = true
    },

    allClose() {
      for (const site of this.akbs.sites) this.opened[site.siteCode] = false
    },

    /**
     * 変更のある物件だけ開く（承認者が開いたとき用）
     */
    openChangedOnly() {
      for (const site of this.akbs.sites) {
        this.opened[site.siteCode] = this.siteChangeCount(site) > 0
      }
    },

    siteName(site) {
      return ((site.site1 || '') + (site.site2 ? ' ' + site.site2 : '')).trim() || site.buildingName || ''
    },

    isEndedSite(site) {
      return !!(site.agreementFlg && site.agreementFlg.indexOf('解約') > -1)
    },

    formatDate(d) {
      if (!d) return ''
      const m = moment(String(d).slice(0, 10), 'YYYY-MM-DD')
      return m.isValid() ? m.format('YYYY/MM/DD') : String(d)
    },

    /**
     * 前日（yyyy-mm-dd）
     */
    dayBefore(d) {
      const m = moment(String(d).slice(0, 10), 'YYYY-MM-DD')
      return m.isValid() ? m.subtract(1, 'days').format('YYYY-MM-DD') : ''
    },

    isNg(site, con, row, key) {
      return this.errorKeys.includes(`${site.siteCode}.${con.key}.${row.key}.${key}`)
    },

    /**
     * NGの欄を覚えて、物件を開き、false を返す
     * 契約は契約コードではなく key で覚える（この画面で足した契約コードは振り直すことがあり、振り直しても赤い枠が残るように）
     */
    ng(site, con, row, key) {
      const k = `${site.siteCode}.${con.key}.${row.key}.${key}`
      if (!this.errorKeys.includes(k)) this.errorKeys.push(k)
      this.opened[site.siteCode] = true
      // 終わった行が引っかかったときは見えるようにする
      if (this.isEnded(row)) this.showEnded[site.siteCode] = true
      return false
    },

    toMainTask() {
      const resolvedRoute = this.$router.resolve(this.mainTaskRoute())
      window.open(resolvedRoute.href, '_blank')
    },

    mainTaskRoute() {
      // 新規登録のメインタスクは受注処理（受注の編集は mode 6 で、受注の _id を orderId に渡す）
      if (this.isCreate) {
        return {
          name: 'RequestPost',
          query: {
            mode: 6,
            orderId: this.val.orderId || this.mainId
          }
        }
      }
      return {
        name: 'RequestPost',
        query: {
          mode: 1,
          requestPostId: this.val.orderId || this.mainId
        }
      }
    },

    // ------------------------------------------------------------
    // 保存前の確認
    // ------------------------------------------------------------

    /**
     * 入力内容に矛盾がないか確認する（あればダイアログを出して false）
     * ・変更後の物件コード（得意先変更・物件コード変更で足す行があるとき。申請時は必須。物件マスタに無ければ注意だけ）
     * ・止める文のある契約（申請時）
     * ・行の期間：開始日必須（申請時）、終了日は開始日以降、同じ契約の行が重ならない（画面にある行どうし）
     * ・直した行・足した行の曜日ごとの勤務（旧 validateHours と同じ）と祝日勤務
     * ・変更が1つも無ければ確認
     * @param {Boolean} isApply 申請・登録なら true（一時保存は緩くする）
     */
    async validate(isApply) {
      this.errorKeys = []
      this.validateError = false

      // 新規登録：物件コード（申請時は必須。物件マスタに無ければ注意だけ）と、足した契約コードの数（受注の人数より少なければ止める）
      if (this.isCreate && !await this.validateCreate(isApply)) return false

      for (const site of this.akbs.sites) {
        const hasNewRow = site.contracts.some(c => c.rows.some(r => r.isNew && !r.deleted))
        if (this.newSiteTargets.includes(site) && hasNewRow) {
          if (this.rp.changeClient && site.newSiteName && !site.newSiteCode) {
            await this.errorDialog(`${this.siteName(site)} の変更後の物件コード`, '候補から選択されていません。ドロップダウンから選択してください。')
            return false
          }
          if (isApply && !site.newSiteCode) {
            await this.errorDialog(`${this.siteName(site)} の変更後の物件コード`, '物件コードが変わるため、変更後の物件コードを入れてください。')
            return false
          }
        }

        for (const con of site.contracts) {
          if (con.mode === 'none') continue
          const target = `${this.siteName(site)} 契約コード ${con.cdContract}`

          if (isApply && con.blocked) {
            await this.errorDialog(`${target}`, con.blocked)
            this.opened[site.siteCode] = true
            return false
          }

          const rows = con.rows.filter(r => !r.deleted)
          for (const row of rows) {
            const period = row.startDateContract ? `${this.formatDate(row.startDateContract)}〜` : '開始日未入力'
            if (isApply && !row.startDateContract) {
              await this.errorDialog(`${target} の契約開始日`, '契約開始日が未入力の行があります。')
              return this.ng(site, con, row, 'startDateContract')
            }
            if (row.endDateContract && row.startDateContract && row.endDateContract < row.startDateContract) {
              await this.errorDialog(`${target} の契約終了日`, `${period} の行の契約終了日が契約開始日より前になっています。`)
              return this.ng(site, con, row, 'endDateContract')
            }
            if (isApply && con.mode === 'end' && this.isLatest(con, row) && !row.endDateContract) {
              await this.errorDialog(`${target} の契約終了日`, '契約終了ですが、契約終了日が未入力です。')
              return this.ng(site, con, row, 'endDateContract')
            }
          }

          // 期間の重なり（画面にある行どうし。終わって出していない行との重なりはバックで見る）
          const sorted = sortRows(rows.filter(r => r.startDateContract).slice())
          for (let i = 0; i < sorted.length - 1; i++) {
            const a = sorted[i]
            const b = sorted[i + 1]
            if (!a.endDateContract || a.endDateContract >= b.startDateContract) {
              await this.errorDialog(`${target} の期間が重なっています`, `${this.formatDate(a.startDateContract)}〜${this.formatDate(a.endDateContract) || '（終了日なし）'} の行と ${this.formatDate(b.startDateContract)}〜 の行の期間が重なっています。\n前の行の終了日を次の行の開始日より前にしてください。`)
              this.ng(site, con, b, 'startDateContract')
              return this.ng(site, con, a, 'endDateContract')
            }
          }

          // 直した行・足した行の勤務の確認
          for (const row of rows) {
            if (!(row.isNew || con.mode === 'over') || (!row.isNew && !this.rowChangeCount(row))) continue
            if (!await this.validateRow(site, con, row, isApply)) return false
          }
        }
      }

      // 受注の人数分に足した「勤務が空の契約コード」（受注に勤務の行が無い分）に、まだ勤務が入っていなければ確認（止めはしない）
      // 一時保存・申請・AKBS登録のどれでも出す（帯の赤い札はやめて、ここで知らせる。ユーザー決定 2026-09-23）
      {
        const blanks = []
        for (const site of this.akbs.sites) {
          for (const con of site.contracts) {
            if (this.isBlankUnfilled(con)) {
              blanks.push(`契約コード ${con.cdContract}：${con.orderName}（${con.orderNth}人目の手配が必要と思われます）`)
            }
          }
        }
        if (blanks.length) {
          const dialog = await dialogs.showConfirmDialog('勤務が空の契約コードがあります', `受注の人数に対して、勤務が入っていない契約コードがあります。\n${blanks.map(b => `・${b}`).join('\n')}\n\nこのまま続けますか？`)
          if (dialog !== 'YES') return false
        }
      }

      // 申請・登録時に、書く行（足した行・修正（上書き）の行）で勤務が1日も無いものがあれば確認（止めはしない。ユーザー 2026-09-22）
      // 受注から作った契約コードで作業曜日・時間が未入力のときなどに起きる（上で聞いた「勤務が空の契約コード」は重ねて聞かない）
      if (isApply) {
        const empties = []
        for (const site of this.akbs.sites) {
          for (const con of site.contracts) {
            if (con.mode === 'none' || this.isBlankUnfilled(con)) continue
            for (const row of con.rows) {
              if (row.deleted || !(row.isNew || con.mode === 'over')) continue
              if (WEEK_LIST.every(w => this.isDayOff(row, w))) {
                empties.push(`${this.siteName(site)} 契約コード ${con.cdContract}（${this.formatDate(row.startDateContract) || '開始日未入力'}〜）`)
              }
            }
          }
        }
        if (empties.length) {
          const dialog = await dialogs.showConfirmDialog('勤務が1日も入っていない行があります', `次の行は、どの曜日にも勤務が入っていません。\n${empties.map(e => `・${e}`).join('\n')}\n\nこのまま続けますか？`)
          if (dialog !== 'YES') return false
        }
      }

      // 申請・登録時に、変更が1つも無いとき
      if (isApply) {
        const count = this.tally.over + this.tally.new + this.tally.end + this.tally.add
        if (!count) {
          const dialog = await dialogs.showConfirmDialog('変更がありません', '「変更なし」以外の契約が1つもありません。このまま続けますか？')
          if (dialog !== 'YES') return false
        }
      }
      return true
    },

    /**
     * 新規登録のときの確認
     * ・物件：候補（AKBS の物件マスタ）からしか選べない。申請時は必須で、物件マスタに無い物件コード（前に直接入力して控えたもの）はエラーで止める
     *   （物件を間違えると契約詳細も雇用指示も全部その物件に付くので、申請者が候補から選び、承認者が W チェックする）
     * ・契約コードの数：契約詳細の行数で清掃員のシフトの必要人数が決まるので、
     *   この画面で足した契約コードが受注の人数（見積で決めた人数の合計）より少なければ申請時にエラーで止める（お客様との決まり）。多い分は OK
     *   （同じ人が同じ曜日に2回入る受注は、契約が人数より多くなる）
     * ・手で足した契約コードの作業内容：作業内容が2つ以上あるのに選んでいなければ申請時にエラーで止める
     * ・月の時間・週の回数：作業内容ごとに受注タスクと比べ、許す差を超えていれば（概要の黄色）確認ダイアログ。止めはしない
     * 一時保存（isApply が false）は物件コードの形だけ見る
     */
    async validateCreate(isApply) {
      for (const site of this.akbs.sites) {
        if (isApply && !site.siteCode) {
          await this.errorDialog('物件が選ばれていません', '契約詳細を登録する物件を候補から選んでください。\n候補に無い物件は、ビルメンツに登録されて AKBS に取り込まれると選べるようになります。')
          return false
        }
        // 候補からしか選べないが、前に直接入力して控えた物件コードが残っていることがあるので、物件マスタに有るかを確かめて無ければ止める
        if (isApply && !(await this.siteExists(site.siteCode))) {
          await this.errorDialog('物件マスタにない物件コードです', `物件コード「${site.siteCode}」は、AKBS の物件マスタにありません。候補から選び直してください。`)
          return false
        }
        // 受注タスクの物件名と物件マスタの物件名が違う：確認（受注タスクで物件を選んだあとに物件名を貼り替えたとき）
        if (isApply && this.orderSiteNameDiff(site)) {
          const dialog = await dialogs.showConfirmDialog('受注タスクの物件名と物件マスタの物件名が違います', `受注タスクの物件名は「${this.orderSiteName}」ですが、物件コード ${site.siteCode} の物件マスタの物件名は「${this.siteName(this.createSiteItem(site))}」です。\n受注タスクで物件を選んだあとに物件名を変えていないか確認してください。\n\nこのまま続けますか？`)
          if (dialog !== 'YES') return false
        }
        // 受注タスクの得意先と違う得意先の物件：もう一度確認（申請者の最終確認。承認者は画面の赤字で W チェック）
        if (isApply && this.isClientDiff(site)) {
          const dialog = await dialogs.showConfirmDialog('受注タスクの得意先と違う得意先の物件です', `受注タスクの得意先は「${this.rp.clientName}（${this.rp.clientCode}）」ですが、物件は「${site.clientName}（${site.clientCode}）」の「${site.siteCode} ${this.siteName(site)}」です。\n\nこのまま続けますか？`)
          if (dialog !== 'YES') return false
        }
      }
      // 足した契約コードが受注の人数より少なければ確認（止めはしない。ユーザー決定 2026-09-23。それまではエラーで止めていた）
      if (isApply) {
        const people = this.orderPeople
        if (this.tally.add < people) {
          await this.errorDialog('契約コードの数が受注の人数より少ないです', `受注の人数（見積の人数の合計）は ${people}名 ですが、この画面で足した契約コードは ${this.tally.add}件 です。\n契約詳細の行数で清掃員のシフトの必要人数が決まるので、人数以上の契約コードが必要です。`)
          return false
        }
        // 手で足した契約コードは、どの受注の作業内容の分かを選んでもらう（作業内容ごとに受注タスクと比べるため。作業内容が1つなら選ばなくてよい）
        if (this.unassignedContracts.length) {
          await this.errorDialog('受注の作業内容が選ばれていない契約コードがあります', `次の契約コードは、受注のどの作業内容の分かが選ばれていません。契約コードの帯で選んでください。\n${this.unassignedContracts.map(c => `・契約コード ${c.cdContract}`).join('\n')}`)
          return false
        }
        // ラウンドプラスの固定清掃の祝日勤務を初期値の「FULL」から変えていれば確認（止めはしない。申請者も承認者も）
        const holidayChanged = this.holidayChangedRows()
        if (holidayChanged.length) {
          const dialog = await dialogs.showConfirmDialog('固定清掃の祝日勤務を変えています', `ラウンドプラスの固定清掃は、祝日勤務「FULL」が初期値です。\n次の契約コードは祝日勤務を変えています。\n\n${holidayChanged.join('\n')}\n\nこのままでいいですか？`)
          if (dialog !== 'YES') return false
        }
        // 作業内容ごとに、月の時間か週の回数が受注タスクと許す差（整数 1%・.5 は 5%）以上違えば確認（止めはしない。概要の表の判定と同じ）
        // 「はい」で進むときは必ず理由を書いてもらい、メモに残す（承認者に伝わる）
        // 明らかに違う（月の時間 10% 超・週の回数 1日以上）はエラーで止める（受注タスク自体が違うなら受注タスクを直してから）
        const fars = this.orderItems.filter(item => this.itemFar(item))
        if (fars.length) {
          const lines = fars.map(item => `・（受注${item.no}）${orderItemName(item)}  ${this.judgeText(item)}`)
          await this.errorDialog('受注タスクと明らかに違います', `次の作業内容は、契約詳細の月の時間か週の回数が受注タスクと明らかに違うので申請できません。\n契約詳細を受注タスクに合わせてください。受注タスク自体が違うなら、受注タスクを直してからもう一度申請してください。\n\n${lines.join('\n')}`)
          return false
        }
        const mismatches = this.orderItems.filter(item => this.itemMismatch(item))
        if (mismatches.length) {
          const lines = mismatches.map(item => `・（受注${item.no}）${orderItemName(item)}  ${this.judgeText(item)}`)
          // 承認（AKBSへ登録）のときは理由を書かせない。申請者が書いた理由はメモにあり、承認者はそれを読んで判断する（ユーザー指示 2026-10-09）
          const reasonNow = this.diffReasonFromMemo()
          const body = this.isApprover
            ? `次の作業内容は、契約詳細の月の時間か週の回数が受注タスクと違います。\n\n${lines.join('\n')}\n\n${reasonNow ? `【申請者の理由】\n${reasonNow}` : '申請者の理由はメモにありません。'}\n\nこのまま登録しますか？`
            : `次の作業内容は、契約詳細の月の時間か週の回数が受注タスクと違います。\n\n${lines.join('\n')}\n\nこのまま続けますか？`
          const dialog = await dialogs.showConfirmDialog('月の時間・週の回数が受注タスクと違います', body)
          if (dialog !== 'YES') return false
          if (!this.isApprover) {
            const reason = await this.askDiffReason(lines.join('\n'))
            if (!reason) return false
            this.setDiffReason(reason)
          }
        } else if (!this.isApprover) {
          // 違いが無ければ、前に書いた理由は消す（承認者が無い違いの理由を読まないように）。承認者はメモを触らない
          this.clearDiffReason()
        }
      }
      return true
    },

    /**
     * 行1件の曜日ごとの勤務と祝日勤務の確認（旧画面の validateHours と同じ）
     */
    async validateRow(site, con, row, isApply) {
      const target = `${this.siteName(site)} 契約コード ${con.cdContract}（${this.formatDate(row.startDateContract) || '開始日未入力'}〜）`
      for (const w of WEEK_LIST) {
        const st = row['start' + w.id] || ''
        const en = row['end' + w.id] || ''
        const wh = Number(row['workingHours' + w.id]) || 0
        const at = row['attendance' + w.id] || []
        const day = `${target} の ${w.name}曜`

        if (isApply && at.length && (!st || !en)) {
          await this.errorDialog(`${day} 勤務時間帯`, '勤務形態が入っていますが、開始・終了時間が未入力です。')
          return this.ng(site, con, row, (st ? 'end' : 'start') + w.id)
        }
        if ((st && !en) || (!st && en)) {
          await this.errorDialog(`${day} 勤務時間帯`, '勤務時間が未完成です。開始と終了の両方を入力してください。')
          return this.ng(site, con, row, (st ? 'end' : 'start') + w.id)
        }
        if (st && en && utils.getHours(st, en) <= 0) {
          await this.errorDialog(`${day} 勤務時間帯`, '開始と終了の時間が逆、もしくは同じです。')
          return this.ng(site, con, row, 'end' + w.id)
        }
        if (st && en && !wh) {
          await this.errorDialog(`${day} 実働時間`, '開始・終了時間が入っていますが、実働時間が未入力です。')
          return this.ng(site, con, row, 'workingHours' + w.id)
        }
        if (wh > 0 && !st) {
          await this.errorDialog(`${day} 勤務時間帯`, '実働時間が入っていますが、勤務時間（開始・終了）が未入力です。')
          return this.ng(site, con, row, 'start' + w.id)
        }
        if (wh > 0) {
          const h = utils.getHours(st, en)
          if (wh > h) {
            await this.errorDialog(`${day} 実働時間が多い`, `開始～終了時間の拘束時間よりも実働時間が多くなっています。\n${st}～${en}➡${h}時間拘束`)
            return this.ng(site, con, row, 'workingHours' + w.id)
          }
          if (h >= 6 && h - wh < 0.75) {
            await this.errorDialog(`${day} 実働時間`, `開始～終了時間で6時間以上拘束しています。\n45分以上の休憩を考慮し、実働時間を入力してください。\n（${h - 0.75}h 以下でないと登録できません）`)
            return this.ng(site, con, row, 'workingHours' + w.id)
          }
        }
      }

      if (isApply && this.holidayKind(row) === 'hours' && !Number(row.kubunHolidayContract)) {
        await this.errorDialog(`${target} の祝日勤務`, '「時間入力」ですが、時間が入っていません。時間を入れるか、FULL・無を選んでください。')
        return this.ng(site, con, row, 'kubunHolidayContract')
      }

      // クリーンプラス金額は 0 以上の整数（DB の列が integer。小数・マイナスは登録で落ちるので一時保存でも止める）
      const cleanPlus = row.cleanPlus
      if (cleanPlus !== '' && cleanPlus !== null && cleanPlus !== undefined && !/^\d+$/.test(String(cleanPlus).trim())) {
        await this.errorDialog(`${target} のクリーンプラス金額`, 'クリーンプラス金額は 0 以上の整数で入力してください。')
        return this.ng(site, con, row, 'cleanPlus')
      }

      // 必須の欄（作業内容・ゴミ搬出・全社休日出勤）。申請・登録のときだけ止める（画面では空の欄を薄い赤にしている。isRequiredEmpty）
      if (isApply) {
        const required = [
          { key: 'cdContentsWork', name: '作業内容', message: '作業内容を選んでください。' },
          { key: 'hasCarryingoutGarbageContract', name: 'ゴミ搬出', message: 'ゴミ搬出の有り・無しを選んでください。' },
          { key: 'hasExportingCompanysHolidayContract', name: '全社休日出勤', message: '全社休日出勤の有り・無しを選んでください。' }
        ]
        for (const item of required) {
          if (this.isRequiredEmpty(row, item.key)) {
            await this.errorDialog(`${target} の${item.name}`, item.message)
            return this.ng(site, con, row, item.key)
          }
        }
      }
      return true
    },

    /**
     * エラーのダイアログ（入力チェックで止めたことを控える。register で承認者への一言を足すのに使う）
     */
    errorDialog(title, message) {
      this.validateError = true
      return dialogs.showErrorDialog(title, message)
    },

    /**
     * 承認者が入力チェックのエラーで止まったとき：この画面では直せないので、差戻してもらうよう伝える
     * 確認ダイアログをキャンセルしただけのときは出さない（validateError で区別）
     */
    async approverCannotFix() {
      if (!this.isApprover || !this.validateError) return
      await dialogs.showInfoDialog('承認者はこの画面で直せません', '申請のあとに受注タスクや AKBS の内容が変わって、申請どおりに登録できなくなっています。\n差戻して、申請者に直してもらってください。')
    },

    /**
     * 物件コードが AKBS の物件マスタにあるか
     * API: akbsTable/getSiteListForEstimate（物件コードで絞る）
     */
    async siteExists(siteCode) {
      try {
        const res = await backend.searchData('akbsTable/getSiteListForEstimate', { siteCode })
        return ((res.data && res.data.data) || []).length > 0
      } catch (e) {
        console.log(e)
        return true
      }
    },

    // ------------------------------------------------------------
    // 保存・申請・承認
    // ------------------------------------------------------------

    /**
     * 保存・登録でバックへ渡す申請の内容を作る
     */
    toSaveData() {
      const val = { ...this.val, akbs: this.toDbAkbs() }
      val.updateUser = this.$store.getters.user.id
      // 変更処理のメインタスクの問い合わせNo（getData で受け取ったもの。申請一覧に出すために控える）
      val.requestNo = this.rp.requestNo || val.requestNo || ''
      val.changeDate = this.rp.changeDate || null
      // 新規登録には変更日が無いので、足した契約コードの契約開始日のうち一番早い日を控える（申請一覧の日付の列に出す）
      if (this.isCreate) {
        const starts = []
        for (const site of this.akbs.sites) {
          for (const con of site.contracts) {
            if (!con.isNewContract) continue
            for (const row of con.rows) {
              if (!row.deleted && row.startDateContract) starts.push(row.startDateContract)
            }
          }
        }
        val.changeDate = starts.sort()[0] || null
      }
      val.clientCode = this.rp.clientCode || ''
      val.clientName = this.rp.clientName || ''
      // 一覧用の対象物件名（5件まで。それ以上は他○件）
      const names = this.akbs.sites.map(s => this.siteName(s))
      val.siteNames = names.length > 5 ? names.slice(0, 5).join('、') + ' 他' + (names.length - 5) + '件' : names.join('、')
      return val
    },

    /**
     * 画面の内容をDBに入れる形にした複製（勤務形態はコードの配列に戻す。original も）
     */
    toDbAkbs() {
      const akbs = utils.clone(this.akbs)
      const toCodes = list => (list || []).map(a => (a && a.id !== undefined ? a.id : a))
      for (const site of akbs.sites) {
        for (const con of site.contracts) {
          for (const row of con.rows) {
            for (const w of WEEK_LIST) {
              row['attendance' + w.id] = toCodes(row['attendance' + w.id])
              if (row.original) {
                row.original['attendance' + w.id] = toCodes(row.original['attendance' + w.id])
              }
            }
          }
          for (const st of con.staffs) {
            for (const w of WEEK_LIST) {
              st['attendance' + w.id] = toCodes(st['attendance' + w.id])
            }
          }
        }
      }
      return akbs
    },

    /**
     * 一時保存・申請
     */
    async save(isApply) {
      // 見ているだけのつもりで押してしまった人を止める（サブタスク「契約詳細登録申請」の部署の人でなければ確かめる）
      if (!await this.confirmDepartment(AKBS_SUB_TASK.CONTRACT, isApply ? '申請' : '一時保存')) return

      if (!await this.validate(isApply)) return

      // 契約終了の契約に清掃員がいるのに雇い入れタスクが未作成なら、確認して一緒に作る（キャンセルなら保存もしない）
      const must = this.empRows.filter(r => r.state === 'todo' && r.must)
      if (must.length) {
        if (!await this.validateEmpEdits(must)) return
        const dialog = await dialogs.showConfirmDialog(
          '雇い入れタスクを登録します',
          `契約終了する場合は清掃員のシフトに影響が出るので、雇い入れタスクを登録します。\n\n${must.map(r => '・' + r.staffName).join('\n')}\n\nタスク依頼部署・タスク担当者・タスク期限は、表に入っている内容で登録します。`,
          isApply ? '登録して申請する' : '登録して一時保存する'
        )
        if (dialog !== 'YES') return
      }

      this.loading = true
      try {
        const val = await this.postSave(isApply)
        if (!val) return

        // 作成必須の雇い入れタスクを作る（保存は済んでいるので、失敗しても保存は取り消さない）
        if (must.length) {
          const made = await this.registerEmpTasks(must, val.id)
          // 保存のときの記載は、まだ申請が無かったこの人たちには入っていないので、できた申請へここで記載する
          await this.syncEmpNotes(val.id)
          if (made.failed.length) {
            await this.errorDialog('雇い入れタスクの登録エラー', `次の清掃員の雇い入れタスクを登録できませんでした。表からもう一度作ってください。\n\n${made.failed.map(r => '・' + r.staffName).join('\n')}`)
          }
        }

        if (isApply) {
          const next = await dialogs.showMesssageDialog('申請しました', '承認をお待ちください。\n承認は「契約詳細権限」を持っている人なら誰でもできます。ただし、作成者本人は承認できません。\n\n遷移先を選択してください。', 'success', [
            { kind: 'normal', text: 'このまま', result: 'stay' },
            { kind: 'primary', text: '申請一覧へ', result: 'list' },
            { kind: 'secondary', text: 'メインタスクへ', result: 'main' },
          ])
          if (next === 'list') {
            this.toList()
            return
          }
          if (next === 'main') {
            this.$router.push(this.mainTaskRoute())
            return
          }
        } else {
          await dialogs.showSuccessDialog('一時保存しました', '続きはこの画面で入力できます。')
        }

        await this.getData()
        this.checkSaved(val.akbs)
      } catch (e) {
        console.log(e)
        await this.errorDialog('保存エラー', '保存に失敗しました。もう一度お試しください。')
      } finally {
        this.loading = false
      }
    },

    /**
     * 動作確認用（確認が終わったら削除する。あわせて flatten と save の呼び出しも消す）
     * 保存に送った akbs と、保存後に取り直した画面の akbs をコンソールに出し、同じかも突き合わせる
     * 保存 → getData の往復で値が変わっていないか（DBに正しく入り、正しく読めているか）を確かめる
     * 開発環境でだけ動く。両方の値をそのまま出したあと、違いがあれば項目ごとに console.table で出す
     * 開き直しで AKBS の今の値に差し替わった欄（最新化）や、バックが付けるものは違いとして出るので、それ以外に違いが無いことを見る
     * @param {Object} sent 保存に送った akbs（toSaveData で作ったもの）
     */
    checkSaved(sent) {
      if (process.env.NODE_ENV === 'production') {
        return
      }

      // JSON を通して undefined のキーを落とし、DBから返る形に揃える
      const sentAkbs = JSON.parse(JSON.stringify(sent))
      const loadedAkbs = JSON.parse(JSON.stringify(this.toDbAkbs()))

      // まず値そのものを出す（コンソールで開いて中身を見られるように、リアクティブでない複製を渡す）
      console.log('[動作確認] DBに保存する前の akbs', sentAkbs)
      console.log('[動作確認] DBに保存したものを取得した akbs', loadedAkbs)

      const before = flatten(sentAkbs)
      const after = flatten(loadedAkbs)

      const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort()
      const diffs = []
      for (const key of keys) {
        if (before[key] !== after[key]) {
          diffs.push({ 項目: key, 保存前: before[key], 保存後: after[key] })
        }
      }

      if (diffs.length) {
        console.log(`[動作確認] 保存前後で ${diffs.length} 項目に違いがあります`)
        console.table(diffs)
      } else {
        console.log(`[動作確認] 保存前後で akbs は一致しました（${keys.length} 項目）`)
      }
    },

    /**
     * バックへ一時保存・申請を依頼する（ダイアログは出さない）
     * API: akbsContractForm/save
     * @param {Boolean} isApply 申請なら true
     * @returns {Promise<Object|null>} 保存した内容（id 入り）。拒否されたら null
     */
    async postSave(isApply) {
      this.markChanged()

      const val = this.toSaveData()
      val.status = isApply ? AKBS_STATUS.PENDING : AKBS_STATUS.SAVED
      val.applicationDate = isApply ? utils.getToday() : (val.applicationDate || null)
      val.creator = this.user
      val.creatorId = this.$store.getters.user.id

      // バックは保存のあと、契約終了の清掃員の申請と、この画面から作った雇い入れタスクの申請へ、今回の変更内容を記載する
      const res = await backend.postData('akbsContractForm/save', { val, expectedStatus: [AKBS_STATUS.SAVED, AKBS_STATUS.RETURNED, AKBS_STATUS.DELETED] })
      if (await this.isRefused(res)) return null

      // 初めての保存ならここで id が付く（雇い入れメインタスクに控えるキーに使う）
      const data = res.data && res.data.data
      val.id = val.id || (data && data.id) || null
      this.val.id = val.id
      return val
    },

    // ------------------------------------------------------------
    // 清掃員の雇い入れタスク
    // ------------------------------------------------------------

    /**
     * 契約の表より上の部分（申請の概要・影響される清掃員など）の高さを見張る
     * 下の契約で変更の方法を押すと、上にある「影響される清掃員」の枠が出たり行が増えたりして、押した契約が下へ逃げて見えなくなる
     * 契約の表の側を触っているときは、上が伸び縮みした分だけスクロールして、触っているところの位置を保つ
     * （上の部分の中を触っているときは動かさない。ResizeObserver は描画の前に呼ばれるので、ちらつかない）
     */
    watchTopHeight() {
      const wrap = this.$refs.topWrap
      const form = this.$refs.form
      if (!wrap || !form || typeof ResizeObserver === 'undefined') return

      // 最後に押したところが上の部分より下か（押したボタンは描画し直しで消えることがあるので、押した時点で控える）
      this.clickedBelowTop = false
      this.topClickHandler = e => {
        this.clickedBelowTop = !wrap.contains(e.target)
      }
      form.addEventListener('click', this.topClickHandler, true)

      let height = wrap.offsetHeight
      this.topObserver = new ResizeObserver(() => {
        const next = wrap.offsetHeight
        const diff = next - height
        height = next
        if (diff && this.clickedBelowTop) {
          form.scrollTop += diff
        }
      })
      this.topObserver.observe(wrap)
    },

    /**
     * 見張りを外す
     */
    unwatchTopHeight() {
      if (this.topObserver) {
        this.topObserver.disconnect()
        this.topObserver = null
      }
      if (this.topClickHandler && this.$refs.form) {
        this.$refs.form.removeEventListener('click', this.topClickHandler, true)
      }
    },

    /**
     * 影響される清掃員に、AKBS登録前の雇い入れ（清掃員の申請）が既にあるかを取る
     * API: akbsContractForm/getEmploymentTasks
     * 清掃員の申請（req_akbs）を清掃員コードで引くだけ。メインタスクは見ない（この画面から作った分も、登録時にできる空の枠で当たる）
     */
    async loadEmpTasks() {
      const staffCodes = this.empCandidates.map(c => c.staffCode)
      if (!staffCodes.length) {
        this.empTasks = []
        return
      }
      try {
        const res = await backend.postData('akbsContractForm/getEmploymentTasks', { id: this.val.id, staffCodes })
        const data = res.data && res.data.data
        this.empTasks = Array.isArray(data) ? data : []
      } catch (e) {
        console.log(e)
      }
    },

    /**
     * 未作成の行の入力を、対象の清掃員に揃える
     * 初期値：担当部署は勤怠採用課、担当者は「未定」、期限は翌日。作成必須の人はチェック済み（外せない）、それ以外はチェック無し
     * 種別は、手で変えていなければ初期値（契約の変更の方法から決まる）に合わせ直す
     * キーを後から足すのでオブジェクトごと入れ替える
     */
    syncEmpEdits() {
      const edits = {}
      for (const c of this.empCandidates) {
        const e = this.empEdits[c.staffCode] || this.defaultEmpEdit()
        if (c.must) e.checked = true
        if (!e.kindTouched) e.kind = c.kind
        edits[c.staffCode] = e
      }
      this.empEdits = edits
    },

    /**
     * 未作成の行の入力の初期値
     */
    defaultEmpEdit() {
      const department = this.departmentList.find(d => {
        const name = d.name || ''
        return name.includes(TASK_DEPARTMENT) && !name.includes(TASK_DEPARTMENT_EXCLUDE)
      }) || null
      const edit = { checked: false, kind: 'change', kindTouched: false, department, staff: null, limitDate: utils.addDays(utils.getToday(), 1) }
      // 担当者「未定」がその部署にいれば選んでおく。いなければ空（登録時に名前だけ「未定」にする）
      edit.staff = this.empStaffList(edit).find(u => u.userName === TASK_STAFF) || null
      return edit
    },

    /**
     * 担当者の候補（選んだ部署の人だけ）
     */
    empStaffList(edit) {
      if (!edit || !edit.department) return []
      return (this.$store.getters.userDepartment || []).filter(u => u.departmentId == edit.department.departmentId)
    },

    /**
     * 部署を選び直した（選んでいた担当者がその部署にいなければ「未定」に戻す）
     */
    empDepartmentSelected(edit) {
      const list = this.empStaffList(edit)
      if (!edit.staff || !list.some(u => u.userId == edit.staff.userId)) {
        edit.staff = list.find(u => u.userName === TASK_STAFF) || null
      }
    },

    /**
     * 未作成の行をこの画面で直せるか
     * 雇い入れタスクはこの画面の保存とは独立していて、承認待ちの間も作れる（AKBS登録済みは完了した申請なので作れない）
     */
    canEditEmp(r) {
      return r.state === 'todo' && !this.isRegistered && !!r.edit
    },

    /**
     * 既にタスクありの行の案内文
     * AKBS登録済み（完了した申請）で開いたときだけ、その清掃員の申請に記載してあるかを出す
     */
    empNoteText(r) {
      if (!this.isRegistered) return ''
      return r.task && r.task.notified ? 'この申請画面に、契約詳細の変更を知らせています。' : 'この申請画面には、何も知らせていません。'
    },

    /**
     * 作ったばかりの雇い入れタスクの申請へ、「契約詳細の変更内容」（req_akbs.contract_notes。見るだけのメモ）を書いてもらう
     * API: akbsContractForm/syncEmploymentNotes
     * 書く内容は、バックが保存済みの申請から作る（画面の保存していない入力は書かない）
     * 書く相手はバックが決める（契約終了の清掃員の申請と、この申請から作った雇い入れの申請。後者は req_akbs.contract_form_id で見分ける）
     * 失敗しても、この画面の次の保存でまた書かれる
     * @param {Number} formId この申請の id
     */
    async syncEmpNotes(formId) {
      try {
        await backend.postData('akbsContractForm/syncEmploymentNotes', { id: formId })
      } catch (e) {
        console.log(e)
      }
    },

    /**
     * 作る行の入力を確かめる（担当部署・期限）
     */
    async validateEmpEdits(rows) {
      for (const r of rows) {
        if (!r.edit || !r.edit.department) {
          await this.errorDialog(`${r.staffName} さんの雇い入れタスク`, 'タスク依頼部署を選択してください。')
          return false
        }
        if (!r.edit.limitDate) {
          await this.errorDialog(`${r.staffName} さんの雇い入れタスク`, 'タスク期限を入力してください。')
          return false
        }
      }
      return true
    },

    /**
     * 表の下のボタン：未作成で✓の清掃員の雇い入れタスクを作る
     * この画面の保存とは独立している。申請の id さえあれば（一度でも保存していれば）、承認待ちの間も使える
     * 未保存のときは先に保存してもらう（ここでは保存しない。保存は右上の「一時保存」「申請」で行う）
     */
    async createEmpTasks() {
      let rows = this.empRows.filter(r => r.state === 'todo' && r.edit && r.edit.checked)
      if (!rows.length) return
      if (!this.val.id) {
        await dialogs.showWarningDialog('先にこの画面を保存してください', '右上の「一時保存」を押して、まずはこの画面を保存してください。\n保存したあとで、雇い入れタスクを作れます。')
        return
      }
      if (!await this.validateEmpEdits(rows)) return

      this.loading = true
      try {
        // 画面を開いたあとで、同じ清掃員の雇い入れがほかで作られているかもしれないので、作る直前に取り直す
        // （同じ清掃員の申請は AKBS登録済みになるまで1つにまとめる。あれば作らず、その申請を使ってもらう）
        await this.loadEmpTasks()
        const skipped = rows.filter(r => !this.empRows.some(x => x.staffCode === r.staffCode && x.state === 'todo'))
        rows = rows.filter(r => !skipped.includes(r))
        if (skipped.length) {
          await dialogs.showWarningDialog('既に雇い入れがあります', `次の清掃員は、既に雇い入れタスクがあるので作りません。表の「開く」から、その申請画面を確認してください。\n\n${skipped.map(r => '・' + r.staffName).join('\n')}`)
        }

        const made = await this.registerEmpTasks(rows, this.val.id)

        // 作ったばかりの申請へ、今回の変更内容を記載する
        if (made.done.length) await this.syncEmpNotes(this.val.id)

        if (made.failed.length) {
          await this.errorDialog('雇い入れタスクの登録エラー', `次の清掃員の雇い入れタスクを登録できませんでした。もう一度お試しください。\n\n${made.failed.map(r => '・' + r.staffName).join('\n')}`)
        } else if (made.done.length) {
          await dialogs.showSuccessDialog('雇い入れタスクを作りました', `${made.done.map(d => `・${d.staffName}（No.${d.requestNo}）`).join('\n')}`)
        }
        // 画面の入力は保存していないので、画面ごと取り直さない（入力が消えるため）。表だけ取り直す
        await this.loadEmpTasks()
      } catch (e) {
        console.log(e)
        await this.errorDialog('登録エラー', '雇い入れタスクの登録に失敗しました。もう一度お試しください。')
      } finally {
        this.loading = false
      }
    },

    /**
     * 雇い入れメインタスク（＋サブタスク）を登録する（登録のときに、清掃員の申請の空の枠もできる）
     * 登録はフロント（requestPostManager.registerEmploymentFromContract）
     * できた申請への「契約詳細の変更内容」の記載は、呼ぶ側がこのあと syncEmpNotes で行う
     * @param {Array} rows 作る行（empRows の要素）
     * @param {Number} formId この申請の id
     * @returns {Promise<{ done: Array, failed: Array }>}
     */
    async registerEmpTasks(rows, formId) {
      const done = []
      const failed = []
      for (const r of rows) {
        const res = await requestPostManager.registerEmploymentFromContract(this, {
          formId,
          staffCode: r.staffCode,
          staffName: r.staffName,
          kind: r.edit.kind,
          department: r.edit.department,
          staff: r.edit.staff,
          limitDate: r.edit.limitDate
        })
        if (res) {
          done.push({ staffCode: r.staffCode, staffName: r.staffName, requestNo: res.requestNo })
        } else {
          failed.push(r)
        }
      }

      return { done, failed }
    },

    /**
     * 雇い入れの申請画面（AkbsInputForm）を別タブで開く
     * 清掃員の申請（req_akbs。空の枠を含む）の id で開く
     */
    openEmpForm(task) {
      const resolvedRoute = this.$router.resolve({ name: 'AkbsInputForm', query: { id: task.reqId } })
      window.open(resolvedRoute.href, '_blank')
    },

    /**
     * ログインユーザーの主な部署が、そのサブタスクの部署と違えば、このまま進めてよいか確かめる（清掃員の申請画面 AkbsInputForm と同じ）
     * この画面はサブタスクを持っていないので、メインタスクのサブタスクを取りに行く（受注処理は受注のサブタスク、変更処理は通常のサブタスク）
     * 該当のサブタスクが無い・部署が空・取れなかったときは確かめない
     * @param {String} taskName AKBS_SUB_TASK.CONTRACT（一時保存・申請）か CONTRACT_APPROVE（AKBSへ登録）
     * @param {String} action ダイアログに出す操作の言葉（一時保存・申請・AKBSへ登録）
     * @returns {Boolean} 進めてよければ true
     */
    async confirmDepartment(taskName, action) {
      const mainId = this.val.orderId || this.mainId
      if (!mainId) return true

      let tasks = []
      try {
        tasks = (this.rp.type === 'order'
          ? await taskRequestManager.getByOrderId(this, mainId)
          : await taskRequestManager.getByRequestPostId(this, mainId)) || []
      } catch (e) {
        console.log(e)
        return true
      }
      const task = tasks.find(t => t && t.status !== 'remove' && isAkbsSubTask(t.requestTask, taskName))
      if (!task || (!task.departmentId && !task.departmentName)) {
        return true
      }
      const me = this.$store.getters.loginUserData || {}
      // ID がそろっていれば ID で、無ければ部署名で比べる（建物管理部と勤怠採用課は同じ扱い）
      if (isSameDepartmentForAkbs(task, me)) {
        return true
      }
      const dialog = await dialogs.showWarningConfirmDialog(
        `${action}してよいですか？`,
        `サブタスク「${task.requestTask}」の部署は「${task.departmentName || '未設定'}」です。\nあなたの部署（${me.departmentName || '不明'}）と違います。\n\nこのまま${action}しますか？`,
        `${action}する`
      )
      return dialog === 'YES'
    },

    /**
     * AKBSへ登録（承認）
     * 雇用指示はこの画面では書かない。清掃員の雇い入れタスクは、作成者が承認の前に上の表から作っている
     */
    async register() {
      // 見ているだけのつもりで押してしまった人を止める（サブタスク「契約詳細承認」の部署の人でなければ確かめる）
      if (!await this.confirmDepartment(AKBS_SUB_TASK.CONTRACT_APPROVE, 'AKBSへ登録')) return

      if (!await this.validate(true)) {
        // 承認者の画面は閲覧のみなので、エラーで止まっても直せない（申請のあとに受注タスクや AKBS が変わったとき）。差戻してもらう
        await this.approverCannotFix()
        return
      }

      const dialog = await dialogs.showConfirmDialog('AKBSへ登録します', '変更した内容を本番のAKBS（契約詳細情報）へ書き込みます。よろしいですか？', '登録する')
      if (dialog !== 'YES') return

      await this.registerAKBS()
    },

    /**
     * バックへ登録を依頼
     * API: akbsContractForm/saveAKBS
     */
    async registerAKBS() {
      this.loading = true
      try {
        this.markChanged()

        const val = this.toSaveData()
        val.status = AKBS_STATUS.REGISTERED
        val.approver = this.user
        val.approverId = this.$store.getters.user.id

        const res = await backend.postData('akbsContractForm/saveAKBS', { val, expectedStatus: [AKBS_STATUS.PENDING], expectedUpdateDate: this.val.updateDate })
        if (await this.isRefused(res)) return

        // サブタスク「契約詳細承認」はバックが完了にしている。それが最終完了タスクなら、メインタスクを完了にする
        // （未完了のサブタスクが残っていれば、最終完了タスクをそちらへ付け替える）
        const main = { _id: val.orderId || this.mainId, type: this.rp.type, requestNo: this.rp.requestNo, estimateNo: this.rp.estimateNo }
        const finished = await finishAfterAkbs(this, main, AKBS_SUB_TASK.CONTRACT_APPROVE)

        // 足した契約コード（暫定）はバックが本番の続きで付け直す。画面の契約コードから変わったものがあれば知らせる
        const renumbered = (res.data && res.data.data && res.data.data.renumbered) || []
        const renumberedText = renumbered.length
          ? '\n\n新規追加した契約コードは、本番の契約コードの続きで付け直しました。\n' + renumbered.map(r => `・${r.siteName}：契約コード ${r.from} → ${r.to}`).join('\n')
          : ''

        await dialogs.showSuccessDialog('AKBSへ登録しました', '変更した内容を本番のAKBSへ書き込みました。' + renumberedText + this.finishedText(finished))
        this.toList()
      } catch (e) {
        console.log(e)
        await this.errorDialog('登録エラー', 'AKBSへの登録に失敗しました。もう一度お試しください。')
        await this.getData()
      } finally {
        this.loading = false
      }
    },

    /**
     * AKBS へ登録したあとの、メインタスクの片付けの結果を知らせる文（何もしていなければ空）
     */
    finishedText(finished) {
      if (finished.mainDone) {
        return '\n\nサブタスクが全て完了したので、メインタスクも完了しました。'
      }
      if (finished.movedTo) {
        return `\n\n未完了のサブタスクが残っているので、「最終完了タスク」を「${finished.movedTo}」へ付け替えました。`
      }
      return ''
    },

    async sendBack() {
      const reason = (this.newReturnReason || '').trim()
      const dialog = await dialogs.showWarningConfirmDialog(
        reason ? 'この申請を差し戻します' : '差戻理由が記入されていません',
        reason ? '差し戻してよろしいでしょうか？' : '理由が記入されていませんがよろしいでしょうか？',
        '差し戻す'
      )
      if (dialog !== 'YES') return

      const entry = `${reason || '＊＊理由記載なし＊＊'}\n${this.user} ${moment().format('YYYY/MM/DD HH:mm')}`
      const returnReason = this.val.returnReason ? `${entry}\n\n${this.val.returnReason}` : entry

      if (await this.changeStatus(AKBS_STATUS.RETURNED, [AKBS_STATUS.PENDING], '差し戻しました', 'この申請は差戻になりました。', '差戻エラー', '差戻に失敗しました。もう一度お試しください。', { returnReason })) {
        this.toList()
      }
    },

    async cancelApply() {
      const dialog = await dialogs.showConfirmDialog('申請を取り消します', '一時保存に戻して、内容を直せるようにします。\n直したあとは、もう一度「申請」を押してください。\n\nよろしいですか？', '取り消す')
      if (dialog !== 'YES') return

      if (await this.changeStatus(AKBS_STATUS.SAVED, [AKBS_STATUS.PENDING], '申請を取り消しました', '一時保存に戻りました。内容を直したら、もう一度「申請」を押してください。', '取消エラー', '申請の取消に失敗しました。もう一度お試しください。')) {
        await this.getData()
      }
    },

    /**
     * 申請を削除する（ユーザー決定 2026-09-23。清掃員の申請画面と同じ）
     * 保存していた内容を消して状況を「削除」にし、一覧へ戻る。誰でも押せる（誰が消したかは DB に残る）
     * 復活は、メインタスクかサブタスクの「契約詳細を登録／変更」で開き直すだけ（未保存と同じ状態から始まる。メインタスクが完了していれば復活できない）
     * サブタスク「契約詳細登録申請」「契約詳細承認」は未完了に戻るので、メインタスクを片付けるときはサブタスク画面でそれらを削除してもらう
     * API: akbsContractForm/deleteRequest
     */
    async deleteRequest() {
      const dialog = await dialogs.showWarningConfirmDialog(
        'この申請を削除します',
        '入力した内容は消えます（元に戻せません）。\nもう一度この契約詳細の申請が要るときは、メインタスクかサブタスクの「契約詳細を登録／変更」から開き直してください。\n\nメインタスクを完了にする場合は、サブタスク「契約詳細登録申請」「契約詳細承認」をサブタスク画面で削除してください。\n\n削除してよろしいですか？',
        '削除する'
      )
      if (dialog !== 'YES') return

      this.loading = true
      try {
        const res = await backend.postData('akbsContractForm/deleteRequest', { id: this.val.id, updateUser: this.$store.getters.user.id })
        if (await this.isRefused(res)) return
        await dialogs.showSuccessDialog('申請を削除しました', '申請一覧へ戻ります。')
        this.toList()
      } catch (e) {
        console.log(e)
        await this.errorDialog('削除エラー', '申請の削除に失敗しました。もう一度お試しください。')
        await this.getData()
      } finally {
        this.loading = false
      }
    },

    /**
     * 状況だけを変える（差戻・申請取消）
     */
    async changeStatus(status, expectedStatus, okTitle, okMessage, ngTitle, ngMessage, extra) {
      this.loading = true
      try {
        const res = await backend.postData('akbsContractForm/changeStatus', { id: this.val.id, status, expectedStatus, updateUser: this.$store.getters.user.id, ...extra })
        if (await this.isRefused(res)) return false
        // 差戻のときはバックが作成者へチャットを送る。送れなかったときは理由を添えて、直接連絡してもらう
        await dialogs.showSuccessDialog(okTitle, okMessage + this.chatResultText(res.data && res.data.data && res.data.data.chat))
        return true
      } catch (e) {
        console.log(e)
        await this.errorDialog(ngTitle, ngMessage)
        await this.getData()
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * 差戻のチャット通知の結果を、成功ダイアログに添える文（通知していないときは空。清掃員の申請画面と同じ）
     * @param {Object} chat バックが返した { sent, reason }
     */
    chatResultText(chat) {
      if (!chat) return ''
      return chat.sent ? `\n\n作成者（${this.val.creator || ''}）へチャットで知らせました。` : `\n\n作成者へのチャットは送れませんでした（${chat.reason}）。\n直接連絡してください。`
    },

    toList() {
      this.$router.push({ name: 'AkbsPendingList' })
    },

    selectList(menuItem) {
      if (menuItem.value === 'akbs') {
        this.toList()
        return
      }
      const name = menuItem.value === 'main' ? 'RequestPostList' : 'RequestPostProgressList'
      this.$router.push({ name })
    },

    /**
     * バックが何もしなかったか（状況違い・古い内容・期間の重なり）。そのときは知らせて、必要なら画面を取り直す
     */
    async isRefused(res) {
      const data = res && res.data && res.data.data
      if (!data || !data.refused) return false

      if (data.reason === 'overlap') {
        await this.errorDialog('期間が重なっています', `本番のAKBSにある行（終了した行を含む）と期間が重なるため、登録できません。\n「終了した行も表示」で確かめて、修正（上書き）で直してください。\n${(data.messages || []).map(m => `・${m}`).join('\n')}`)
        return true
      }

      if (data.reason === 'updated') {
        const user = (this.$store.getters.userList || []).find(u => u.id == data.updateUser)
        const name = user ? utils.deleteKana(user.user_name) : '誰か'
        const when = data.updateDate ? moment(data.updateDate).format('M/D HH:mm') : ''
        await dialogs.showWarningDialog('内容が変更されています', `確認している間に ${name} さんが ${when} に変更されたようです。\n今の操作は行いませんでした。画面を更新します。`)
      } else {
        await dialogs.showWarningDialog('状況が変わっています', `この申請は、別の人の操作で「${data.status || '不明'}」になっています。\n今の操作は行いませんでした。最新の内容を表示します。`)
      }

      await this.getData()
      return true
    },
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
/* ヘッダーの「AKBSに登録済み」の印（承認済み。ボタンの代わり。AkbsInputForm と同じ） */
.registered-mark {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border: 2px solid #fff;
  border-radius: 9999px;
  background: #15803d;
  color: #fff;
  font-size: 0.875rem;
  font-weight: bold;
  white-space: nowrap;
}

.loading-mask {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  color: #6b7280;
  font-weight: 700;
  font-size: 14px;
}

.page-main {
  min-width: 1000px;
}

.cap {
  font-size: 10px;
  line-height: 14px;
  color: #6b7280;
}

/* 変更後の物件コードの表 */
table.new-site-table {
  border-collapse: collapse;
  font-size: 12px;
  line-height: 16px;
}

table.new-site-table th,
table.new-site-table td {
  border: 1px solid #e5e7eb;
  padding: 8px 14px;
  background: #fff;
  vertical-align: middle;
}

/* メインタスクの物件名（見るだけ）は薄いグレー */
table.new-site-table td.cell-main-site {
  background: #f3f4f6;
  color: #374151;
}

/* 契約詳細の保存先の物件は緑で目立たせる（ここに登録されると分かるように）。見出しも同じ緑 */
table.new-site-table td.cell-target-site,
table.new-site-table th.th-target-site {
  background: #dcfce7;
}

table.new-site-table th.th-target-site {
  color: #166534;
  border-color: #86efac;
}

/* 文字だけで出す得意先・物件（受注タスクで決まっている・閲覧）：名前を上に太く、コードを下に小さく */
.site-fixed {
  padding: 2px 0;
}

.site-fixed-name {
  font-weight: 700;
  font-size: 13px;
}

.site-fixed-code {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  letter-spacing: 0.02em;
}

table.new-site-table th {
  background: #eef2ff;
  color: #312e81;
  font-weight: 700;
  text-align: center;
  border-color: #c7d2fe;
}

/* 新規登録の物件の選択（候補からだけ。検索可） */
.new-site {
  width: 480px;
  font-size: 13px;
}

.new-site >>> .multiselect__tags {
  min-height: 34px;
  padding: 6px 30px 0 8px !important;
  font-size: 13px;
  text-align: left;
  border-color: #d1d5db;
}

/* 勤務形態の欄は矢印を隠しているが、ここは候補から選ぶ欄だと分かるように出す */
.new-site.multiselect >>> .multiselect__select {
  display: block;
  height: 32px;
}

.new-site >>> .multiselect__single,
.new-site >>> .multiselect__input,
.new-site >>> .multiselect__placeholder {
  font-size: 13px;
  margin-bottom: 4px;
  padding-top: 0;
  text-align: left;
}

/* 選んだ得意先・物件は名前を上に太く、コードを下に小さく（長い名前は折り返す。左寄せ） */
.new-site >>> .multiselect__single {
  white-space: normal;
  line-height: 1.3;
}

.pick-label {
  text-align: left;
}

.pick-label-name {
  font-weight: 700;
  font-size: 13px;
}

.pick-label-code {
  margin-top: 1px;
  font-size: 11px;
  font-weight: 400;
  color: #6b7280;
  letter-spacing: 0.02em;
}

/* 選んだあとは名前の色だけ緑に（コードは灰色のまま） */
.new-site.picked >>> .pick-label-name {
  color: #14532d;
}

.new-site >>> .multiselect__option {
  padding: 6px 10px;
  min-height: 0;
  line-height: 1.3;
}

/* 物件は必須なので、未選択は薄い赤（行の必須の欄と同じ色） */
.new-site.required-empty >>> .multiselect__tags {
  background-color: #fee2e2;
}

/* 選んだ得意先・物件は緑で目立たせる（選び終わったことが一目で分かるように） */
.new-site.picked >>> .multiselect__tags {
  background-color: #dcfce7;
  border-color: #15803d;
}

.new-site.picked >>> .multiselect__single {
  color: #14532d;
  font-weight: 700;
}

.site-opt {
  font-size: 12px;
}

.site-opt-flg {
  color: #6b7280;
  font-size: 11px;
}

.site-opt-addr {
  color: #6b7280;
  font-size: 11px;
}

/* 得意先の選択（物件と同じ見た目・同じ幅。得意先名は長いものが多い） */
.new-site.new-client {
  width: 480px;
}

/* 選んだ物件の住所・受注タスクと同じ得意先 */
.site-chosen {
  margin-top: 2px;
  font-size: 11px;
  color: #4b5563;
}

/* 案内（登録のない得意先・候補がまだ無い） */
.site-note {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.5;
  color: #6b7280;
  white-space: normal;
  max-width: 480px;
}

/* 控えてある物件コードが候補に無い */
.site-missing {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 700;
  color: #b91c1c;
}

.view {
  font-size: 14px;
  line-height: 20px;
  color: #374151;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 4px 8px;
}

.accordion {
  display: none;
}

.open {
  display: block;
}

.changed-count {
  margin-left: 8px;
  padding: 0 8px;
  border-radius: 4px;
  background: #fef9c3;
  color: #b9601e;
  font-size: 12px;
}

/* 申請の概要の「変更の内訳」の札（変えた欄・物件の見出しの「n件 変更」と同じ黄色） */
.tally-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tally-tag {
  display: inline-flex;
  align-items: baseline;
  /* 隣の項目（.view）と同じ高さ */
  height: 30px;
  line-height: 28px;
  padding: 0 10px;
  border: 1px solid #fde047;
  border-radius: 4px;
  background: #fef9c3;
  color: #b9601e;
  font-size: 12px;
  font-weight: 700;
}

.tally-tag b {
  margin: 0 3px 0 8px;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}

/* ---- 契約の表 ---- */
table.con-table {
  table-layout: fixed;
  border-collapse: collapse;
  width: 100%;
  min-width: 1742px; /* 固定幅の列 950px ＋ 曜日7列（1列 113px 以上） */
  font-size: 12px;
  line-height: 16px;
}

/* 幅を書いた列は固定。曜日の列だけ幅を書かず、画面が広いときの余りは曜日の列に配る（変更の方法などが広がらないように） */
.col-ops { width: 150px; }
.col-con { width: 190px; }
.col-period { width: 150px; }
.col-work { width: 120px; }
.col-attr { width: 84px; }

table.con-table th,
table.con-table td {
  border: 1px solid #e5e7eb;
  padding: 6px;
  background: #fff;
  vertical-align: middle;
}

table.con-table th {
  background: #eef2ff;
  color: #312e81;
  font-weight: 700;
  text-align: center;
  padding: 8px 6px;
  border-color: #c7d2fe;
}

/* 見出しの行は、縦にスクロールしても上に固定する（ユーザー要望 2026-09-23）
   sticky は一番近いスクロールする枠（.form-scroll）を基準にするので、表の枠（.con-table-wrap）は overflow を付けずにおく
   border-collapse の表では固定した見出しに線が付いてこないので、上下の線は内側の影で描く */
.con-table-wrap {
  overflow: visible;
}

table.con-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  box-shadow: inset 0 1px 0 #c7d2fe, inset 0 -1px 0 #c7d2fe;
}

table.con-table th.sun {
  color: #dc2626;
}

table.con-table th.attr-start,
table.con-table td.attr-start {
  border-left: 2px solid #d1d5db;
}

table.con-table th.attr {
  background: #e0e7ff;
}

/* 「全社休日出勤」は6文字で列幅（84px）に収まりにくいので、この見出しだけ少し小さく */
table.con-table th.attr-narrow {
  font-size: 11px;
  letter-spacing: -0.02em;
}

/* 作業内容の列（曜日と区分の間。区分の左の太い線はこの列の左に付く） */
table.con-table td.work-cell {
  text-align: center;
}

table.con-table td.attr {
  background: #fcfcfd;
  text-align: center;
}

/* 行の状態（契約のセルの中。変更後・変更前・修正（上書き）・契約終了） */
.row-state {
  margin-top: 6px;
  font-weight: 700;
  font-size: 11px;
}

.row-state.edit {
  color: #4338ca;
}

.row-state.newrow {
  color: #065f46;
}

/* 担当清掃員の行の「（清掃員）」（名前の上、太字なし） */
.staff-cap {
  font-size: 10px;
  font-weight: 400;
}

table.con-table td.period {
  vertical-align: top;
}

/* 契約のセル（契約コード・行の状態）は上下左右の中央 */
table.con-table td.con-cell {
  vertical-align: middle;
  text-align: center;
}

/* 表示だけの行（AKBSの今の値） */
table.con-table tr.view td {
  background: #f9fafb;
  color: #4b5563;
}

table.con-table tr.view td.con-cell {
  background: #fff;
  color: #374151;
}

table.con-table tr.view td.day-on {
  background: #f3f4f6;
}

/* 変更（履歴残す）の変更前の行は全体を薄いグレーに（変更後の行と見分けやすくする） */
table.con-table tr.prev td,
table.con-table tr.prev td.con-cell,
table.con-table tr.prev td.day-on,
table.con-table tr.prev td.attr {
  background: #f3f4f6;
  color: #6b7280;
}

table.con-table tr.prev .con-code {
  color: #6b7280;
}

table.con-table tr.prev .attendance-label {
  background: #9ca3af;
}

table.con-table tr.prev .period .val {
  color: #6b7280;
}

/* 終了した行は薄く */
table.con-table tr.ended td {
  color: #9ca3af;
}

table.con-table tr.ended .con-code {
  color: #9ca3af;
}

table.con-table tr.ended .attendance-label {
  background: #9ca3af;
}

/* 修正（上書き）の行 */
table.con-table tr.edit td.day-on {
  background: #f0fdf4;
}

/* 足した行 */
table.con-table tr.newrow td {
  background: #ecfdf5;
}

/* 担当清掃員の行（薄い紫。ユーザー指定 #dbd0e6 を少し薄めた色。黄色系だと変更ありの黄色が埋もれ、薄い青緑は変更後の行の色とかぶった） */
table.con-table tr.staff td {
  background: #f4ebf7db;
  color: #4f05cf;
  font-size: 11px;
}

/* 契約詳細の行と担当清掃員の行の境目（紫の線。契約の太枠 3px より細くする） */
table.con-table tr.staff-first td {
  border-top: 2px solid #b39ccf;
}

table.con-table td.staff-time {
  text-align: center;
}

/* 担当清掃員の勤務形態は、時間帯と同じ色（灰色の補足の色にしない） */
table.con-table tr.staff td.staff-time .cap {
  color: inherit;
}

table.con-table tr.addrow td {
  text-align: left;
  padding: 6px 10px;
}

/* 備考・クリーンプラス金額・ランクの行（勤務の行の下に1行。行の色（変更後・修正・閲覧）は勤務の行と同じ） */
table.con-table tr.note-row td.note-cell {
  text-align: left;
  padding: 4px 10px;
  border-top: 0;
}

.note-fields {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
}

.note-fields .note-label {
  flex: none;
  font-weight: 700;
  color: #4b5563;
  white-space: nowrap;
}

.note-fields .note-label:not(:first-child) {
  margin-left: 14px;
}

/* 備考は残りの幅を使う。1行で足りなければ縦に広げられる */
.note-fields textarea.note-input {
  flex: 1 1 auto;
  min-width: 200px;
  min-height: 26px;
  padding: 3px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  color: #111827;
  background: #fff;
  resize: vertical;
}

.note-fields .note-text {
  flex: 1 1 auto;
  min-width: 200px;
  font-size: 12px;
  line-height: 16px;
  white-space: pre-wrap;
  word-break: break-all;
}

.note-fields .note-value {
  flex: none;
  font-size: 12px;
}

.note-fields .note-text.none,
.note-fields .note-value.none {
  color: #9ca3af;
}

.note-fields input.clean-plus-input {
  flex: none;
  width: 90px;
  height: 26px;
  padding: 0 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  text-align: right;
  font-size: 12px;
  color: #111827;
  background: #fff;
}

.note-fields .note-unit {
  flex: none;
  color: #9ca3af;
  font-size: 10px;
}

.note-fields select.rank-select {
  flex: none;
  width: 64px;
}

/* 物件コード×契約コードごとの太枠は一旦やめて（2026-09-18）、契約の間の gap の行を太い棒線1本にする。
   grp-first / last / grp-r / grp-l のクラスは残してあるので、太枠に戻すときはここに枠線を書き足す */
table.con-table tr.gap td {
  padding: 0;
  border: 0;
  background: transparent;
}

/* 契約の間の空き（見出しの帯が区切りになるので、線は引かず少し空けるだけ。線に戻すなら background と height を入れる） */
.gap-bar {
  height: 14px;
  margin: 0;
  background: transparent;
}

/* 契約の見出しの帯（契約コード）。区切りと名札を兼ねる */
table.con-table tr.con-head td {
  padding: 5px 10px;
  border: 1px solid #c7d2fe;
  border-top: 2px solid #4338ca;
  background: #e0e7ff;
  color: #312e81;
  font-size: 12px;
  font-weight: 700;
  text-align: left;
}

table.con-table tr.con-head td b {
  font-size: 15px;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
}

.con-head-new {
  margin-left: 10px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #d1fae5;
  color: #065f46;
  font-size: 10px;
}

/* 概要の「受注の内容」（作業内容ごとに1行。受注画面の【】の見出しと同じ文字を目立たせる） */
/* 受注の内容と契約詳細の比べの表（作業内容ごとに1行。数を列でそろえる） */
table.order-table {
  border-collapse: collapse;
  font-size: 12px;
  line-height: 18px;
}

table.order-table th,
table.order-table td {
  border: 1px solid #e5e7eb;
  padding: 3px 8px;
  background: #fff;
  vertical-align: middle;
}

table.order-table th {
  background: #eef2ff;
  color: #312e81;
  font-weight: 700;
  text-align: center;
  border-color: #c7d2fe;
  font-size: 11px;
  white-space: nowrap;
}

/* 受注タスクの列と契約詳細の列を色で分ける */
table.order-table th.grp-order {
  background: #e0e7ff;
}

table.order-table th.grp-contract {
  background: #dcfce7;
  color: #14532d;
  border-color: #bbf7d0;
}

table.order-table td.col-num {
  text-align: right;
  white-space: nowrap;
}

table.order-table td.col-num b {
  font-size: 14px;
}

table.order-table td.col-cnt,
table.order-table td.col-no,
table.order-table td.col-judge {
  text-align: center;
  white-space: nowrap;
}

table.order-table td.col-people {
  white-space: nowrap;
}

/* 合計の行（見せるだけ。薄いグレーで太字） */
table.order-table tr.total-row td {
  background: #f3f4f6;
  font-weight: 700;
  color: #374151;
}

table.order-table tr.total-row td.col-judge .total-diff {
  color: #4b5563;
}

/* 受注タスクと違う数のセルだけ黄色（変えた欄と同じ色） */
table.order-table td.mismatch {
  background: #fef08a;
}

table.order-table .unknown {
  color: #9ca3af;
}

.judge-ok {
  color: #15803d;
  font-weight: 700;
  font-size: 14px;
}

/* 明らかに違う（申請できない） */
.judge-far {
  color: #b91c1c;
  font-weight: 700;
  font-size: 12px;
}

.judge-ng {
  color: #b45309;
  font-weight: 700;
  font-size: 12px;
}

.judge-none {
  color: #9ca3af;
}

/* 受注タスクと違う理由のモーダル */
.reason-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
}

.reason-box {
  width: 560px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  padding: 16px 20px;
}

.reason-title {
  font-size: 15px;
  font-weight: 700;
  color: #b45309;
  margin-bottom: 6px;
}

.reason-desc {
  font-size: 12px;
  color: #374151;
  margin-bottom: 8px;
}

.reason-lines {
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
  background: #fef3c7;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 10px;
  white-space: pre-wrap;
}

.reason-text {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 13px;
}

.reason-btns {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.reason-btn {
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 700;
  border: 1px solid;
}

.reason-btn.cancel {
  border-color: #d1d5db;
  background: #fff;
  color: #374151;
}

.reason-btn.ok {
  border-color: #4338ca;
  background: #4338ca;
  color: #fff;
}

.reason-btn.ok:disabled {
  border-color: #d1d5db;
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

.order-item-name {
  font-weight: 700;
  color: #312e81;
  font-size: 14px;
}

/* 受注の項目の番号（「受注1」。契約コードの帯・作業内容の選択欄・ダイアログでも同じ番号で呼ぶ） */
.order-badge {
  flex: none;
  padding: 1px 8px;
  border-radius: 9999px;
  background: #312e81;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
}

.order-item-people {
  font-weight: 700;
  color: #b91c1c;
}

.order-item-remarks {
  font-size: 11px;
  color: #6b7280;
}

/* 人数より勤務の行が少ない作業内容の知らせ（赤） */
.order-item-blank {
  font-size: 11px;
  font-weight: 700;
  color: #b91c1c;
}

/* 手で足した契約コードの、受注の作業内容の選択（帯の中。未選択は薄い赤） */
.con-head-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  font-weight: 400;
}

.con-head-item-cap {
  font-size: 11px;
  color: #6b7280;
}

.con-head-item-select {
  width: 320px;
  min-height: 24px;
  font-size: 12px;
}

.con-head-item-select >>> .multiselect__tags {
  min-height: 24px;
  padding: 2px 8px 0 8px;
  font-size: 12px;
  text-align: left;
}

.con-head-item-select >>> .multiselect__single,
.con-head-item-select >>> .multiselect__placeholder {
  margin-bottom: 2px;
  padding-top: 0;
  font-size: 12px;
  line-height: 18px;
}

.con-head-item-select.required-empty >>> .multiselect__tags {
  background-color: #fee2e2;
}

/* 受注から作った契約コード（帯に、受注のどの作業内容の何人目かを出す） */
.con-head-order {
  margin-left: 10px;
  color: #3730a3;
  font-size: 11px;
  font-weight: 700;
}

/* 受注と違う欄（黄色）を受注の今の値に戻すボタン（帯の中。変えた欄と同じ黄色系） */
.con-head-reset {
  margin-left: 10px;
  padding: 1px 8px;
  border: 1px solid #ca8a04;
  border-radius: 4px;
  background: #fef9c3;
  color: #854d0e;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
}

.con-head-reset:hover {
  background: #fde047;
}

/* 変更なしの契約（帯に灰色で出す。変更のある契約には何も出さない） */
.con-head-none {
  margin-left: 10px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #4b5563;
  font-size: 11px;
}

/* 畳める契約の帯（同じ物件のほかの契約に変更があるときの、変更なしの契約）。帯のどこを押しても開閉する */
table.con-table tr.con-head.foldable td {
  cursor: pointer;
  user-select: none;
}

table.con-table tr.con-head.foldable:hover td {
  background: #c7d2fe;
}

.con-head-mark {
  display: inline-block;
  width: 16px;
  font-size: 10px;
}

.con-head-fold {
  margin-left: 10px;
  color: #4338ca;
  font-size: 11px;
  text-decoration: underline;
}

table.con-table td.blocked {
  background: #fef2f2;
  color: #b91c1c;
  font-weight: 700;
  padding: 6px 10px;
}

/* 変更の方法（表の左の列。契約1件の全部の行にまたがる） */
table.con-table td.ops {
  vertical-align: middle;
  /* 契約の見出しの帯（tr.con-head）と同じ色 */
  background: #e0e7ff !important;
  border-color: #c7d2fe;
  padding: 6px;
}

/* ボタンは幅固定（列が広がっても伸びない）。見た目・振る舞いは雇用指示の表（AkbsEmploymentTable の .op-btn / .state）をそのまま写す */
.ops-list {
  width: 136px;
}

.op-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 26px;
  margin-bottom: 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  /* 入力欄は白なので、押せることが分かるよう色を敷いて影を落とす */
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.18);
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.op-btn:last-child {
  margin-bottom: 0;
}

.op-btn:hover {
  border-color: #4338ca;
  color: #4338ca;
  box-shadow: 0 2px 5px rgba(49, 46, 129, 0.25);
}

/* 押し込んだ分だけ影を消して下げる */
.op-btn:active {
  box-shadow: none;
  transform: translateY(1px);
}

/* 取消は入力欄と見分けが付くよう、塗りつぶしのボタンにする */
.op-btn.cancel {
  background: #6b7280;
  border-color: #6b7280;
  color: #fff;
  font-weight: 700;
}

.op-btn.cancel:hover {
  background: #4b5563;
  border-color: #4b5563;
  color: #fff;
}

/* 2行のボタン（見出し＋履歴の扱い）。縦に積むので高さは中身に合わせる */
.op-btn.stack {
  flex-direction: column;
  height: auto;
  padding: 5px 0;
  line-height: 1.25;
}

/* 履歴の扱いは補足なので少し小さく。1行目と詰まって見えないよう上に余白を空ける */
.op-btn .op-sub {
  margin-top: 3px;
  font-size: 10px;
}

/* 履歴が残る方が普段使う操作なので、こちらを目立たせる */
.op-btn.keep {
  background: #eef2ff;
  border-color: #4338ca;
  color: #4338ca;
  font-weight: 700;
}

.op-btn.keep:hover {
  background: #e0e7ff;
}

/* 上書きは変更前が残らないので、あえて目立たせない。下に余白を空けて他の2つと切り離す。
   ホバーしたときだけ警告色にして、選ぼうとした時点で気付けるようにする */
.op-btn.overwrite {
  margin-bottom: 14px;
  border-color: #818cf8;
  color: #4f46e5;
}

.op-btn.overwrite:hover {
  border-color: #b45309;
  color: #b45309;
}

/* 契約終了は内容の変更ではないので、上に余白を空けて切り離す。
   補足の行が無いぶん、2行のボタンと同じ高さを確保して文字を上下中央に置く */
.op-btn.finish {
  margin-top: 14px;
  min-height: 41px;
  /* 灰色だと押せないボタンに見えるので、終了の意味に合わせて薄いローズ色にする（解約済み・終了した契約の赤字と同じ系統） */
  background: #fff1f2;
  border-color: #fda4af;
  color: #be123c;
  font-weight: 700;
}

.op-btn.finish:hover {
  background: #ffe4e6;
  border-color: #e11d48;
  color: #be123c;
  box-shadow: 0 2px 5px rgba(190, 18, 60, 0.25);
}

/* 選んだあとの状態の文字（雇用指示の表の .state と同じ。td.state と名前がかぶるので mode-state） */
.mode-state {
  display: block;
  margin-bottom: 7px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
}

.mode-state.over {
  color: #92400e;
}

.mode-state.new {
  color: #1e40af;
}

.mode-state.add {
  color: #065f46;
}

.mode-state.none,
.mode-state.end {
  color: #6b7280;
}

/* 契約コード（札はやめて、青い太字を左右中央に置く） */
.con-code {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
  margin-bottom: 4px;
  color: #1e40af;
  font-weight: 700;
  font-size: 11px;
  line-height: 1.2;
  text-align: center;
}

.con-code b {
  font-size: 18px;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
}

/* 行の状態の印（適用中・これからの行・終了した行）。左右中央に置く */
.row-badge {
  display: table;
  margin: 0 auto;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
}

.row-badge.now { background: #e0e7ff; color: #312e81; }
.row-badge.future { background: #d1fae5; color: #065f46; }
.row-badge.ended { background: #e5e7eb; color: #6b7280; }

.field {
  margin-top: 5px;
}

.field label {
  display: block;
  font-size: 10px;
  color: #6b7280;
}

.period .field + .field {
  margin-top: 6px;
}

.period .val {
  font-size: 12px;
  color: #111827;
}

.period .val.none {
  color: #9ca3af;
}

/* 自動で入る終了日（変更後の開始日が未入力のとき）。雇用指示の表の auto-end と同じ見た目 */
.period .auto-end {
  font-size: 10px;
  font-weight: 700;
  line-height: 1.35;
  color: #dc2626;
}

.period .val.changed {
  background-color: #fef9c3;
}

.period .val.ng,
.period .auto-end.ng {
  box-shadow: inset 0 0 0 2px #dc2626;
}

.staff-name {
  text-align: center;
  font-weight: 700;
}

.del-link {
  display: block;
  margin: 6px auto 0;
  color: #b91c1c;
  font-size: 11px;
  font-weight: 700;
  text-decoration: underline;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.add-btn {
  padding: 3px 12px;
  border-radius: 4px;
  border: 1px solid #4338ca;
  background: #eef2ff;
  color: #4338ca;
  font-weight: 700;
  font-size: 11px;
  cursor: pointer;
}

/* 曜日のセル */
table.con-table td.day-cell {
  height: 1px;
}

.day {
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 100%;
}

/* 表示だけのときの勤務形態の札のまとまり（変わったときに全体を黄色にするための入れ物。札どうしの間隔は .day と同じ） */
.attendance-labels {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* 表示だけ（閲覧のみ）のときの変えた欄。入力欄ではないので、文字の地を黄色にする（2026-09-24） */
.view-changed {
  background-color: #fef9c3;
  border-radius: 4px;
  padding: 1px 4px;
}

.attendance-label {
  margin: 0 auto;
  padding: 2px 0;
  width: 80%;
  border-radius: 4px;
  background: #15803d;
  color: #fff;
  font-weight: 700;
  text-align: center;
  font-size: 11px;
}

.times {
  display: flex;
  align-items: center;
  gap: 2px;
  justify-content: center;
}

.times.view-text {
  font-size: 11px;
}

.times >>> input {
  flex: 1;
  min-width: 0;
}

.tilde {
  flex: none;
  color: #6b7280;
  font-size: 11px;
  padding: 0 2px;
}

.work {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 11px;
  color: #4b5563;
}

.work input.wh {
  width: 48px;
  height: 26px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  text-align: center;
  padding: 0 2px;
  font-size: 12px;
  color: #111827;
}

.changed-off {
  margin: 0 auto;
  padding: 2px 6px;
  border-radius: 4px;
  background: #fef9c3;
  color: #b9601e;
  font-weight: 700;
  text-align: center;
  font-size: 11px;
}

.holiday-hours {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 4px;
}

.holiday-hours input.hours-input {
  width: 48px;
  height: 26px;
  padding: 0 4px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  text-align: right;
  font-size: 12px;
  color: #111827;
  background: #fff;
}

.holiday-hours .unit {
  font-size: 10px;
  color: #9ca3af;
  white-space: nowrap;
}

/* 受注の祝日対応「ゴミ出しのみ」の印（祝日勤務のセルの上。バッジではなく少し濃いグレーの文字。欄の幅を広げないよう「受注：」は付けない） */
.holiday-garbage {
  margin-bottom: 2px;
  color: #4b5563;
  font-size: 10px;
  font-weight: bold;
  white-space: nowrap;
  text-align: center;
}

.cell-select {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 2px 18px 2px 4px;
  font-size: 11px;
  text-align: center;
  background: #fff;
  color: #111827;
}

.cell-select.left {
  text-align: left;
}

/* コピペ */
.day-copy {
  display: flex;
  gap: 4px;
  margin-top: auto;
  padding-top: 2px;
}

/* 行ごとのコピー・貼付（契約のセルの下。曜日のコピー・貼付と同じ見た目のボタン） */
.row-copy {
  display: flex;
  gap: 4px;
  margin-top: 6px;
}

.day-btn {
  flex: 1;
  padding: 2px 0;
  border: 1px solid;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.day-btn.copy {
  border-color: #4338ca;
  background: #eef2ff;
  color: #4338ca;
}

.day-btn.copy:hover:not(:disabled) {
  background: #c7d2fe;
}

.day-btn.paste {
  border-color: #3730a3;
  background: #c7d2fe;
  color: #312e81;
}

.day-btn.paste:hover:not(:disabled) {
  background: #a5b4fc;
}

.day-btn:disabled {
  border-color: #d1d5db;
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

/* 勤務形態の複数選択 */
.multiselect >>> .multiselect__tags {
  padding-right: 6px !important;
  min-height: 28px;
  padding-top: 4px;
  font-size: 12px;
  text-align: center;
}

.multiselect >>> .multiselect__select {
  display: none;
}

/* 候補は開いたときに位置・幅を入れ直す（attendanceOpened）。部品の開閉アニメは transition: all なので、
   そのままだと位置・幅の変化まで動いて横から滑り込むように見える。ふわっと出る効果（透明度）だけ残す */
.multiselect >>> .multiselect__content-wrapper {
  transition-property: opacity;
}

/* 契約期間の日付欄（部品の見出しは出さない。欄の上の「契約開始日」は自前の .field label で出す） */
.period .date-field >>> label {
  display: none;
}

.period .date-field >>> .mt-1 {
  margin-top: 0;
}

/* 変えた欄は黄色 */
.changed >>> input:not([type='checkbox']),
.changed >>> select,
input.changed,
select.changed,
textarea.changed,
.changed >>> .multiselect__tags {
  background-color: #fef9c3 !important;
}

/* ラウンドプラスの固定清掃の祝日勤務を初期値の「FULL」から変えた欄は、目立つ濃い黄色＋太字 */
select.order-holiday-changed,
input.order-holiday-changed {
  background-color: #fde047 !important;
  box-shadow: inset 0 0 0 2px #ca8a04;
  font-weight: bold;
}
span.order-holiday-changed {
  background-color: #fde047;
  padding: 0 6px;
  border-radius: 4px;
  box-shadow: inset 0 0 0 2px #ca8a04;
  font-weight: bold;
}

/* NGの欄は赤い枠 */
.ng >>> input:not([type='checkbox']),
.ng >>> select,
input.ng,
select.ng,
.ng >>> .multiselect__tags {
  box-shadow: inset 0 0 0 2px #dc2626 !important;
}

/* 有り｜無しのボタンが NG（どちらも選んでいない）：2つまとめて赤い枠 */
.flag-kind.ng-kind {
  border-radius: 4px;
  box-shadow: 0 0 0 2px #dc2626;
}

/* 必須なのに空の欄は薄い赤（清掃員の申請画面 AkbsInputForm と同じ色。NG の赤い枠とは別ものと分かるよう、地の色にしている）
   変更ありの黄色より優先する（空のままでは申請できないので） */
.required-empty >>> input:not([type='checkbox']),
input.required-empty,
select.required-empty,
.flag-kind.required-empty .kind-btn {
  background-color: #fee2e2 !important;
}

/* 閲覧のみのときは入力欄を文字に見せる */
.readonly >>> input:disabled,
.readonly >>> select:disabled {
  background-color: transparent !important;
  border-color: transparent !important;
  color: #374151;
  -webkit-text-fill-color: #374151;
  opacity: 1;
  cursor: default;
}

.readonly >>> .multiselect--disabled {
  opacity: 1;
  background: transparent;
}

.readonly >>> .multiselect--disabled .multiselect__tags {
  border-color: transparent;
  background: transparent;
}

/* 物件の見出しの「この物件を外す」。文字だけだと見落とすので、赤い枠の押せるボタンにする（見出しの青地に映える） */
.remove-site-btn {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border: 1px solid #dc2626;
  border-radius: 4px;
  background: #fff;
  color: #b91c1c;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.18);
  transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;
}

.remove-site-btn:hover {
  background: #dc2626;
  color: #fff;
  box-shadow: 0 2px 5px rgba(220, 38, 38, 0.35);
}

.remove-site-btn:active {
  box-shadow: none;
  transform: translateY(1px);
}

/* 有り｜無しの2つ並びボタン（雇用指示の表の交通費区分 .fare-kind / .kind-btn と同じ作り。枠線を1px重ねて連結して見せる） */
.flag-kind {
  display: flex;
  justify-content: center;
}

/* 2つとも同じ幅にして、どちらが選ばれているか幅で錯覚しないようにする */
.kind-btn {
  flex: 1;
  height: 26px;
  cursor: pointer;
  margin-left: -1px;
  padding: 0 4px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.kind-btn:first-child {
  margin-left: 0;
  border-radius: 4px 0 0 4px;
}

.kind-btn:last-child {
  border-radius: 0 4px 4px 0;
}

/* 選んでいない側だけホバーで藍色の文字にする（選んでいる側は藍色の地なので文字を白のままにする） */
.kind-btn:hover:not(:disabled):not(.selected) {
  border-color: #4338ca;
  color: #4338ca;
}

.kind-btn:disabled {
  cursor: default;
}

/* 選んでいるものは塗りつぶす。隣に重ねた枠線に隠れないよう前面に出す */
.kind-btn.selected {
  position: relative;
  z-index: 1;
  background: #4338ca;
  border-color: #4338ca;
  color: #fff;
}

/* 変更ありは、選んでいない側を黄色にして変わったことが分かるようにする */
.flag-kind.changed-kind .kind-btn:not(.selected) {
  background-color: #fef9c3;
}

/* 受注タスク（見積）で決まっていて変えられない欄（選んでいる側はそのまま、選んでいない側を薄く） */
.flag-kind.order-locked .kind-btn:not(.selected) {
  color: #9ca3af;
  background-color: #f3f4f6;
}

.cell-select.order-locked {
  background-color: #f3f4f6;
  color: #111827;
  opacity: 1;
}

/* 物件の表の下の「契約コードを追加」 */
.add-contract {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0 10px;
}


/* スクロールの位置は watchTopHeight で自前で保つので、ブラウザの自動の位置合わせは切る
   （上の部分が画面より上に隠れているときはブラウザも同じ分だけ動かすので、両方効くと2倍ずれる） */
.form-scroll {
  overflow-anchor: none;
}

/* 高さを見張る外側の枠。中の枠の上下の余白（mt-4 など）も高さに含める（含めないと、枠が出たときの余白の分だけずれる） */
.top-wrap {
  display: flow-root;
}

/* 影響される清掃員の枠と見出し。ほかの枠（藍色）と見分けが付くよう紫にする（契約の表の清掃員の行と同じ色） */
.emp-card {
  border: 1px solid #b39ccf;
}

.emp-card-head {
  background: #f4ebf7;
  border-bottom: 1px solid #b39ccf;
  color: #4f05cf;
}

/* 「雇い入れタスクを作る」ボタンも見出しと同じ紫（共通のボタンは藍色で決め打ちなので、ここだけ上書きする） */
.emp-create-btn {
  background-color: #4f05cf !important;
}

.emp-create-btn:hover:not(:disabled) {
  background-color: #3f04a6 !important;
}

.emp-create-btn:focus {
  --tw-ring-color: #4f05cf !important;
}

/* ---- 影響される清掃員の表（紫ベース。見出しの帯と同じ色で揃え、色の種類は増やさない。直せない行と作成済みの札だけ灰色） ---- */
/* 表の上の説明文 */
.emp-lead {
  margin-bottom: 6px;
  color: #3b0764;
  font-size: 13px;
  font-weight: 700;
}

table.emp-task-table {
  width: 100%;
  min-width: 1260px;
  border-collapse: collapse;
  font-size: 12px;
  line-height: 18px;
}

table.emp-task-table th,
table.emp-task-table td {
  border: 1px solid #e0d3ea;
  padding: 6px 8px;
  text-align: left;
  vertical-align: middle;
}

table.emp-task-table th {
  background: #f4ebf7;
  color: #4f05cf;
  font-weight: 700;
  white-space: nowrap;
}

/* チェックボックスも紫（共通の見た目は文字色で塗られる） */
table.emp-task-table input[type='checkbox'] {
  color: #4f05cf;
}

table.emp-task-table td.text-center,
table.emp-task-table th.w-12 {
  text-align: center;
}

/* 既にタスクありの行はここでは作れないので、薄い灰色の地にする */
table.emp-task-table tr.exist td {
  background: #f9fafb;
}

table.emp-task-table td.emp-cd {
  color: #4f05cf;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
}

.emp-code {
  padding-left: 4px;
  color: #6b7280;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

/* 状態の札（濃さと枠の有無だけで見分ける） */
.emp-chip {
  display: inline-block;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.emp-chip.must { background: #4f05cf; color: #fff; }
.emp-chip.exist { background: #fff; color: #3b0764; border-color: #7c5aa6; }
.emp-chip.todo { background: #fff; color: #6b5a80; border-color: #b39ccf; }
/* この画面から作った雇い入れ（もうやることが無いので灰色） */
.emp-chip.done { background: #e5e7eb; color: #374151; border-color: #9ca3af; }

/* 雇い入れ種別（ドロップダウン。横幅を取らない） */
.emp-kind-select {
  height: 38px;
  padding: 0 28px 0 8px;
  border: 1px solid #4f05cf;
  border-radius: 4px;
  color: #4f05cf;
  font-size: 12px;
  font-weight: 700;
}

/* 幅は欄の側（.task-select）で決める。中身の幅に任せると、開いたとき（検索の入力欄に切り替わる）に列の幅が変わって画面が動く */
table.emp-task-table td.emp-select-cell {
  width: 200px;
}

.task-select {
  width: 200px;
}

/* 期限（部品の見出しは出さない） */
table.emp-task-table td.emp-date-cell {
  width: 150px;
}

.emp-date-cell .date-field >>> label {
  display: none;
}

.emp-date-cell .date-field >>> .mt-1 {
  margin-top: 0;
}

/* メモに何か書いてあるとき：オレンジ色の欄（AkbsInputForm と同じ。閲覧のみのときも灰色にしない） */
.memo-filled >>> label {
  color: #c2410c;
}

.memo-filled >>> textarea,
.memo-filled >>> textarea:disabled {
  border: 2px solid #f97316;
  background: #fff7ed !important;
  color: #7c2d12;
  -webkit-text-fill-color: #7c2d12;
  font-weight: bold;
}

.emp-end {
  color: #dc2626;
  font-weight: 700;
}

.emp-no {
  margin-left: 10px;
  color: #3b0764;
}

.emp-open {
  color: #4f05cf;
  text-decoration: underline;
  white-space: nowrap;
}

.emp-note {
  margin-left: 12px;
  color: #4b5563;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
}

/* 宛先の部署・担当者（1つ選ぶ・検索可）。上の勤務形態用の小さい見た目を打ち消して普通の入力欄の大きさにする */
.task-select >>> .multiselect__tags {
  min-height: 38px;
  padding: 8px 40px 0 8px !important;
  font-size: 13px;
  text-align: left;
  border-color: #d1d5db;
}

.task-select >>> .multiselect__select {
  display: block;
}

.task-select >>> .multiselect__single,
.task-select >>> .multiselect__input {
  font-size: 13px;
  margin-bottom: 6px;
}

/* 長い名前は折り返さず省略する（折り返すと欄の高さが変わる） */
.task-select >>> .multiselect__single {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* 開いたときの検索の入力欄。共通の入力欄の見た目（@tailwindcss/forms の余白・枠・行の高さ・フォーカスの輪）が付くと、
   閉じているときの文字より背が高くなって欄が大きくなるので、閉じているときと同じ大きさに揃える */
.task-select >>> .multiselect__input {
  height: 20px;
  min-height: 20px;
  line-height: 20px;
  padding: 0 0 0 5px !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent;
}

.task-select >>> .multiselect__placeholder {
  color: #9ca3af;
  font-size: 13px;
  margin-bottom: 6px;
}

.task-select >>> .multiselect__option--highlight {
  background: #4f05cf;
}

.task-select >>> .multiselect__option--selected.multiselect__option--highlight {
  background: #4f05cf;
}
</style>
